import React, { useState, useEffect, useCallback, useRef, useMemo, createContext, useContext } from 'react';
import { flushSync } from 'react-dom';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { Chess } from 'chess.js';
import { doc, updateDoc, arrayUnion, arrayRemove, increment, getDoc, collection, query, where, getDocs, addDoc, deleteDoc } from 'firebase/firestore';
import { firestore } from '../../firebase';

// Import mobile-optimized components
import BoardThree from '../../components/WorkingChessLogic/3Board.jsx';
import MovesThree from '../../components/WorkingChessLogic/3Moves.jsx';
import { useGameState } from '../../components/WorkingChessLogic/GameStateContext.js';
import useGameLogic from '../../components/WorkingChessLogic/UseGameState.js';
import { createStartPosition } from '../../util/PositionData';
import MobileMoves, { MobileNavigationControls, MobileMovesEngine } from '../components/MobileMoves.jsx';
import UnifiedMobileCommentInput from '../components/UnifiedMobileCommentInput.jsx';

// Import API and utilities
import { fetchGame, fetchNextGame, fetchPrevGame, putGame } from '../../util/api/game.js';
import { useRecordModeLogic } from '../components/RecordModeComponent.js';
import { TAGS } from "../../util/constants.js";
import constants from "../../util/constants.js";
import { cleanProfanity } from '../../util/filterProfanity';
import { updateCommentIdInMoves, getMove } from '../../util/Chess.js';
import { checkRateLimit } from '../../util/rateLimiter';
import { getRateLimit, buildRateKey } from '../../util/rateLimitConfig';
import { getCurrentBoardPosition } from '../../components/WorkingChessLogic/UseGameState.js';
import { sortCommentsByUserPremium } from '../../util/userUtils.js';
import { getNodeAtPathInfinite, parseVariationPath, calculateCumulativeMoveIndex } from '../../util/VariationPathUtils.js';
import useStockfishAnalysisManager from '../../hooks/useStockfishAnalysisManager';
import { useUnifiedStockfishAnalysis } from '../../hooks/useUnifiedStockfishAnalysis';

// Import TTS functionality
import { useRecordModeTTS } from '../../hooks/useMoveVoiceover';
import { ChessSocialContext } from '../../util/ChessSocialContext.js';
import stockfishEngine from '../../util/StockfishEngine.js';

// Import bookmark modal
import BookmarkModal from '../../components/modals/BookmarkModal.js';
import NavigationConfirmModal from '../../components/modals/NavigationConfirmModal';
import { isNativeApp } from '../../util/platformDetection';
import BestLineComponent from '../components/BestLineComponent.jsx';
import mansalva from '../../images/Mansalva/Mansalva-Regular.ttf';
import chessslogo from '../../images/chessslogo.png';
import chesssboard from '../../images/chesssboard.png';

// Import StockfishIcons for move classifications
import BestIcon from '../../images/StockfishIcons/Best.png';
import BlunderIcon from '../../images/StockfishIcons/Blunder.png';
import BrillianceIcon from '../../images/FooterIcons/Brilliance2.png';
import ExcellentIcon from '../../images/StockfishIcons/Excellent.png';
import GoodIcon from '../../images/StockfishIcons/Good.png';
import GreatIcon from '../../images/StockfishIcons/Great.png';
import InaccurateIcon from '../../images/StockfishIcons/Inaccurate.png';
import MissIcon from '../../images/StockfishIcons/Miss.png';
import MistakeIcon from '../../images/StockfishIcons/Mistake.png';
import TheoryIcon from '../../images/StockfishIcons/Theory.png';

// Create context for Record Mode
export const RecordModeContext = createContext({ 
  isRecordMode: false, 
  setIsRecordMode: () => {} 
});

const CHESSBOARD = constants.CHESSBOARD;

// Available StockfishIcons for move classifications
const CLASSIFICATION_ICONS = [
  { id: 'best', name: 'Best', icon: BestIcon, color: '#00ff00' },
  { id: 'brilliant', name: 'Brilliant', icon: BrillianceIcon, color: '#ff6600' },
  { id: 'great', name: 'Great', icon: GreatIcon, color: '#4CAF50' },
  { id: 'excellent', name: 'Excellent', icon: ExcellentIcon, color: '#2196F3' },
  { id: 'good', name: 'Good', icon: GoodIcon, color: '#8BC34A' },
  { id: 'theory', name: 'Theory', icon: TheoryIcon, color: '#9C27B0' },
  { id: 'inaccurate', name: 'Inaccurate', icon: InaccurateIcon, color: '#FF9800' },
  { id: 'mistake', name: 'Mistake', icon: MistakeIcon, color: '#FF5722' },
  { id: 'miss', name: 'Miss', icon: MissIcon, color: '#f44336' },
  { id: 'blunder', name: 'Blunder', icon: BlunderIcon, color: '#ff0000' }
];

/**
 * Mobile-optimized ViewGame component
 * 
 * Key differences from desktop version:
 * - Touch-optimized interface
 * - Simplified layout for mobile screens
 * - Essential features only (board, moves, basic controls)
 * - No complex modals or advanced features
 * - Responsive design for various mobile screen sizes
 */
const ViewGameMobile = ({ user }) => {
  const history = useHistory();
  const location = useLocation();
  const { gameId } = useParams();
  
  // Extract URL parameters for filter detection (desktop pattern)
  const searchParams = new URLSearchParams(location.search);
  const tag = searchParams.get('tag');
  // Create query object like desktop
  const query = { get: (key) => searchParams.get(key) };
  
  // Desktop-equivalent helper functions
  const isInPuzzleFilter = () => {
    return query.get('tag') === 'Puzzle' || tag === 'Puzzle';
  };
  
  const redirectToPortalWithPuzzleFilter = () => {
    // Use history.push with state to set the Puzzle tag in Portal
    // Use 'Puzzle' to match portal's activeTab check (portal checks activeTab === 'Puzzle')
    // console.log('Mobile: Redirecting to portal with state:', { 
    //   activeTag: 'Puzzle', 
    //   filterTag: 'Puzzle'
    // });
    history.push('/portal', { 
      activeTag: 'Puzzle', 
      filterTag: 'Puzzle'
    });
  };

  // Game state context
  const { state, dispatch } = useGameState();
  
  // Record mode context to communicate with App.js
  const recordModeContext = useContext(RecordModeContext);
  const { setIsRecordMode } = recordModeContext || { setIsRecordMode: () => {} };
  const { 
    moves: contextMoves, 
    currentMove: contextCurrentMove, 
    orientation: contextOrientation,
    starredMoves: contextStarredMoves,
    rootFEN: contextRootFEN
  } = state;

  // Game logic functions - minimal destructuring to prevent infinite loops
  const { goToMove, moves } = useGameLogic();

  // Helper function to find the deepest comment variation info for nested variations
  const getDeepestCommentInfo = (currentMoveId, contextMoves, state, comments) => {
    if (!currentMoveId.includes('|')) {
      return null;
    }

    try {
      // For nested variations, we need to traverse to the deepest level
      const parts = currentMoveId.split('|');
      
      // Start from the deepest variation and work backwards
      for (let i = parts.length - 1; i >= 1; i--) {
        const variationPath = parts.slice(0, i + 1).join('|');
        const mainMoveIdx = parseInt(parts[0], 10);
        const currentPartSegments = parts[i].split(':');
        const variationIdx = parseInt(currentPartSegments[0], 10);
        
        // Get the variation at this level
        let variation = null;
        if (i === 1) {
          // First level variation
          if (mainMoveIdx === -1) {
            variation = state?.startPosition?.variations?.[variationIdx];
          } else {
            const mainMove = contextMoves[mainMoveIdx];
            variation = mainMove?.variations?.[variationIdx];
          }
        } else {
          // Nested variation - use VariationPathUtils to get the parent move
          try {
            const parentPath = parts.slice(0, i).join('|');
            const { getNodeAtPathInfinite } = require('../../util/VariationPathUtils');
            const parentResult = getNodeAtPathInfinite(contextMoves, parentPath, state);
            if (parentResult.success && parentResult.node?.variations) {
              variation = parentResult.node.variations[variationIdx];
            }
          } catch (error) {
            console.error('Error getting nested variation:', error);
            continue;
          }
        }
        
        // Check if this variation contains comment moves
        if (variation && variation.length > 0) {
          const firstCommentMove = variation.find(varMove => 
            varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT
          );
          
          if (firstCommentMove && firstCommentMove.creator?.id !== undefined) {
            // Try to find the matching comment by ID
            const creatorId = firstCommentMove.creator.id;
            const matchingComment = comments?.find(comment => comment.id === creatorId);
            
            if (matchingComment) {
              return {
                content: matchingComment.content,
                username: matchingComment.username,
                commentId: creatorId,
                variationLevel: i
              };
            }
          }
        }
      }
    } catch (error) {
      console.error('Error in getDeepestCommentInfo:', error);
    }
    
    return null;
  };

  // Detect iPad/tablet - check multiple conditions
  const detectIsTablet = () => {
    // Check screen width
    const widthCheck = window.innerWidth >= 768;
    
    // Check if it's an iPad via user agent
    const isIPad = /iPad/i.test(navigator.userAgent) || 
                   (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    // Check aspect ratio for tablet-like screens
    const aspectRatio = window.innerWidth / window.innerHeight;
    const isTabletAspectRatio = aspectRatio > 0.65 && aspectRatio < 1.5 && 
                                Math.min(window.innerWidth, window.innerHeight) >= 600;
    
    // Debug logging for development
    // console.log('Tablet Detection:', {
    //   width: window.innerWidth,
    //   height: window.innerHeight,
    //   widthCheck,
    //   isIPad,
    //   userAgent: navigator.userAgent,
    //   platform: navigator.platform,
    //   maxTouchPoints: navigator.maxTouchPoints,
    //   aspectRatio,
    //   isTabletAspectRatio,
    //   result: widthCheck || isIPad || isTabletAspectRatio
    // });
    
    return widthCheck || isIPad || isTabletAspectRatio;
  };

  // Local state
  const [game, setGame] = useState(null);
  const [gameMetadata, setGameMetadata] = useState(null);
  const [fetchingGame, setFetchingGame] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTablet, setIsTablet] = useState(detectIsTablet());

  // Mobile-specific state  
  const [showGameInfo, setShowGameInfo] = useState(false);
  const [viewMode, setViewMode] = useState('View'); // 'View' or 'Record'
  const [autoPlaySpeed, setAutoPlaySpeed] = useState(7500); // milliseconds per move (legacy, can be removed)
  
  // Keep useChessThreadFont as local state since it's used elsewhere
  const [useChessThreadFont, setUseChessThreadFont] = useState(() => {
    const saved = localStorage.getItem('chess-use-chessthread-font');
    return saved ? JSON.parse(saved) : true; // Default to Mansalva
  });
  
  // Social interaction state
  const [showComments, setShowComments] = useState(false);
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [shareLink, setShareLink] = useState('');
  
  // Bookmark state
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showBookmarkModal, setShowBookmarkModal] = useState(false);
  
  // Comment functionality state
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [rateLimitError, setRateLimitError] = useState(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportCommentIndex, setReportCommentIndex] = useState(null);
  const [isCommentInputFocused, setIsCommentInputFocused] = useState(false);
  const [likingComments, setLikingComments] = useState(new Set()); // Track comments being liked for optimistic UI
  const [commentModeSource, setCommentModeSource] = useState(null); // Track how comment mode was initiated

  // Touch/swipe navigation state
  const [touchStartY, setTouchStartY] = useState(0);
  const [touchEndY, setTouchEndY] = useState(0);
  const [isNavigating, setIsNavigating] = useState(false);
  const [nextGameId, setNextGameId] = useState(null);
  const [prevGameId, setPrevGameId] = useState(null);
  
  // Smooth scroll animation state
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState(null); // 'horizontal' or 'vertical'
  
  // Comment swipe state
  const [commentTouchStartX, setCommentTouchStartX] = useState(0);
  const [commentTranslateX, setCommentTranslateX] = useState(0);
  const [isCommentSwiping, setIsCommentSwiping] = useState(false)

  // Stockfish analysis state
  const [stockfishInitialized, setStockfishInitialized] = useState(false);
  const [hasAnalysisCache, setHasAnalysisCache] = useState(false);
  const [currentAnalysisCache, setCurrentAnalysisCache] = useState(null);
  
  // Cache functions (will be properly implemented)
  const getCachedAnalysisForFEN = useCallback((fen) => {
    return null; // Placeholder
  }, []);
  
  const checkForCachedAnalysis = useCallback((moveId, fen) => {
    return null; // Placeholder
  }, []);

  // Use Stockfish analysis manager (similar to web version)
  const {
    stockfishAnalysis,
    stockfishIsAnalyzing,
    stockfishManager,
    handleStockfishAnalysisRequest
  } = useStockfishAnalysisManager(
    state.moves || [],
    state.currentMove,
    [getCachedAnalysisForFEN, checkForCachedAnalysis]
  );

  // Board sizing for mobile
  const [boardSize, setBoardSize] = useState(350);

  // Mobile annotation tool states (for board communication)
  const [mobileActiveColor, setMobileActiveColor] = useState(undefined);
  const [mobileArrowMode, setMobileArrowMode] = useState(false);
  const [mobileArrowColor, setMobileArrowColor] = useState(undefined);
  const [mobileArrowStart, setMobileArrowStart] = useState(null);
  
  // State for tracking which specific move is being actively edited
  const [activelyEditingMoveId, setActivelyEditingMoveId] = useState(null);
  
  // Username/subtitle editing state
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const recordUsernameInputRef = useRef(null);
  
  // Progress indicator icon state
  const [selectedProgressIcon, setSelectedProgressIcon] = useState('best');
  const [showIconSelector, setShowIconSelector] = useState(false);
  
  // TTS word animation state
  const [currentSpeakingWordIndex, setCurrentSpeakingWordIndex] = useState(-1);
  
  // Track intro overlay visibility (separate from start position)
  const [showIntroOverlay, setShowIntroOverlay] = useState(true);
  
  // Track intro text outro animation
  const [introTextSlideOut, setIntroTextSlideOut] = useState(false);
  
  // Custom TTS function for intro title with word tracking
  const speakIntroTitleWithWordTracking = useCallback(async (text, ttsRate = 1.0) => {
    if (!text) return;
    
    const words = text.split(' ');
    setCurrentSpeakingWordIndex(-1); // Reset before starting
    
    try {
      // Start TTS
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = ttsRate;
      
      // Estimate timing for word highlighting
      const avgWordsPerMinute = 150; // Typical TTS rate
      const adjustedWPM = avgWordsPerMinute * ttsRate;
      const msPerWord = 60000 / adjustedWPM;
      
      // Highlight each word in sequence
      words.forEach((word, index) => {
        setTimeout(() => {
          console.log(`[Word Animation] Highlighting word ${index}: "${word}"`);
          setCurrentSpeakingWordIndex(index);
        }, index * msPerWord);
      });
      
      // Clear highlighting when done (add 20% buffer)
      const totalDuration = words.length * msPerWord * 1.2;
      setTimeout(() => {
        setCurrentSpeakingWordIndex(-1);
      }, totalDuration);
      
      // Speak the text
      speechSynthesis.speak(utterance);
      
      return new Promise((resolve) => {
        utterance.onend = () => {
          setCurrentSpeakingWordIndex(-1);
          // Start slide-out animation
          setIntroTextSlideOut(true);
          // Hide intro overlay after slide animation completes (500ms animation + 50ms buffer)
          setTimeout(() => {
            setShowIntroOverlay(false);
            setIntroTextSlideOut(false); // Reset for next time
            resolve();
          }, 550);
        };
        utterance.onerror = () => {
          setCurrentSpeakingWordIndex(-1);
          // Start slide-out animation even on error
          setIntroTextSlideOut(true);
          // Hide intro overlay after slide animation completes
          setTimeout(() => {
            setShowIntroOverlay(false);
            setIntroTextSlideOut(false); // Reset for next time
            resolve();
          }, 550);
        };
      });
      
    } catch (error) {
      console.error('TTS error:', error);
      setCurrentSpeakingWordIndex(-1);
    }
  }, []);
  
  // Board ref for direct manipulation
  const boardRef = useRef(null);
  
  // Puzzle mode detection state (moved before hook call to fix initialization order)
  const [gameHasPuzzleTag, setGameHasPuzzleTag] = useState(false);
  
  // Puzzle-specific state
  const [puzzleIncorrectMove, setPuzzleIncorrectMove] = useState(false);
  const [puzzleComplete, setPuzzleComplete] = useState(false);
  const [puzzleProgress, setPuzzleProgress] = useState({ current: 0, total: 0 });
  
  // Use Record Mode Logic Hook (replaces duplicate logic)
  const recordModeLogic = useRecordModeLogic({
    // Core game data
    contextMoves,
    contextCurrentMove,
    contextOrientation,
    contextStarredMoves,
    state,
    gameMetadata,
    boardSize,
    
    // Navigation functions
    goToMove,
    
    // View control
    setViewMode,
    
    // Mobile settings
    useChessThreadFont,
    setUseChessThreadFont,
    
    // Board size calculator function
    calculateRecordModeBoardSize: () => window.calculateRecordModeBoardSize?.() || boardSize,
    
    // Additional required parameters
    stockfishAnalysis,
    stockfishIsAnalyzing,
    gameHasPuzzleTag,
    puzzleIncorrectMove,
    detectNewMove: (moveData) => {
      // Placeholder - this function should be implemented for puzzle mode
      // console.log('New move detected:', moveData);
    },
    mobileActiveColor,
    mobileArrowMode,
    mobileArrowColor,
    mobileArrowStart,
    boardRef,
    
    // Custom intro TTS function with word tracking
    customIntroTTSFunction: speakIntroTitleWithWordTracking,
    
    // Intro overlay control
    setShowIntroOverlay,
    
    // Intro text slide-out control
    setIntroTextSlideOut
  });

  // Destructure recording logic from hook (replaces duplicate state)
  const {
    recordSubTab,
    setRecordSubTab,
    isAutoPlaying,
    setIsAutoPlaying,
    totalRecordingTime,
    setTotalRecordingTime,
    currentMoveTime,
    setCurrentMoveTime,
    localAnnotationEdits,
    setLocalAnnotationEdits,
    
    // Move enhancements
    moveEnhancements,
    setMoveEnhancements,
    updateMoveEnhancement,
    getMoveEnhancement,
    clearMoveEnhancement,
    hasMoveEnhancements,
    
    // Title editing
    isEditingTitle,
    setIsEditingTitle,
    recordTitleInputRef,
    recordTextareaRef,
    
    // TTS
    voiceoverEnabled,
    setVoiceoverEnabled,
    ttsRate,
    setTtsRate,
    selectedVoiceIndex,
    setSelectedVoiceIndex,
    availableVoices,
    setAvailableVoices,
    isSpeaking,
    
    // Playback speed
    playbackSpeedMultiplier,
    setPlaybackSpeedMultiplier,
    
    // Background
    backgroundImage,
    setBackgroundImage,
    
    // Board zoom
    boardZoomConfig,
    setBoardZoomConfig,
    
    // Time adjustment functions
    adjustMoveTime,
    canDecreaseMoveTime,
    
    // Functions
    calculateMoveDisplayTime,
    calculateTotalRecordingTime,
    calculateCurrentMoveTime,
    handleBackgroundImageUpload,
    clearBackgroundImage,
    handleColorSwatchSelect,
    getSquarePosition,
    analyzeMoveShouldZoom,
    triggerBoardZoom,
    triggerMoveSpecificZoom,
    handleLocalAnnotationChange,
    toggleAutoPlay,
    
    // TTS functions
    ttsSpeak: speakMoveAnnotation,
    ttsStop: stopSpeaking,
    calculateMoveTime,
    
    // Refs
    contextCurrentMoveRef,
    contextMovesRef,
    localAnnotationEditsRef,
    autoPlayIntervalRef
  } = recordModeLogic;

  // Comment Mode state
  const [isCommentMode, setIsCommentMode] = useState(false);
  const [commentMoves, setCommentMoves] = useState([]);
  const [parentMoveIndex, setParentMoveIndex] = useState(null);
  const [commentStep, setCommentStep] = useState(1); // 1: moves, 2: caption
  const [showCustomization, setShowCustomization] = useState(false);
  const [commentCaption, setCommentCaption] = useState('');
  const [currentCommentMoveIndex, setCurrentCommentMoveIndex] = useState(-1); // -1 = before moves, 0+ = move index
  const [showConfirmation, setShowConfirmation] = useState(false); // For reusable confirmation subtab
  
  // Memoize FEN to prevent infinite re-analysis loops
  const memoizedFEN = useMemo(() => state.currentFEN, [state.currentFEN]);
  
  // Use unified Stockfish analysis for BestLineComponent (only when in comment mode)
  const {
    analysis: unifiedAnalysis,
    isAnalyzing: unifiedIsAnalyzing,
    error: unifiedError
  } = useUnifiedStockfishAnalysis(memoizedFEN, {
    enabled: isCommentMode // Only analyze when in comment mode
  });
  
  
  // State restoration approach: save game state when entering comment mode
  const [savedGameState, setSavedGameState] = useState(null);
  const [commentStartMoveIndex, setCommentStartMoveIndex] = useState(null);
  // Track variations created during comment mode for cleanup
  const [commentVariationPaths, setCommentVariationPaths] = useState([]);
  // Keep track of the last known good state before any moves
  const lastGoodStateRef = useRef(null);

  // Ensure clean start state
  useEffect(() => {
    // Force reset on mount
    setTranslateY(0);
    setSwipeDirection(null);
    setIsDragging(false);
    setTouchStartY(0);
  }, []);
  
  // Capture "last good state" whenever we're not in comment mode
  // This saves the state BEFORE any new moves are made
  useEffect(() => {
    if (!isCommentMode) {
      lastGoodStateRef.current = {
        moves: [...state.moves],
        startPosition: state.startPosition ? JSON.parse(JSON.stringify(state.startPosition)) : null,
        currentMove: contextCurrentMove,
        currentFEN: state.currentFEN
      };
      // console.log('📸 Captured last good state at position:', contextCurrentMove, {
      //   startPositionVariations: lastGoodStateRef.current.startPosition?.variations?.length || 0
      // });
    }
  }, [contextCurrentMove, state.moves, state.startPosition, state.currentFEN, isCommentMode]);

  // Update board size based on screen dimensions
  useEffect(() => {
    const updateBoardSize = () => {
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      
      // Calculate optimal board size for mobile and tablets
      // Use the comprehensive tablet detection
      const isTabletDevice = detectIsTablet();
      setIsTablet(isTabletDevice);
      
      // For tablets/iPads, use MUCH smaller board to fit everything
      if (isTabletDevice) {
        // BE AGGRESSIVE - board is SQUARE so 450px = 450px tall!
        // UI elements need ~400-500px, so board can only be ~300-350px MAX
        let boardSize = 305; // HARD CAP - small enough to fit with all UI
        
        console.log('iPad Board Size (AGGRESSIVE):', {
          screenHeight,
          screenWidth,
          forcedBoardSize: boardSize,
          isTabletDevice
        });
        
        setBoardSize(boardSize);
        return;
      }
      
      // Original phone calculation
      const uiElementsHeight = 330;
      const availableHeight = screenHeight - uiElementsHeight;
      const maxBoardWidth = Math.min(screenWidth - 20, 400);
      const maxBoardHeight = Math.max(availableHeight, 280);
      
      let optimalSize = Math.min(maxBoardWidth, maxBoardHeight);
      setBoardSize(Math.max(optimalSize, 280));
    };

    updateBoardSize();
    window.addEventListener('resize', updateBoardSize);
    return () => window.removeEventListener('resize', updateBoardSize);
  }, []);


  // Expose comment moves status globally for bottom navigation access
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Only consider moves "unsaved" when in active comment mode with pending moves
      const hasUnsavedCommentMoves = commentMoves.length > 0 && isCommentMode;
      window.mobileViewGameHasCommentMoves = hasUnsavedCommentMoves;
    }
    
    // Cleanup on unmount
    return () => {
      if (typeof window !== 'undefined') {
        window.mobileViewGameHasCommentMoves = false;
      }
    };
  }, [commentMoves.length, isCommentMode]);

  // Disable keyboard hotkeys when comment input is focused
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isCommentInputFocused) {
        // Block common hotkeys when typing in comment field
        if (e.key === ' ' || // Space bar
            e.key === 'ArrowLeft' || 
            e.key === 'ArrowRight' || 
            e.key === 'ArrowUp' || 
            e.key === 'ArrowDown') {
          e.stopPropagation(); // Prevent the event from reaching other handlers
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true); // Use capture phase
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isCommentInputFocused]);

  // Handle click outside textarea to exit editing mode
  useEffect(() => {
    if (!activelyEditingMoveId) return;

    const handleClickOutside = (e) => {
      // Check if the click was outside the textarea
      if (recordTextareaRef.current && !recordTextareaRef.current.contains(e.target)) {
        // Only close if there's no content
        const currentMoveId = activelyEditingMoveId;
        const localEdits = localAnnotationEdits[currentMoveId] || {};
        const hasContent = localEdits.content !== undefined ? localEdits.content : '';
        
        if (!hasContent || hasContent.trim() === '') {
          setActivelyEditingMoveId(null);
        }
      }
    };

    // Add event listener with a small delay to avoid immediate closure
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [activelyEditingMoveId, localAnnotationEdits]);

  // Focus username input when editing starts
  useEffect(() => {
    if (isEditingUsername && recordUsernameInputRef.current) {
      recordUsernameInputRef.current.focus();
      recordUsernameInputRef.current.select();
    }
  }, [isEditingUsername]);

  // Close icon selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showIconSelector && !e.target.closest('.icon-selector-container')) {
        setShowIconSelector(false);
      }
    };

    if (showIconSelector) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showIconSelector]);

  // Add custom styles for shorter control buttons
  useEffect(() => {
    const styleId = 'mobile-controls-styles';
    
    // Remove existing styles if any
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }

    // Add new styles
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .mobile-controls-override button {
        height: 40px !important;
        font-size: 16px !important;
        min-height: 40px !important;
      }
    `;
    document.head.appendChild(style);

    // Cleanup on unmount
    return () => {
      const styleElement = document.getElementById(styleId);
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, []);

  // Initialize Stockfish engine for mobile analysis
  useEffect(() => {
    const initializeStockfish = async () => {
      try {
        if (stockfishEngine && !stockfishInitialized) {
          await stockfishEngine.initialize();
          setStockfishInitialized(true);
          
          // Expose analysis cache for the engine
          window.currentAnalysisCache = currentAnalysisCache;
          window.stockfishAnalysisManager = stockfishManager;
          
          // Add event listener for analysis requests from the board
          const handleAnalysisRequest = (event) => {
            if (event.detail && event.detail.fen) {
              handleStockfishAnalysisRequest(event.detail.fen);
            }
          };
          
          window.addEventListener('request-stockfish-analysis', handleAnalysisRequest);
          
          return () => {
            window.removeEventListener('request-stockfish-analysis', handleAnalysisRequest);
            window.currentAnalysisCache = null;
            window.stockfishAnalysisManager = null;
          };
        }
      } catch (error) {
        console.error('Failed to initialize Stockfish engine for mobile:', error);
      }
    };

    initializeStockfish();
  }, [stockfishInitialized, currentAnalysisCache, stockfishManager, handleStockfishAnalysisRequest]);

  // Helper function to check for tags
  const checkForTag = useCallback((game, tagToCheck) => {
    if (Array.isArray(game?.tags)) {
      return game.tags.some(tag => typeof tag === 'string' && tag.toUpperCase() === tagToCheck);
    }
    if (typeof game?.tag === 'string' && game.tag.toUpperCase() === tagToCheck) {
      return true;
    }
    return false;
  }, []);

  // Check if current game is bookmarked
  const checkIfGameIsBookmarked = useCallback(async () => {
    if (!user?.credentials?.userId || !gameId) return;
    
    try {
      const userId = user.credentials.userId;
      
      // Query all bookmark folders for this user
      const folderQuery = query(
        collection(firestore, "bookmarkFolders"),
        where("userId", "==", userId)
      );
      
      const folderSnapshot = await getDocs(folderQuery);
      
      // Check if gameId exists in any folder's games array
      for (const folderDoc of folderSnapshot.docs) {
        const folderData = folderDoc.data();
        const games = folderData.games || [];
        
        if (games.some(game => game.gameId === gameId)) {
          setIsBookmarked(true);
          return;
        }
      }
      
      setIsBookmarked(false);
    } catch (error) {
      console.error('Error checking bookmark status:', error);
      setIsBookmarked(false);
    }
  }, [user, gameId]);

  // Fetch game data
  useEffect(() => {
    const loadGame = async () => {
      if (!gameId || fetchingGame) {
        return;
      }

      // Check if game is already loaded
      if (gameMetadata && (gameMetadata.id === gameId || gameMetadata.gameId === gameId)) {
        return;
      }

      setFetchingGame(true);
      setIsLoading(true);
      setError(null);

      try {
        const gameData = await fetchGame(gameId);

        if (!gameData) {
          throw new Error("Failed to load game - empty response");
        }

        if (gameData.error) {
          throw new Error(gameData.error);
        }

        // Create Chess instance
        const newGame = new Chess(gameData.fen);

        // Apply moves to the chess instance
        if (gameData.moves && Array.isArray(gameData.moves)) {
          gameData.moves.forEach((move) => {
            try {
              if (move.notation) newGame.move(move.notation);
            } catch (e) {
              console.error('Error applying move:', move.notation, e);
            }
          });
        }

        setGame(newGame);

        // Initialize game state
        const startPosition = gameData?.startPosition || createStartPosition(gameData?.fen);
        
        const safeGameData = {
          fen: gameData?.fen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
          orientation: gameData?.pov === undefined || gameData?.pov ? 'white' : 'black',
          moves: Array.isArray(gameData?.moves) ? gameData.moves : [],
          starredMoves: Array.isArray(gameData?.starredMoves) ? gameData.starredMoves : [],
          startPosition: startPosition
        };

        dispatch({ 
          type: 'INITIALIZE_GAME', 
          payload: safeGameData
        });

        // Process game metadata
        let processedGame = { ...gameData };
        
        // Ensure creatorHandle exists
        if (!processedGame.creatorHandle) {
          if (processedGame.creator && typeof processedGame.creator === 'string' && !processedGame.creator.includes(' ')) {
            processedGame.creatorHandle = processedGame.creator;
          } else {
            processedGame.creatorHandle = 'Anonymous';
          }
        }

        setGameMetadata(processedGame);
        
        // Check if the game has puzzle tag (copied from desktop ViewGame)
        const hasPuzzleTag = checkForTag(processedGame, 'PUZZLE');
        setGameHasPuzzleTag(hasPuzzleTag);
        
        // Reset puzzle state for puzzles
        if (hasPuzzleTag) {
          setPuzzleIncorrectMove(false);
          setPuzzleComplete(false);
        }
        
        // Load comments from game metadata
        if (processedGame.comments) {
          setComments(processedGame.comments);
        } else {
          setComments([]);
        }

        // Check if game is bookmarked
        await checkIfGameIsBookmarked();

      } catch (error) {
        console.error("Error loading game:", error);
        
        // Provide more specific error messages
        let errorMessage = error.message;
        if (error.response?.status === 500) {
          errorMessage = "Server error - please try again later";
        } else if (error.response?.status === 404) {
          errorMessage = "Game not found";
        } else if (error.response?.status === 403) {
          errorMessage = "Access denied - please log in";
        } else if (!error.response) {
          errorMessage = "Network error - check your connection";
        }
        
        setError(errorMessage);
      } finally {
        setFetchingGame(false);
        setIsLoading(false);
      }
    };

    loadGame();
  }, [gameId, dispatch]);

  // Fetch next/prev game IDs for navigation
  useEffect(() => {
    const fetchNavigationGames = async () => {
      if (!gameId || !gameMetadata) return;

      try {
        // Extract navigation context from location.state (same as web version)
        const origin = location.state?.origin;
        const stateGameIds = location.state?.gameIds;
        
        // Set collection-based navigation if we have gameIds array from state
        if (stateGameIds && Array.isArray(stateGameIds)) {
          const currentIndex = stateGameIds.indexOf(gameId);
          if (currentIndex !== -1) {
            const nextId = currentIndex < stateGameIds.length - 1 ? stateGameIds[currentIndex + 1] : null;
            const prevId = currentIndex > 0 ? stateGameIds[currentIndex - 1] : null;
            
            setNextGameId(nextId);
            setPrevGameId(prevId);
            // Don't return - continue to fetch API metadata for boundary fallback
          }
        }

        // TODO: IMPERFECT SOLUTION - This fetches API metadata for every game load
        // even when we have collection context. This is inefficient and should be optimized.
        // Consider: Only fetch API metadata when approaching collection boundaries (game 8-9)
        // or defer metadata fetch until first boundary crossing attempt.
        
        // Always fetch API-based next/prev games for metadata fallback (like desktop)
        const searchParams = new URLSearchParams(location.search);
        const tag = searchParams.get('tag');
        const orderBy = searchParams.get('orderBy');
        const userId = searchParams.get('userId');

        // Fetch next and previous games in parallel
        const [nextGame, prevGame] = await Promise.all([
          fetchNextGame(gameId, orderBy, userId, tag).catch(() => null),
          fetchPrevGame(gameId, orderBy, userId, tag).catch(() => null)
        ]);

        // Always populate gameMetadata for desktop-style fallback navigation
        setGameMetadata(metadata => ({
          ...metadata,
          nextGameId: nextGame?.id || null,
          prevGameId: prevGame?.id || null
        }));

        // If no collection context, also update state
        if (!stateGameIds || !Array.isArray(stateGameIds)) {
          setNextGameId(nextGame?.id || null);
          setPrevGameId(prevGame?.id || null);
        }

      } catch (error) {
        console.error("Error fetching navigation games:", error);
      }
    };

    fetchNavigationGames();
  }, [gameId, gameMetadata, location.state, location.search]);

  // Reset navigation state when component loads
  useEffect(() => {
    setIsNavigating(false);
    setTranslateY(0); // Reset position
    setIsDragging(false);
  }, [gameId]);

  // Puzzle utility functions (moved here to avoid temporal dead zone)
  const calculatePuzzleProgress = useCallback((currentPath, moves) => {
    if (!moves || moves.length === 0) return { current: 0, total: 0 };
    
    try {
      // Get current move index in main line
      let currentMoveIndex = -1; // Start at -1 to represent starting position
      if (currentPath !== '-1' && !currentPath.includes('|')) {
        currentMoveIndex = parseInt(currentPath);
      }
      
      // Count total puzzle moves (even-indexed moves: 0, 2, 4, 6... - player's moves)
      const totalPuzzleMoves = Math.ceil(moves.length / 2);
      
      // Count current puzzle moves completed
      let currentPuzzleMoves = 0;
      for (let i = 0; i < moves.length; i += 2) { // Only check even indices (player's moves)
        if (i <= currentMoveIndex) {
          currentPuzzleMoves++;
        }
      }
      
      return {
        current: currentPuzzleMoves,
        total: totalPuzzleMoves
      };
    } catch (error) {
      console.error('Error calculating puzzle progress:', error);
      return { current: 0, total: 0 };
    }
  }, []);
  
  // Check if a move is the correct next puzzle solution move
  const isPuzzleMoveCorrect = useCallback((notation, currentPath, moves) => {
    if (!moves || moves.length === 0) return false;
    
    try {
      // Get the expected next move in main line
      let nextMoveIndex = 0;
      if (currentPath === '-1') {
        nextMoveIndex = 0; // First move
      } else if (!currentPath.includes('|')) {
        nextMoveIndex = parseInt(currentPath) + 1;
      } else {
        // If we're in a variation, puzzle moves are only valid in main line
        return false;
      }
      
      // Check if there's a next move and if it matches
      if (nextMoveIndex < moves.length) {
        const expectedMove = moves[nextMoveIndex];
        return expectedMove && expectedMove.notation === notation;
      }
      
      return false;
    } catch (error) {
      console.error('Error checking puzzle move:', error);
      return false;
    }
  }, []);
  
  // Auto-play opponent response after correct puzzle move
  const handleOpponentAutoPlay = useCallback((currentPath) => {
    try {
      // Get the opponent's response move index (just +1 from current move)
      let opponentMoveIndex = 0;
      if (currentPath === '-1') {
        opponentMoveIndex = 1; // If we're at start, opponent plays move 1
      } else if (!currentPath.includes('|')) {
        opponentMoveIndex = parseInt(currentPath) + 1; // Next move is opponent's
      }
      
      // console.log('Auto-playing opponent move from', currentPath, 'to', opponentMoveIndex);
      
      // Check if opponent move exists
      if (state.moves && opponentMoveIndex < state.moves.length) {
        setTimeout(() => {
          // console.log('Executing opponent autoplay to move:', opponentMoveIndex);
          goToMove(opponentMoveIndex.toString());
        }, 500); // 500ms delay matching desktop
      } else {
        // Puzzle complete - no more opponent moves
        // console.log('Puzzle complete - no more moves');
        setPuzzleComplete(true);
      }
      
    } catch (error) {
      console.error('Error in opponent autoplay:', error);
    }
  }, [state.moves, goToMove]);

  // Puzzle progress tracking
  useEffect(() => {
    if (gameHasPuzzleTag && state.moves) {
      const progress = calculatePuzzleProgress(contextCurrentMove, state.moves);
      setPuzzleProgress(progress);
    }
  }, [gameHasPuzzleTag, contextCurrentMove, state.moves, calculatePuzzleProgress]);

  // Helper function to get tag display
  const getTagDisplay = (tag) => {
    if (!tag) return '';
    
    if (tag.startsWith('Opening') && tag.includes(' - ')) {
      return tag.split(' - ')[1];
    }
    
    return TAGS[tag]?.label || tag;
  };

  // Helper function to get tag styling
  const getTagStyle = (tag) => {
    if (tag && tag.startsWith('Opening') && tag.includes(' - ')) {
      return {
        color: 'black',
        background: 'gold'
      };
    }
    
    return {
      color: TAGS[tag]?.color || '#FFFFFF',
      background: TAGS[tag]?.background || '#3066BE'
    };
  };

  // Constants for score weights
  const LIKE_WEIGHT = 4;
  const DISLIKE_WEIGHT = -2;

  // Handle like click
  const handleLikeClick = async () => {
    if (!user?.authenticated || !user?.credentials?.userId || !gameId || !gameMetadata) return;
    
    const userId = user.credentials.userId;
    const gameRef = doc(firestore, "games", gameId);
    
    try {
      // If already liked, remove the like
      if (gameMetadata?.likes?.includes(userId)) {
        await updateDoc(gameRef, {
          likes: arrayRemove(userId),
          score: increment(-LIKE_WEIGHT)
        });
        
        // Delete the like notification when unliking
        await deleteLikeNotification();
        
        // Update local state
        setGameMetadata(prev => ({
          ...prev,
          likes: prev.likes.filter(id => id !== userId),
          score: (prev.score || 0) - LIKE_WEIGHT
        }));
      } else {
        // Add like and remove dislike if present
        const updates = {
          likes: arrayUnion(userId),
          score: increment(LIKE_WEIGHT)
        };
        
        if (gameMetadata?.dislikes?.includes(userId)) {
          updates.dislikes = arrayRemove(userId);
          updates.score = increment(LIKE_WEIGHT - DISLIKE_WEIGHT); // Adjust for removing dislike
        }
        
        await updateDoc(gameRef, updates);
        
        // Create notification for the post creator when liking
        const creatorHandle = gameMetadata?.creatorHandle;
        if (creatorHandle) {
          await createLikeNotification(creatorHandle);
        }
        
        // Update local state
        setGameMetadata(prev => {
          const newState = {
            ...prev,
            likes: [...(prev.likes || []), userId],
            score: (prev.score || 0) + LIKE_WEIGHT
          };
          
          if (prev.dislikes?.includes(userId)) {
            newState.dislikes = prev.dislikes.filter(id => id !== userId);
            newState.score += Math.abs(DISLIKE_WEIGHT);
          }
          
          return newState;
        });
      }
    } catch (error) {
      console.error('Error liking game:', error);
    }
  };

  // Handle dislike click
  const handleDislikeClick = async () => {
    if (!user?.authenticated || !user?.credentials?.userId || !gameId || !gameMetadata) return;
    
    const userId = user.credentials.userId;
    const gameRef = doc(firestore, "games", gameId);
    
    try {
      // If already disliked, remove the dislike
      if (gameMetadata?.dislikes?.includes(userId)) {
        await updateDoc(gameRef, {
          dislikes: arrayRemove(userId),
          score: increment(-DISLIKE_WEIGHT)
        });
        
        // Update local state
        setGameMetadata(prev => ({
          ...prev,
          dislikes: prev.dislikes.filter(id => id !== userId),
          score: (prev.score || 0) - DISLIKE_WEIGHT
        }));
      } else {
        // Add dislike and remove like if present
        const updates = {
          dislikes: arrayUnion(userId),
          score: increment(DISLIKE_WEIGHT)
        };
        
        if (gameMetadata?.likes?.includes(userId)) {
          updates.likes = arrayRemove(userId);
          updates.score = increment(DISLIKE_WEIGHT - LIKE_WEIGHT); // Adjust for removing like
        }
        
        await updateDoc(gameRef, updates);
        
        // Delete the like notification when disliking a post that was liked
        if (gameMetadata?.likes?.includes(userId)) {
          await deleteLikeNotification();
        }
        
        // Update local state
        setGameMetadata(prev => {
          const newState = {
            ...prev,
            dislikes: [...(prev.dislikes || []), userId],
            score: (prev.score || 0) + DISLIKE_WEIGHT
          };
          
          if (prev.likes?.includes(userId)) {
            newState.likes = prev.likes.filter(id => id !== userId);
            newState.score -= LIKE_WEIGHT;
          }
          
          return newState;
        });
      }
    } catch (error) {
      console.error('Error disliking game:', error);
    }
  };

  // Notification functions
  const createCommentNotification = async (creatorHandle, commentContent) => {
    // Don't proceed if no creator handle is available
    if (!creatorHandle) {
      console.error("Cannot create notification: Post creator handle is missing");
      return;
    }

    const authenticatedUserHandle = user?.credentials?.handle;

    // Don't create notification if commenting on your own post
    if (creatorHandle === authenticatedUserHandle) {
      return;
    }

    try {
      // Add a new document with a generated id
      const docRef = await addDoc(collection(firestore, "notifications"), {
        type: "new_comment",
        commentBy: authenticatedUserHandle,
        commentTo: creatorHandle,
        targetUser: creatorHandle, // Add targetUser field for efficient querying
        gameId: gameId, // Add gameId to allow navigation to the specific game
        commentContent: commentContent, // Add the actual comment content
        status: "unread",
        timestamp: new Date(),
      });

      // console.log("Comment notification created with ID: ", docRef.id);
    }
    catch (error) {
      console.error("Error creating comment notification:", error);
    }
  };

  // Function to create a like notification in Firestore
  const createLikeNotification = async (creatorHandle) => {
    // Don't proceed if no creator handle is available
    if (!creatorHandle) {
      console.error("Cannot create like notification: Post creator handle is missing");
      return;
    }

    const authenticatedUserHandle = user?.credentials?.handle;

    // Don't create notification if liking your own post
    if (creatorHandle === authenticatedUserHandle) {
      return;
    }

    try {
      // Add a new document with a generated id
      const docRef = await addDoc(collection(firestore, "notifications"), {
        type: "new_like",
        likedBy: authenticatedUserHandle,
        likedTo: creatorHandle,
        targetUser: creatorHandle, // Add targetUser field for efficient querying
        gameId: gameId, // Add gameId to allow navigation to the specific game
        status: "unread",
        timestamp: new Date(),
      });

      // console.log("Like notification created with ID: ", docRef.id);
      return docRef.id; // Return the ID for later reference if needed
    }
    catch (error) {
      console.error("Error creating like notification:", error);
      return null;
    }
  };

  // Function to delete a like notification when a user unlikes a post
  const deleteLikeNotification = async () => {
    const authenticatedUserHandle = user?.credentials?.handle;

    try {
      // Query for notifications matching this user, game and type
      const q = query(
        collection(firestore, "notifications"),
        where("likedBy", "==", authenticatedUserHandle),
        where("gameId", "==", gameId),
        where("type", "==", "new_like")
      );

      const querySnapshot = await getDocs(q);

      // Check if we found any documents
      if (querySnapshot.empty) {
        // console.log("No like notifications found to delete");
        return;
      }

      // Delete all matching notifications
      const deletePromises = [];
      querySnapshot.forEach((document) => {
        deletePromises.push(deleteDoc(document.ref));
      });

      await Promise.all(deletePromises);
      // console.log("Like notifications deleted successfully");
    }
    catch (error) {
      console.error("Error deleting like notification:", error);
    }
  };
  
  // Handle comment like
  const handleCommentLike = async (commentIndex) => {
    if (!user?.authenticated || !user?.credentials?.userId || !gameId) return;
    
    const userId = user.credentials.userId;
    const gameRef = doc(firestore, "games", gameId);
    const commentId = comments[commentIndex]?.id || `temp-${commentIndex}`;
    
    // Optimistic UI update
    setLikingComments(prev => new Set([...prev, commentId]));
    
    // Optimistically update local state
    const updatedComments = JSON.parse(JSON.stringify(comments));
    const commentToUpdate = updatedComments[commentIndex];
    
    if (!Array.isArray(commentToUpdate.likes)) {
      commentToUpdate.likes = [];
    }
    
    const wasLiked = commentToUpdate.likes.includes(userId);
    if (wasLiked) {
      commentToUpdate.likes = commentToUpdate.likes.filter(id => id !== userId);
    } else {
      commentToUpdate.likes.push(userId);
    }
    
    // Update local state immediately for responsive UI
    setComments(updatedComments);
    
    try {
      // Get current game data
      const docSnap = await getDoc(gameRef);
      if (!docSnap.exists()) return;
      
      const gameData = docSnap.data();
      const commentsArray = gameData.comments || [];
      
      if (commentIndex >= 0 && commentIndex < commentsArray.length) {
        const serverUpdatedComments = JSON.parse(JSON.stringify(commentsArray));
        const serverCommentToUpdate = serverUpdatedComments[commentIndex];
        
        // Initialize likes array if it doesn't exist
        if (!Array.isArray(serverCommentToUpdate.likes)) {
          serverCommentToUpdate.likes = [];
        }
        
        // Toggle like on server data
        if (serverCommentToUpdate.likes.includes(userId)) {
          serverCommentToUpdate.likes = serverCommentToUpdate.likes.filter(id => id !== userId);
        } else {
          serverCommentToUpdate.likes.push(userId);
        }
        
        // Update in Firestore
        await updateDoc(gameRef, {
          comments: serverUpdatedComments
        });
        
        // Update local state with server response
        setComments(serverUpdatedComments);
      }
    } catch (error) {
      console.error('Error liking comment:', error);
      // Revert optimistic update on error
      setComments(comments);
    } finally {
      // Remove from liking state
      setLikingComments(prev => {
        const newSet = new Set(prev);
        newSet.delete(commentId);
        return newSet;
      });
    }
  };

  // Handle comment report
  const handleCommentReport = (commentIndex) => {
    setReportCommentIndex(commentIndex);
    setReportModalOpen(true);
  };

  // Handle report submission
  const handleReportSubmit = async (reportType) => {
    if (!user?.authenticated || !user?.credentials?.userId || !gameId || reportCommentIndex === null) return;
    
    const userId = user.credentials.userId;
    const gameRef = doc(firestore, "games", gameId);
    
    try {
      // Get current game data
      const docSnap = await getDoc(gameRef);
      if (!docSnap.exists()) return;
      
      const gameData = docSnap.data();
      const commentsArray = gameData.comments || [];
      
      if (reportCommentIndex >= 0 && reportCommentIndex < commentsArray.length) {
        const updatedComments = JSON.parse(JSON.stringify(commentsArray));
        const commentToUpdate = updatedComments[reportCommentIndex];
        
        // Create report object
        const reportObject = {
          userId: userId,
          reportType: reportType || 'general',
          timestamp: new Date().toISOString()
        };
        
        // Initialize reports array if it doesn't exist
        if (!Array.isArray(commentToUpdate.reports)) {
          commentToUpdate.reports = [];
        }
        
        // Add the report
        commentToUpdate.reports.push(reportObject);
        
        // Update in Firestore
        await updateDoc(gameRef, {
          comments: updatedComments
        });
        
        // Update local state
        setComments(updatedComments);
      }
      
      // Close modal
      setReportModalOpen(false);
      setReportCommentIndex(null);
    } catch (error) {
      console.error('Error reporting comment:', error);
    }
  };

  // Helper function to find comment variations
  const findCommentVariations = (commentId, moves) => {
    const commentVariations = [];
    
    const scanForCommentMoves = (movesArray, path = "") => {
      if (!movesArray || !movesArray.length) return;
      
      movesArray.forEach((move, moveIndex) => {
        const currentPath = path ? `${path}${moveIndex}` : `${moveIndex}`;
        
        // Check variations recursively
        if (move.variations && move.variations.length) {
          move.variations.forEach((variation, varIndex) => {
            // Check if this variation contains comment moves by this comment's creator
            const hasCommentByThisUser = variation.some(varMove => 
              varMove?.creator?.type === constants.CHESSBOARD.MOVE_TYPES.COMMENT && 
              varMove?.creator?.id === commentId
            );
            
            if (hasCommentByThisUser) {
              const firstCommentMove = variation.find(varMove => 
                varMove?.creator?.type === constants.CHESSBOARD.MOVE_TYPES.COMMENT &&
                varMove?.creator?.id === commentId
              );
              
              if (firstCommentMove) {
                commentVariations.push({
                  parentMove: move,
                  parentMoveIndex: moveIndex,
                  variationIndex: varIndex,
                  variation: variation,
                  firstCommentMove: firstCommentMove,
                  parentMoveId: currentPath,
                  variationMoveId: `${currentPath}|${varIndex}:0`
                });
              }
            }
            
            // Continue scanning nested variations
            scanForCommentMoves(variation, `${currentPath}|${varIndex}:`);
          });
        }
      });
    };
    
    scanForCommentMoves(moves);
    return commentVariations;
  };

  // Helper function to check if a comment has variations
  const commentHasVariations = (commentId, moves) => {
    return findCommentVariations(commentId, moves).length > 0;
  };

  // Helper function to check if a comment is move-linked
  const isCommentMoveLinked = (comment) => {
    return comment.moveIndex !== undefined && comment.moveIndex !== null;
  };

  // Helper function to enrich comments with move information
  const enrichComments = (comments, moves) => {
    if (!comments || !Array.isArray(comments)) return [];
    
    return comments.map(comment => {
      const hasVariationMoves = commentHasVariations(comment.id, moves);
      const isMoveLinked = isCommentMoveLinked(comment);
      
      return {
        ...comment,
        hasVariationMoves,
        isMoveLinked,
        hasMoves: hasVariationMoves || isMoveLinked,
        likesCount: comment.likes ? comment.likes.length : 0
      };
    });
  };

  // Helper function to check if comment is linked to current move
  const isCurrentMoveComment = (comment, currentMove, moves) => {
    // Direct move link match
    if (comment.moveIndex === currentMove) return true;
    
    // Check if comment has variations that include the current move
    if (comment.hasVariationMoves) {
      const variations = findCommentVariations(comment.id, moves);
      return variations.some(variation => {
        // Check if current move is the parent move of this variation
        return variation.parentMoveId === currentMove || 
               variation.variationMoveId === currentMove;
      });
    }
    
    return false;
  };

  // Helper function to get sorted comments for mobile (prioritizes current move comments)
  const getSortedCommentsForMobile = (comments, moves, currentMove = null) => {
    const enrichedComments = enrichComments(comments, moves);
    
    // Separate into three priority groups
    const currentMoveComments = enrichedComments.filter(comment => 
      currentMove && isCurrentMoveComment(comment, currentMove, moves)
    );
    const otherWithMoves = enrichedComments.filter(comment => 
      comment.hasMoves && (!currentMove || !isCurrentMoveComment(comment, currentMove, moves))
    );
    const withoutMoves = enrichedComments.filter(comment => !comment.hasMoves);
    
    // Sort each group by premium status and likes (using desktop utility)
    const sortedCurrentMoveComments = sortCommentsByUserPremium(currentMoveComments);
    const sortedOtherWithMoves = sortCommentsByUserPremium(otherWithMoves);
    const sortedWithoutMoves = sortCommentsByUserPremium(withoutMoves);
    
    // Combine: current move comments first, other comments with moves, then comments without moves
    return [...sortedCurrentMoveComments, ...sortedOtherWithMoves, ...sortedWithoutMoves];
  };

  // Helper function to render moves preview button
  const renderMovesPreviewButton = (commentId, moves) => {
    const variations = findCommentVariations(commentId, moves);
    
    if (variations.length === 0) return null;
    
    // For now, show the first variation found
    const variation = variations[0];
    
    // Calculate move number for display using cumulative calculation
    const parentCumulativeIndex = calculateCumulativeMoveIndex(variation.parentMoveId);
    const baseNumber = Math.floor(parentCumulativeIndex / 2) + 1;
    const isWhiteMove = parentCumulativeIndex % 2 === 0;
    const moveNum = isWhiteMove ? baseNumber : `${baseNumber}...`;
    
    return (
      <div style={{
        marginTop: '8px',
        padding: '8px',
        backgroundColor: '#333',
        borderRadius: '8px',
        border: '1px solid #ff6600',
        width: 'fit-content'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '12px'
        }}>
          <span style={{ color: '#ff6600', fontWeight: 'bold' }}>Variation:</span>
          
          {/* Parent Move Button */}
          <button
            onClick={() => {
              dispatch({ type: 'GOTO_MOVE', payload: variation.parentMoveId });
              setShowComments(false);
            }}
            style={{
              background: contextCurrentMove === variation.parentMoveId ? '#58b047' : '#666',
              color: contextCurrentMove === variation.parentMoveId ? 'black' : 'white',
              border: 'none',
              padding: '4px 8px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: 'bold'
            }}
          >
            {moveNum}{moveNum.toString().includes('...') ? '' : '.'} {variation.parentMove.notation}
          </button>
          
          {/* Branch Icon */}
          <i className="fa-solid fa-code-branch fa-rotate-90" style={{ color: '#ff6600', fontSize: '12px' }}></i>
          
          {/* Variation Move Button */}
          <button
            onClick={() => {
              dispatch({ type: 'GOTO_MOVE', payload: variation.variationMoveId });
              setShowComments(false);
            }}
            style={{
              background: contextCurrentMove === variation.variationMoveId ? '#58b047' : '#666',
              color: contextCurrentMove === variation.variationMoveId ? 'black' : 'white',
              border: 'none',
              padding: '4px 8px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: 'bold'
            }}
          >
            {variation.firstCommentMove.notation}
          </button>
          
          {/* More moves indicator */}
          {variation.variation.length > 1 && (
            <span style={{ color: '#666', fontSize: '11px' }}>...</span>
          )}
        </div>
      </div>
    );
  };
  
  // Mobile version of captureMoveData (similar to desktop AddComment2.jsx)
  const captureMobileMoveData = (linkToMove) => {
    if (!linkToMove) return null;

    try {
      // Get current move from the game state
      const currentMoveObj = getMove(contextMoves, contextCurrentMove);
      const moveNotation = currentMoveObj?.notation || '';
      
      // Get current FEN from game state
      const moveFEN = getCurrentBoardPosition(contextMoves, contextCurrentMove);
      
      // Get move squares for highlighting (from the current move object)
      const moveFromSquare = currentMoveObj?.from || '';
      const moveToSquare = currentMoveObj?.to || '';
      
      // Get parent move index for variations (using current move as base)
      const parentMoveIndex = contextCurrentMove;
      
      return {
        moveIndex: contextCurrentMove,
        moveNotation: moveNotation,
        moveFEN: moveFEN,
        moveFromSquare: moveFromSquare,
        moveToSquare: moveToSquare,
        parentMoveIndex: parentMoveIndex
      };
    } catch (error) {
      console.error('[ViewGameMobile] Error capturing move data:', error);
      return null;
    }
  };

  // Handle comment submission - enhanced to support move linking
  const handleAddComment = useCallback(async (commentContent, moveData = null, preservePosition = true) => {
    if (!user?.authenticated || !user?.credentials?.userId || !gameId || !gameMetadata) {
      console.error('User not authenticated or missing data');
      return;
    }
    
    if (!commentContent || !commentContent.trim()) {
      console.error('Comment content is required');
      return;
    }
    
    const userId = user.credentials.userId;
    
    // Rate limiting check
    const userTier = 'free'; // TODO: Get actual user tier from user context
    const config = getRateLimit('COMMENT', userTier);
    const rateKey = buildRateKey(userId, gameId, 'comment');
    
    const isLimited = checkRateLimit({
      key: rateKey,
      limit: config.limit,
      windowMs: config.windowMs
    });

    if (isLimited) {
      setRateLimitError("Please wait before commenting again.");
      return;
    }
    
    // Clear any previous rate limit error
    setRateLimitError(null);
    
    const content = commentContent.trim();
    const commentId = comments.length || 0;
    const username = user.credentials.handle;
    
    setSubmittingComment(true);
    
    // Store current position to preserve it after comment submission
    const currentPosition = preservePosition ? contextCurrentMove : null;
    
    try {
      // Clean profanity from comment content
      const cleanedContent = cleanProfanity(content);
      
      // Create comment object with optional move data
      const commentObject = {
        id: commentId,
        user: userId,
        content: cleanedContent,
        username: username,
        likes: [],
        createdAt: new Date().toISOString(),
        // Add move data if linking to current move
        ...(moveData && {
          moveIndex: moveData.moveIndex,
          moveNotation: moveData.moveNotation,
          moveFEN: moveData.moveFEN,
          moveFromSquare: moveData.moveFromSquare,
          moveToSquare: moveData.moveToSquare
        })
      };
      
      // Always update moves with comment ID for both text comments and variation comments
      // This matches desktop behavior and enables proper variation previews
      // console.log(`[CommentBug] handleAddComment: contextMoves before updateCommentIdInMoves:`, contextMoves);
      // console.log(`[CommentBug] handleAddComment: commentId to assign:`, commentId);
      
      // Look for comment moves with id=-1 before update
      const commentMovesBeforeUpdate = [];
      const findCommentMoves = (moves, path = '') => {
        moves.forEach((move, index) => {
          if (move?.creator?.type === 'COMMENT' && move?.creator?.id === -1) {
            commentMovesBeforeUpdate.push({ 
              path: path + index, 
              move: move,
              content: move.content,
              annotations: {
                content: move.content,
                arrows: move.arrows,
                highlightedSquares: move.highlightedSquares
              },
              detailedAnnotations: {
                hasContent: !!move.content,
                contentLength: move.content?.length || 0,
                contentValue: move.content,
                hasHighlights: !!move.highlightedSquares && Object.keys(move.highlightedSquares).length > 0,
                highlightsCount: Object.keys(move.highlightedSquares || {}).length,
                highlightsValue: move.highlightedSquares
              }
            });
          }
          if (move.variations) {
            move.variations.forEach((variation, varIndex) => {
              findCommentMoves(variation, `${path}${index}|${varIndex}:`);
            });
          }
        });
      };
      findCommentMoves(contextMoves);
      // console.log(`[CommentBug] handleAddComment: Found ${commentMovesBeforeUpdate.length} comment moves with id=-1:`, commentMovesBeforeUpdate);
      
      let updatedMoves = updateCommentIdInMoves(contextMoves, commentId);
      
      // Look for comment moves with the new commentId after update
      const commentMovesAfterUpdate = [];
      const findUpdatedCommentMoves = (moves, path = '') => {
        moves.forEach((move, index) => {
          if (move?.creator?.type === 'COMMENT' && move?.creator?.id === commentId) {
            commentMovesAfterUpdate.push({ 
              path: path + index, 
              move: move,
              content: move.content,
              annotations: {
                content: move.content,
                arrows: move.arrows,
                highlightedSquares: move.highlightedSquares
              },
              detailedAnnotations: {
                hasContent: !!move.content,
                contentLength: move.content?.length || 0,
                contentValue: move.content,
                hasHighlights: !!move.highlightedSquares && Object.keys(move.highlightedSquares).length > 0,
                highlightsCount: Object.keys(move.highlightedSquares || {}).length,
                highlightsValue: move.highlightedSquares
              }
            });
          }
          if (move.variations) {
            move.variations.forEach((variation, varIndex) => {
              findUpdatedCommentMoves(variation, `${path}${index}|${varIndex}:`);
            });
          }
        });
      };
      findUpdatedCommentMoves(updatedMoves);
      // console.log(`[CommentBug] handleAddComment: Found ${commentMovesAfterUpdate.length} comment moves with id=${commentId}:`, commentMovesAfterUpdate);
      
      // console.log(`[CommentBug] handleAddComment: updatedMoves after updateCommentIdInMoves:`, updatedMoves);
      
      // Create updated game data
      const updatedGameData = {
        ...gameMetadata,
        moves: updatedMoves,
        comments: [...comments, commentObject],
        // IMPORTANT: Include start position data (including any variations created during comment mode)
        ...(state.startPosition && { startPosition: state.startPosition })
      };
      
      // console.log(`[CommentBug] handleAddComment: Final updatedGameData being sent to database:`, updatedGameData);
      
      const finalCommentMoves = updatedGameData.moves.filter(move => move?.creator?.type === 'COMMENT');
      // console.log(`[CommentBug] handleAddComment: Comment moves in final data:`, finalCommentMoves);
      
      // Check if comment moves are in variations instead of main moves array
      const findCommentMovesInVariations = (moves, path = '') => {
        const found = [];
        moves.forEach((move, index) => {
          if (move.variations && move.variations.length > 0) {
            move.variations.forEach((variation, varIndex) => {
              variation.forEach((varMove, varMoveIndex) => {
                if (varMove?.creator?.type === 'COMMENT') {
                  found.push({
                    path: `${path}${index}|${varIndex}:${varMoveIndex}`,
                    move: varMove,
                    content: varMove.content,
                    highlightedSquares: varMove.highlightedSquares
                  });
                }
              });
              // Recursively check nested variations
              found.push(...findCommentMovesInVariations(variation, `${path}${index}|${varIndex}:`));
            });
          }
        });
        return found;
      };
      
      const commentMovesInVariations = findCommentMovesInVariations(updatedGameData.moves);
      // console.log(`[CommentBug] handleAddComment: Comment moves found in variations:`, commentMovesInVariations);
      
      // Detailed logging of variation content being sent to database
      commentMovesInVariations.forEach((commentMove, index) => {
        // console.log(`[CommentBug] handleAddComment: Variation comment move ${index} details:`, {
        //   path: commentMove.path,
        //   notation: commentMove.move.notation,
        //   content: {
        //     value: commentMove.content,
        //     type: typeof commentMove.content,
        //     length: commentMove.content?.length || 0,
        //     isEmpty: !commentMove.content || commentMove.content === '',
        //     raw: JSON.stringify(commentMove.content)
        //   },
        //   highlightedSquares: {
        //     value: commentMove.highlightedSquares,
        //     hasKeys: Object.keys(commentMove.highlightedSquares || {}).length > 0
        //   },
        //   fullMove: commentMove.move
        // });
      });
      
      // Detailed analysis of content vs highlighted squares in final data
      finalCommentMoves.forEach((move, index) => {
        // console.log(`[CommentBug] handleAddComment: Final comment move ${index} detailed analysis:`, {
        //   notation: move.notation,
        //   content: {
        //     exists: !!move.content,
        //     value: move.content,
        //     length: move.content?.length || 0,
        //     type: typeof move.content
        //   },
        //   highlightedSquares: {
        //     exists: !!move.highlightedSquares,
        //     value: move.highlightedSquares,
        //     count: Object.keys(move.highlightedSquares || {}).length,
        //     type: typeof move.highlightedSquares
        //   },
        //   arrows: {
        //     exists: !!move.arrows,
        //     value: move.arrows,
        //     count: move.arrows?.length || 0,
        //     type: typeof move.arrows
        //   }
        // });
      });
      
      // Update local state immediately for responsive UI
      setComments([...comments, commentObject]);
      setGameMetadata(updatedGameData);
      dispatch({ type: 'SET_MOVES', payload: updatedMoves }); // Update moves immediately to show comment previews
      setNewComment(''); // Clear input
      
      // console.log(`[CommentBug] handleAddComment: About to call putGame with gameId:`, gameId);
      
      // Update via backend API (same as web version)
      await putGame(gameId, updatedGameData);
      
      // console.log(`[CommentBug] handleAddComment: putGame completed successfully`);
      
      // Create notification for game creator (if not commenting on own game)
      const creatorHandle = gameMetadata?.creatorHandle;
      if (creatorHandle) {
        await createCommentNotification(creatorHandle, cleanedContent);
      }
      
      // Preserve board position for text-only and move-linked comments
      if (preservePosition && currentPosition !== null) {
        // Small delay to ensure state has updated
        setTimeout(() => {
          goToMove(currentPosition);
        }, 50);
      }
      
      // console.log('Comment added successfully');
      
    } catch (error) {
      console.error('Error adding comment:', error);
      
      // Revert local state on error
      setComments(comments);
      setGameMetadata(gameMetadata);
      setNewComment(content); // Restore comment content
    } finally {
      setSubmittingComment(false);
    }
  }, [user, gameId, gameMetadata, comments, contextMoves, contextCurrentMove, state, goToMove, createCommentNotification, dispatch]);
  
  // Handle comment submission from input
  const handleSubmitComment = async () => {
    if (!newComment.trim() || submittingComment) return;
    
    await handleAddComment(newComment);
  };
  
  // Helper function to get current move notation for checkbox display
  const getCurrentMoveNotation = () => {
    const currentMove = getMove(contextMoves, contextCurrentMove);
    if (!currentMove || contextCurrentMove === '-1') return null;
    
    // Calculate move number and color
    const moveIndex = parseInt(contextCurrentMove.split('|')[0]);
    const moveNumber = Math.floor(moveIndex / 2) + 1;
    const isWhite = moveIndex % 2 === 0;
    
    return `${moveNumber}${isWhite ? '.' : '...'} ${currentMove.notation || ''}`;
  };

  // Handle submission from unified comment input
  const handleUnifiedCommentSubmit = async (commentData) => {
    if (submittingComment) return;
    
    const { content, linkToMove, moveNotation } = commentData;
    
    // Capture move data if user wants to link to current move
    const moveData = linkToMove ? captureMobileMoveData(true) : null;
    
    await handleAddComment(content, moveData);
  };

  // Handle bookmark click
  const handleBookmarkClick = async () => {
    if (!user?.authenticated || !user?.credentials?.userId) {
      alert('Please log in to bookmark games');
      return;
    }
    
    if (isBookmarked) {
      handleRemoveBookmark();
    } else {
      setShowBookmarkModal(true);
    }
  };

  // Handle bookmark save from modal
  const handleBookmarkSave = (folderName, isPrivate) => {
    setIsBookmarked(true);
    setShowBookmarkModal(false);
    
    // Show success notification (similar to web version)
    const notification = document.createElement('div');
    notification.textContent = `Saved to ${folderName}`;
    notification.style.position = 'fixed';
    notification.style.top = '50%';
    notification.style.left = '50%';
    notification.style.transform = 'translate(-50%, -50%)';
    notification.style.backgroundColor = '#58b047';
    notification.style.color = 'white';
    notification.style.padding = '12px 20px';
    notification.style.borderRadius = '8px';
    notification.style.zIndex = '10000';
    notification.style.fontSize = '16px';
    notification.style.fontWeight = 'bold';
    document.body.appendChild(notification);
    
    setTimeout(() => {
      document.body.removeChild(notification);
    }, 2000);
  };

  // Handle removing bookmark
  const handleRemoveBookmark = async () => {
    if (!user?.credentials?.userId || !gameId) return;
    
    try {
      const userId = user.credentials.userId;
      
      // Query all bookmark folders for this user
      const folderQuery = query(
        collection(firestore, "bookmarkFolders"),
        where("userId", "==", userId)
      );
      
      const folderSnapshot = await getDocs(folderQuery);
      
      // Find and remove the game from whichever folder it's in
      for (const folderDoc of folderSnapshot.docs) {
        const folderData = folderDoc.data();
        const games = folderData.games || [];
        
        const gameIndex = games.findIndex(game => game.gameId === gameId);
        if (gameIndex !== -1) {
          // Remove the game and reindex others
          const updatedGames = [...games];
          const removedGame = updatedGames.splice(gameIndex, 1)[0];
          
          // Reindex remaining games
          updatedGames.forEach(game => {
            if (game.index > removedGame.index) {
              game.index -= 1;
            }
          });
          
          // Update the folder document
          await updateDoc(folderDoc.ref, {
            games: updatedGames,
            updatedAt: new Date().toISOString()
          });
          
          setIsBookmarked(false);
          
          // Show removal notification
          const notification = document.createElement('div');
          notification.textContent = 'Removed from Bookmarks';
          notification.style.position = 'fixed';
          notification.style.top = '50%';
          notification.style.left = '50%';
          notification.style.transform = 'translate(-50%, -50%)';
          notification.style.backgroundColor = '#ff0050';
          notification.style.color = 'white';
          notification.style.padding = '12px 20px';
          notification.style.borderRadius = '8px';
          notification.style.zIndex = '10000';
          notification.style.fontSize = '16px';
          notification.style.fontWeight = 'bold';
          document.body.appendChild(notification);
          
          setTimeout(() => {
            document.body.removeChild(notification);
          }, 2000);
          
          break;
        }
      }
    } catch (error) {
      console.error('Error removing bookmark:', error);
      alert('Failed to remove bookmark. Please try again.');
    }
  };

  // Function to handle tag click and redirect to portal with tag filter
  const handleTagClick = (tag) => {
    // Find the label for this tag to match what's displayed in the Portal tabs
    const tagLabel = TAGS[tag]?.label || tag;

    // When redirecting to portal, also update the current URL to include the tag
    const currentPath = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tag', tag);

    // Update the current URL without navigating away
    window.history.replaceState(
      {},
      '',
      `${currentPath}?${searchParams.toString()}`
    );

    // Now navigate to the portal with the tag
    history.push('/portal', { activeTag: tagLabel });
  };

  // Comment Mode Functions
  
  // Helper function to find all possible next moves from the current position
  const findAllPossibleNextMoves = useCallback((currentPath, moves, gameState) => {
    const possibleMoves = [];
    
    try {
      // Handle starting position
      if (currentPath === '-1') {
        // Check main line first move
        if (moves && moves.length > 0) {
          possibleMoves.push({
            notation: moves[0].notation,
            path: '0',
            type: 'main'
          });
        }
        
        // Check starting position variations
        if (gameState?.startPosition?.variations && Array.isArray(gameState.startPosition.variations)) {
          gameState.startPosition.variations.forEach((variation, varIndex) => {
            if (variation && variation.length > 0) {
              possibleMoves.push({
                notation: variation[0].notation,
                path: `-1|${varIndex}:0`,
                type: 'start_variation'
              });
            }
          });
        }
        
        return possibleMoves;
      }
      
      // For non-starting positions, check main line continuation
      if (!currentPath.includes('|')) {
        // We're on main line
        const currentIndex = parseInt(currentPath, 10);
        if (!isNaN(currentIndex)) {
          // Check next main line move
          if (currentIndex + 1 < moves.length) {
            possibleMoves.push({
              notation: moves[currentIndex + 1].notation,
              path: String(currentIndex + 1),
              type: 'main'
            });
          }
          
          // Check variations from current main line position
          const currentMove = moves[currentIndex];
          if (currentMove?.variations && Array.isArray(currentMove.variations)) {
            currentMove.variations.forEach((variation, varIndex) => {
              if (variation && variation.length > 0) {
                possibleMoves.push({
                  notation: variation[0].notation,
                  path: `${currentIndex}|${varIndex}:0`,
                  type: 'variation'
                });
              }
            });
          }
        }
      } else {
        // We're in a variation - check for continuation and nested variations
        const result = getNodeAtPathInfinite(moves, currentPath, gameState);
        if (result.success) {
          const segments = parseVariationPath(currentPath);
          const lastSegment = segments[segments.length - 1];
          
          if (lastSegment.type === 'variation') {
            // Check if there's a next move in current variation
            if (result.nodeArray && lastSegment.moveIndex + 1 < result.nodeArray.length) {
              const nextMove = result.nodeArray[lastSegment.moveIndex + 1];
              const nextPath = currentPath.substring(0, currentPath.lastIndexOf(':')) + ':' + (lastSegment.moveIndex + 1);
              possibleMoves.push({
                notation: nextMove.notation,
                path: nextPath,
                type: 'variation_continuation'
              });
            }
            
            // Check for nested variations from current position
            if (result.node?.variations && Array.isArray(result.node.variations)) {
              result.node.variations.forEach((nestedVariation, varIndex) => {
                if (nestedVariation && nestedVariation.length > 0) {
                  possibleMoves.push({
                    notation: nestedVariation[0].notation,
                    path: `${currentPath}|${varIndex}:0`,
                    type: 'nested_variation'
                  });
                }
              });
            }
          }
        }
      }
      
    } catch (error) {
      console.error('Error finding possible next moves:', error);
    }
    
    return possibleMoves;
  }, []);
  
  // Detect if a new move differs from existing moves and should trigger comment mode
  const detectNewMove = useCallback((notation, fromSquare, toSquare, source = 'user') => {
    // console.log('detectNewMove called with:', { notation, fromSquare, toSquare, source, isCommentMode, currentPath: contextCurrentMove, puzzleMode: gameHasPuzzleTag });
    
    // PRIORITY 1: PUZZLE MODE VALIDATION (takes precedence over all other logic)
    if (gameHasPuzzleTag && !isCommentMode) {
      // console.log('Puzzle mode - checking move validity');
      
      // Check if move is correct solution move
      const isCorrect = isPuzzleMoveCorrect(notation, contextCurrentMove, state.moves);
      
      if (isCorrect) {
        // console.log('Puzzle move is CORRECT:', notation);
        
        // Allow the move to proceed by navigating to next position
        let nextMoveIndex = 0;
        if (contextCurrentMove === '-1') {
          nextMoveIndex = 0;
        } else if (!contextCurrentMove.includes('|')) {
          nextMoveIndex = parseInt(contextCurrentMove) + 1;
        }
        
        // Navigate to the correct move
        goToMove(nextMoveIndex.toString());
        
        // Update puzzle progress
        const progress = calculatePuzzleProgress(nextMoveIndex.toString(), state.moves);
        setPuzzleProgress(progress);
        
        // Schedule opponent autoplay
        handleOpponentAutoPlay(nextMoveIndex.toString());
        
        return false; // Don't trigger comment mode
        
      } else {
        // console.log('Puzzle move is INCORRECT:', notation);
        
        // Allow 300ms for visual feedback (piece movement animation) before showing error
        setTimeout(() => {
          // Show incorrect move feedback after visual delay
          setPuzzleIncorrectMove(true);
          
          // Auto-hide error after 1.25 seconds (half the original time)
          setTimeout(() => {
            setPuzzleIncorrectMove(false);
          }, 1250);
          
          // IMPORTANT: If the board has already applied the move, we need to revert it
          // Force the board back to the current position to undo any visual move
          setTimeout(() => {
            // console.log('Reverting incorrect move - staying at position:', contextCurrentMove);
            goToMove(contextCurrentMove); // Stay at the same position
          }, 50); // Short delay after showing error to revert position
          
        }, 300); // 300ms delay to allow visual move feedback
        
        // Don't allow the move - block it completely
        return false; // Don't trigger comment mode, don't allow move
      }
    }
    
    // If already in comment mode, handle with smart overwrite logic
    if (isCommentMode) {
      // console.log('🎯 Smart overwrite logic:', {
      //   notation,
      //   currentCommentMoveIndex,
      //   commentMovesLength: commentMoves.length
      // });
      
      // Check if we're overwriting an existing move at the current position
      const nextIndex = currentCommentMoveIndex + 1;
      const existingNextMove = commentMoves[nextIndex];
      
      if (existingNextMove && existingNextMove.notation === notation) {
        // Same move - restore this move to solid and keep subsequent half-moves
        // console.log('🎯 Same move detected - restoring to solid state');
        setCommentMoves(prevMoves => {
          const updated = prevMoves.map((move, index) => ({
            ...move,
            state: index <= nextIndex ? 'solid' : 'half'
          }));
          return updated;
        });
        setCurrentCommentMoveIndex(nextIndex);
        return true;
      } else {
        // Different move - delete all half-moves and add this as new solid move
        // console.log('🎯 Different move detected - deleting half-moves and adding new move');
        
        // Calculate the FEN after applying this move
        let fenAfterMove = null;
        try {
          const tempGame = new Chess(state.currentFEN);
          const tempMoveResult = tempGame.move({ 
            from: fromSquare, 
            to: toSquare,
            promotion: 'q' // Default promotion
          });
          if (tempMoveResult && tempMoveResult.san === notation) {
            fenAfterMove = tempGame.fen();
            // console.log('✅ Calculated FEN after additional move:', notation, '->', fenAfterMove);
          } else {
            console.error('❌ Move notation mismatch when calculating additional FEN:', {
              expected: notation,
              calculated: tempMoveResult?.san,
              move: tempMoveResult
            });
            fenAfterMove = state.currentFEN; // Fallback to current FEN
          }
        } catch (error) {
          console.error('❌ Error calculating FEN after additional move:', error);
          fenAfterMove = state.currentFEN; // Fallback to current FEN
        }
        
        const newMove = {
          notation,
          fromSquare,
          toSquare,
          moveId: `comment_${Date.now()}_${Math.random()}`,
          timestamp: Date.now(),
          state: 'solid',
          moveNumber: null,
          isWhite: null,
          fen: fenAfterMove, // Capture the board position AFTER the move is applied
          // Add content and visual annotation fields
          content: '',
          arrows: [],
          highlightedSquares: {},
          // Enhanced: Add path context for nested variation support
          originalPath: contextCurrentMove, // Capture the path we're branching from
          isNestedVariation: contextCurrentMove.includes('|'), // Flag for nested variations
          parentPath: contextCurrentMove // Store parent path for context
        };
        
        flushSync(() => {
          setCommentMoves(prevMoves => {
            // Keep only solid moves up to current position, add new move
            const solidMoves = prevMoves.slice(0, currentCommentMoveIndex + 1)
              .filter(move => move.state === 'solid');
            const updated = [...solidMoves, newMove];
            // console.log('🎯 Updated comment moves (smart overwrite):', updated);
            return updated;
          });
          
          setCurrentCommentMoveIndex(currentCommentMoveIndex + 1);
        });
        return true;
      }
    }
    
    // NEW LOGIC: Before entering comment mode, check if this move matches any existing path
    try {
      // Find all possible next moves from current position
      const possibleMoves = findAllPossibleNextMoves(contextCurrentMove, state.moves, state);
      // console.log('Possible moves from', contextCurrentMove, ':', possibleMoves);
      
      // Debug move comparison for any remaining edge cases
      // console.log('Move detection fallback - checking possible moves:', possibleMoves.length);
      
      // Check if the made move matches any existing path
      // Try both exact match and normalized comparison (in case of notation format differences)
      let matchingMove = possibleMoves.find(move => move.notation === notation);
      
      // If no exact match, try normalized comparison (remove check/checkmate symbols, etc.)
      if (!matchingMove) {
        const normalizeNotation = (n) => n?.replace(/[+#]/g, '').trim();
        const normalizedNotation = normalizeNotation(notation);
        // console.log('No exact match found, trying normalized comparison with:', normalizedNotation);
        
        matchingMove = possibleMoves.find(move => {
          const normalizedMove = normalizeNotation(move.notation);
          // console.log(`  Comparing "${normalizedNotation}" with "${normalizedMove}"`);
          return normalizedMove === normalizedNotation;
        });
      }
      
      if (matchingMove) {
        // Move matches an existing path - navigate to it instead of entering comment mode
        // console.log('Move matches existing path:', matchingMove.path, 'type:', matchingMove.type);
        // console.log('BEFORE goToMove - current contextCurrentMove:', contextCurrentMove);
        
        // Navigate to the existing move path
        goToMove(matchingMove.path);
        
        // console.log('AFTER goToMove - navigating to:', matchingMove.path);
        
        // Do NOT enter comment mode, do NOT render AddComment
        return false; // Indicate that comment mode should not be triggered
      }
      
      // If we reach here, the move is unique and doesn't match any existing path
      // console.log('Move is unique, entering comment mode for notation:', notation);
      
    } catch (error) {
      console.error('Error checking for existing moves:', error);
      // In case of error, fall back to entering comment mode (safer option)
    }
    
    // ENTER COMMENT MODE: Move is unique, not found in any existing path
    setIsCommentMode(true);
    setCommentModeSource(source); // Track how comment mode was initiated (user/stockfish)
    setParentMoveIndex(contextCurrentMove);
    setCommentMoves([]);
    setCommentStep(1);
    setCommentCaption('');
    setShowCustomization(false);
    setCurrentCommentMoveIndex(-1); // Start before any moves
    setCommentVariationPaths([]); // Clear tracking of comment variations
    
    // Use the last good state (captured BEFORE the move was made)
    // This ensures we save the state without the variation that was just added
    const stateToSave = lastGoodStateRef.current || {
      moves: [...state.moves],
      startPosition: state.startPosition ? JSON.parse(JSON.stringify(state.startPosition)) : null,
      currentMove: contextCurrentMove,
      currentFEN: state.currentFEN
    };
    // console.log('💾 Using last good state for restoration:', {
    //   startPositionVariations: stateToSave.startPosition?.variations?.length || 0,
    //   variations: stateToSave.startPosition?.variations,
    //   wasFromRef: !!lastGoodStateRef.current
    // });
    setSavedGameState(stateToSave);
    setCommentStartMoveIndex(contextCurrentMove);
    
    // Calculate the FEN after applying this move
    let fenAfterMove = null;
    try {
      const tempGame = new Chess(state.currentFEN);
      const tempMoveResult = tempGame.move({ 
        from: fromSquare, 
        to: toSquare,
        promotion: 'q' // Default promotion
      });
      if (tempMoveResult && tempMoveResult.san === notation) {
        fenAfterMove = tempGame.fen();
        // console.log('✅ Calculated FEN after move:', notation, '->', fenAfterMove);
      } else {
        console.error('❌ Move notation mismatch when calculating FEN:', {
          expected: notation,
          calculated: tempMoveResult?.san,
          move: tempMoveResult
        });
        fenAfterMove = state.currentFEN; // Fallback to current FEN
      }
    } catch (error) {
      console.error('❌ Error calculating FEN after move:', error);
      fenAfterMove = state.currentFEN; // Fallback to current FEN
    }
    
    // Add this new unique move to the comment moves
    const newMove = {
      notation,
      fromSquare,
      toSquare,
      moveId: `comment_${Date.now()}_${Math.random()}`,
      timestamp: Date.now(),
      state: 'solid', // 'solid' or 'half' for half-move system
      // Add position tracking for proper numbering
      moveNumber: null, // Will be calculated when rendering
      isWhite: null, // Will be calculated when rendering
      fen: fenAfterMove, // Capture the board position AFTER the move is applied
      // Add content and visual annotation fields
      content: '',
      arrows: [],
      highlightedSquares: {}
    };
    
    // Update both state values together to prevent timing issues
    flushSync(() => {
      setCommentMoves([newMove]);
      setCurrentCommentMoveIndex(0); // Set to the first (and only) move
    });
    
    // console.log('Comment mode initiated for unique move:', notation, 'from position:', contextCurrentMove);
    
    return true; // Comment mode triggered
  }, [isCommentMode, contextCurrentMove, state.moves, state.currentFEN, findAllPossibleNextMoves, goToMove, gameHasPuzzleTag, isPuzzleMoveCorrect, calculatePuzzleProgress, handleOpponentAutoPlay]);

  // Handle automatic half-move detection when navigating backwards
  const updateHalfMoveStates = useCallback((newMoveIndex) => {
    if (!isCommentMode || !commentMoves.length) return;
    
    // console.log('🎯 Half-move detection:', { 
    //   newMoveIndex, 
    //   currentCommentMoveIndex, 
    //   commentMovesLength: commentMoves.length 
    // });
    
    setCommentMoves(prevMoves => {
      const updated = prevMoves.map((move, index) => ({
        ...move,
        state: index <= newMoveIndex ? 'solid' : 'half'
      }));
      
      // console.log('🎯 Updated half-move states:', updated.map(m => ({ notation: m.notation, state: m.state })));
      return updated;
    });
  }, [isCommentMode, commentMoves.length, currentCommentMoveIndex]);
  
  // Handle deleting all half-moves
  const deleteHalfMoves = useCallback(() => {
    if (!isCommentMode) return;
    
    // console.log('🗑️ Deleting half-moves');
    setCommentMoves(prevMoves => {
      const solidMoves = prevMoves.filter(move => move.state === 'solid');
      // console.log('🗑️ Remaining solid moves:', solidMoves.map(m => ({ notation: m.notation, state: m.state })));
      return solidMoves;
    });
    
    // Update current index to last solid move
    const lastSolidIndex = commentMoves.filter(move => move.state === 'solid').length - 1;
    setCurrentCommentMoveIndex(lastSolidIndex);
  }, [isCommentMode, commentMoves]);

  // Navigate to comment move by reconstructing the position
  const navigateToCommentMove = useCallback((targetMoveIndex) => {
    // console.log('🔥 CommentMyMoves: navigateToCommentMove Called:', {
    //   step: '3_NAVIGATE_START',
    //   targetMoveIndex,
    //   isCommentMode,
    //   parentMoveIndex,
    //   commentMovesLength: commentMoves.length,
    //   dispatchType: typeof dispatch,
    //   dispatchExists: !!dispatch
    // });

    if (!isCommentMode || parentMoveIndex === null) {
      // console.log('🔥 CommentMyMoves: Early exit - not in comment mode or no parent:', {
      //   step: '3_EARLY_EXIT',
      //   isCommentMode,
      //   parentMoveIndex
      // });
      return;
    }

    // If target is -1, just go to parent position (start of comment line)
    if (targetMoveIndex === -1) {
      // console.log('🔥 CommentMyMoves: Going to parent position:', {
      //   step: '3_PARENT_POSITION',
      //   parentMoveIndex
      // });
      goToMove(String(parentMoveIndex));
      return;
    }

    // For moves 0 and above, use the stored FEN from the comment move
    try {
      // Check if target move index is valid
      if (targetMoveIndex < 0 || targetMoveIndex >= commentMoves.length) {
        console.error('🔥 CommentMyMoves: Invalid target move index:', {
          step: '3_INVALID_INDEX',
          targetMoveIndex,
          commentMovesLength: commentMoves.length
        });
        goToMove(String(parentMoveIndex));
        return;
      }
      
      const targetMove = commentMoves[targetMoveIndex];
      if (!targetMove) {
        console.error('🔥 CommentMyMoves: No comment move found at index:', {
          step: '3_NO_MOVE_FOUND',
          targetMoveIndex
        });
        goToMove(String(parentMoveIndex));
        return;
      }
      
      // Use the stored FEN from the comment move
      const targetFEN = targetMove.fen;
      if (!targetFEN) {
        console.error('🔥 CommentMyMoves: No FEN stored in comment move:', {
          step: '3_NO_FEN',
          targetMove
        });
        goToMove(String(parentMoveIndex));
        return;
      }
      
      // console.log('🔥 CommentMyMoves: About to update board directly:', {
      //   step: '4_DIRECT_BOARD_UPDATE',
      //   targetFEN,
      //   targetMoveIndex,
      //   moveNotation: targetMove.notation,
      //   boardRefExists: !!boardRef?.current,
      //   boardRefMethods: boardRef?.current ? Object.keys(boardRef.current) : 'no ref'
      // });
      
      // Update board FEN
      if (boardRef?.current?.updateBoardFEN) {
        // console.log('🔥 CommentMyMoves: Using direct board updateBoardFEN method');
        boardRef.current.updateBoardFEN(targetFEN);
      } else {
        // console.log('🔥 CommentMyMoves: No direct board method, falling back to dispatch');
        dispatch({
          type: 'SET_FEN',
          payload: targetFEN
        });
      }
      
      // console.log('✅ CommentMyMoves: Board update completed');

      // Check if the update worked after a brief delay
      setTimeout(() => {
        const boardCurrentFEN = boardRef?.current?.getCurrentFEN ? boardRef.current.getCurrentFEN() : 'no board ref';
        // console.log('🔥 CommentMyMoves: Final verification:', {
        //   step: '6_FINAL_VERIFICATION',
        //   targetFEN,
        //   boardCurrentFEN,
        //   success: boardCurrentFEN === targetFEN
        // });
      }, 50);
      
    } catch (error) {
      console.error('🔥 CommentMyMoves: Error in navigateToCommentMove:', error);
      goToMove(String(parentMoveIndex));
    }
  }, [isCommentMode, parentMoveIndex, commentMoves, goToMove, dispatch, boardRef]);

  // Enter comment mode with proper initialization
  const enterCommentMode = useCallback(() => {
    // Store the current position as parent for reset
    setParentMoveIndex(contextCurrentMove);
    
    // Initialize comment mode state
    setIsCommentMode(true);
    setCommentMoves([]);
    setCommentStep(1);
    setCommentCaption('');
    
    // Force switch to comment tab in MobileMoves
    // This will be handled by passing isCommentMode to MobileMovesEngine
    // console.log('Entered comment mode from position:', contextCurrentMove);
  }, [contextCurrentMove]);

  // Exit comment mode with optional confirmation
  const exitCommentMode = useCallback((confirmed = false) => {
    if (!confirmed && commentMoves.length > 0) {
      // Show our reusable confirmation subtab instead of window.confirm
      setShowConfirmation(true);
      return false;
    }

    // Reset board to parent move position
    if (parentMoveIndex !== null) {
      goToMove(parentMoveIndex);
    }

    // Clear comment mode state and set redirect flag
    setIsCommentMode(false);
    setCommentModeSource(null); // Reset source tracking
    setCommentMoves([]);
    setParentMoveIndex(null);
    setCommentStep(1);
    setCommentCaption('');
    setShowCustomization(false);
    setCurrentCommentMoveIndex(-1);
    setShowConfirmation(false);
    
    // Clear the saved game state
    setSavedGameState(null);
    setCommentStartMoveIndex(null);

    // console.log('Exited comment mode, returned to position:', parentMoveIndex);
    return true;
  }, [commentMoves.length, parentMoveIndex, goToMove]);

  // Go back from caption step to moves step
  const goBackToMoves = useCallback(() => {
    setCommentStep(1);
  }, []);

  // Handle confirmation actions
  const confirmDelete = useCallback(() => {
    setShowConfirmation(false);
    
    // Restore the saved game state to undo all comment moves
    if (savedGameState) {
      // console.log('Restoring saved game state:', savedGameState);
      
      // Restore the moves array
      dispatch({ type: 'SET_MOVES', payload: savedGameState.moves });
      
      // CRITICAL FIX: For start position, we must restore the EXACT saved state
      // The issue is that variations are added BEFORE we save the state
      // So we need to restore to the state from BEFORE the first move was made
      if (commentStartMoveIndex === '-1') {
        // When we started from the start position, restore it completely
        // This should remove any variations added during comment mode
        const cleanStartPosition = savedGameState.startPosition || {
          fen: state.initialFEN || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
          content: '',
          arrows: [],
          highlightedSquares: {},
          variations: [] // IMPORTANT: Clear all variations if none were saved
        };
        
        // console.log('🔄 Restoring start position, removing comment variations');
        dispatch({ type: 'SET_START_POSITION_COMPLETE', payload: cleanStartPosition });
      } else if (savedGameState.startPosition !== undefined) {
        // For other positions, restore normally
        dispatch({ type: 'SET_START_POSITION_COMPLETE', payload: savedGameState.startPosition });
      }
      
      // Navigate back to the original position
      if (commentStartMoveIndex !== null) {
        goToMove(commentStartMoveIndex);
      }
    }
    
    exitCommentMode(true); // Force delete without further confirmation
  }, [exitCommentMode, savedGameState, commentStartMoveIndex, goToMove, dispatch, state.initialFEN]);

  const cancelDelete = useCallback(() => {
    setShowConfirmation(false);
    // Stay in comment mode, no other action needed
  }, []);

  // Proceed from moves to caption step
  const proceedToCaption = useCallback(() => {
    if (commentMoves.length === 0) {
      alert('No moves to save. Make some moves first.');
      return;
    }
    
    setCommentStep(2);
  }, [commentMoves, parentMoveIndex, dispatch, handleAddComment]);

  // Submit the complete comment with moves and caption
  const submitCommentMoves = useCallback(async (caption) => {
    if (!caption || !caption.trim()) {
      alert('Please add a caption for your variation.');
      return;
    }

    try {
      // Store parent position for variation navigation
      const variationParentPosition = parentMoveIndex;
      
      // Convert commentMoves to proper move objects with creator type and preserve annotations

      
      const originalMovesLength = contextMoves.length;
      
      const commentMoveObjects = commentMoves.map((move, index) => {
        const moveObject = {
          notation: move.notation,
          from: move.fromSquare || '',
          to: move.toSquare || '',
          creator: {
            type: 'COMMENT',
            id: -1 // Will be updated by updateCommentIdInMoves
          },
          // Preserve all annotation data
          content: move.content || '',
          arrows: move.arrows || [],
          highlightedSquares: move.highlightedSquares || {},
          // Preserve other move data
          moveId: move.moveId,
          timestamp: move.timestamp,
          state: move.state,
          fen: move.fen
        };
        
        // console.log(`[CommentBug] submitCommentMoves: Converted move ${index}:`, {
        //   original: move,
        //   converted: moveObject,
        //   annotations: {
        //     content: moveObject.content,
        //     arrows: moveObject.arrows,
        //     highlightedSquares: moveObject.highlightedSquares
        //   },
        //   contentComparison: {
        //     originalContent: move.content,
        //     convertedContent: moveObject.content,
        //     originalHighlights: move.highlightedSquares,
        //     convertedHighlights: moveObject.highlightedSquares
        //   }
        // });
        
        return moveObject;
      });
      
      // console.log(`[CommentBug] submitCommentMoves: Final commentMoveObjects:`, commentMoveObjects);
      
      // Add the comment moves to the game state as a variation
      if (commentMoveObjects.length > 0) {
        const dispatchPayload = {
          path: parentMoveIndex?.toString() || '-1',
          variation: commentMoveObjects
        };
        
        // console.log(`[CommentBug] submitCommentMoves: Dispatching ADD_VARIATION_AT_PATH:`, dispatchPayload);
        
        dispatch({
          type: 'ADD_VARIATION_AT_PATH',
          payload: dispatchPayload
        });
        
        // console.log(`[CommentBug] submitCommentMoves: Redux dispatch completed, waiting for state update`);
        
        // Wait for Redux state to actually update by polling
        let attempts = 0;
        const maxAttempts = 50; // 500ms max wait
        while (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 10));
          attempts++;
          
          // Check if variations have been added to the parent move
          const parentIdx = parseInt(parentMoveIndex?.toString() || '-1');
          if (parentIdx >= 0 && parentIdx < contextMoves.length) {
            const parentMove = contextMoves[parentIdx];
            if (parentMove?.variations && parentMove.variations.length > 0) {
              // Check if any variation contains comment moves
              const hasCommentMoves = parentMove.variations.some(variation => 
                variation.some(move => move?.creator?.type === 'COMMENT')
              );
              if (hasCommentMoves) {
                // console.log(`[CommentBug] submitCommentMoves: Redux state updated after ${attempts * 10}ms, found comment moves in variations`);
                break;
              }
            }
          } else if (parentIdx === -1) {
            // Check start position variations
            if (state.startPosition?.variations && state.startPosition.variations.length > 0) {
              const hasCommentMoves = state.startPosition.variations.some(variation => 
                variation.some(move => move?.creator?.type === 'COMMENT')
              );
              if (hasCommentMoves) {
                // console.log(`[CommentBug] submitCommentMoves: Redux state updated after ${attempts * 10}ms, found comment moves in start position variations`);
                break;
              }
            }
          }
        }
        
        if (attempts >= maxAttempts) {
          console.warn(`[CommentBug] submitCommentMoves: Timeout waiting for Redux state update`);
        }
      }
      
      // Use existing handleAddComment function (no moveData needed - moves are now in game state)
      await handleAddComment(caption.trim(), null, false);
      
      // Reset comment mode and set redirect flag
      setIsCommentMode(false);
      setCommentModeSource(null); // Reset source tracking
      setCommentMoves([]);
      setParentMoveIndex(null);
      setCommentStep(1);
      setCommentCaption('');
      setShowCustomization(false);
      
      // Navigate to variation's parent position
      if (variationParentPosition !== null) {
        setTimeout(() => {
          goToMove(variationParentPosition);
        }, 100);
      }
        
      // console.log('Successfully submitted comment with moves');
    } catch (error) {
      console.error('Error submitting comment moves:', error);
      alert('Failed to submit comment. Please try again.');
    }
  }, [commentMoves, parentMoveIndex, dispatch, handleAddComment, goToMove]);

  // Navigation blocking when in comment mode
  useEffect(() => {
    if (!isCommentMode) return;

    const handleBeforeUnload = (e) => {
      if (isCommentMode && commentMoves.length > 0) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    const handlePopState = (e) => {
      if (isCommentMode && commentMoves.length > 0) {
        e.preventDefault();
        // Show our confirmation subtab instead of window.confirm
        setShowConfirmation(true);
        // Push state back to prevent navigation
        window.history.pushState(null, '', window.location.href);
        return;
      }
    };

    // Prevent browser navigation
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    // Push a state to handle back button
    window.history.pushState(null, '', window.location.href);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isCommentMode, commentMoves.length, exitCommentMode]);

  // Intercept board navigation when in comment mode
  const protectedGoToMove = useCallback((moveId) => {
    if (isCommentMode && commentMoves.length > 0) {
      // Show confirmation instead of navigating
      setShowConfirmation(true);
      return; // Block navigation
    }
    goToMove(moveId);
  }, [isCommentMode, commentMoves.length, goToMove]);

  // Block keyboard navigation when in comment mode
  useEffect(() => {
    if (!isCommentMode) return;

    const handleKeyDown = (e) => {
      if (isCommentMode && commentMoves.length > 0 && ['ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
        
        // Show confirmation instead of immediately asking
        setShowConfirmation(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isCommentMode, commentMoves.length, exitCommentMode]);

  // Callback to update comment move content/arrows/highlights from MobileMoves
  const updateCommentMoveContent = useCallback((moveIndex, updates) => {
    if (!isCommentMode || moveIndex < 0 || moveIndex >= commentMoves.length) return;
    
    // console.log(`[CommentBug] updateCommentMoveContent: moveIndex=${moveIndex}, updates=`, updates);
    // console.log(`[CommentBug] Current commentMoves before update:`, commentMoves);
    
    setCommentMoves(prevMoves => {
      const newMoves = [...prevMoves];
      const oldMove = newMoves[moveIndex];
      newMoves[moveIndex] = {
        ...newMoves[moveIndex],
        ...updates // Merge in content, arrows, and/or highlightedSquares
      };
      
      // console.log(`[CommentBug] Updated move ${moveIndex}:`, {
      //   before: oldMove,
      //   after: newMoves[moveIndex],
      //   updates: updates
      // });
      // console.log(`[CommentBug] Full commentMoves after update:`, newMoves);
      
      return newMoves;
    });
  }, [isCommentMode, commentMoves.length]);

  // Memoized current comment move data for MobileMoves
  const currentCommentMoveData = useMemo(() => {
    if (!isCommentMode || currentCommentMoveIndex < 0 || currentCommentMoveIndex >= commentMoves.length) {
      // console.log(`[CommentBug] currentCommentMoveData: returning empty data (not in comment mode or invalid index)`);
      return { content: '', arrows: [], highlightedSquares: {} };
    }
    
    const currentMove = commentMoves[currentCommentMoveIndex];
    
    // Enhanced: Check if this is a nested variation move that needs special handling
    if (currentMove.isNestedVariation && currentMove.originalPath) {
      // console.log(`[CommentBug] currentCommentMoveData: Handling nested variation move with path:`, currentMove.originalPath);
      
      // For nested variations, try to load content from the game state using the original path
      try {
        const { getNodeAtPathInfinite } = require('../../util/VariationPathUtils');
        const result = getNodeAtPathInfinite(state.moves, currentMove.originalPath, state);
        
        if (result.success && result.node) {
          // Load content from game state but preserve comment mode annotations
          const gameStateContent = result.node.content || '';
          const moveData = {
            content: currentMove.content || gameStateContent, // Prioritize comment mode content
            arrows: currentMove.arrows || result.node.arrows || [],
            highlightedSquares: currentMove.highlightedSquares || result.node.highlightedSquares || {},
            // Flag this as nested variation for special handling
            isNestedVariation: true,
            originalPath: currentMove.originalPath
          };
          
          // console.log(`[CommentBug] currentCommentMoveData: Nested variation data loaded:`, moveData);
          return moveData;
        } else {
          console.warn(`[CommentBug] currentCommentMoveData: Failed to load nested variation data for path:`, currentMove.originalPath);
        }
      } catch (error) {
        console.error(`[CommentBug] currentCommentMoveData: Error loading nested variation:`, error);
      }
    }
    
    // Standard linear comment move handling
    const moveData = {
      content: currentMove.content || '',
      arrows: currentMove.arrows || [],
      highlightedSquares: currentMove.highlightedSquares || {},
      isNestedVariation: false
    };
    
    // console.log(`[CommentBug] currentCommentMoveData: moveIndex=${currentCommentMoveIndex}, returning:`, moveData);
    // console.log(`[CommentBug] currentCommentMoveData: full currentMove=`, currentMove);
    
    return moveData;
  }, [isCommentMode, currentCommentMoveIndex, commentMoves, commentMoves.length, state.moves, state]);

  // Wrapper function for MobileMoves to update current comment move
  const updateCurrentCommentMoveContent = useCallback((field, value) => {
    if (!isCommentMode || currentCommentMoveIndex < 0) return;
    
    // console.log(`[CommentBug] updateCurrentCommentMoveContent: moveIndex=${currentCommentMoveIndex}, field=${field}, value=`, value);
    
    const currentMove = commentMoves[currentCommentMoveIndex];
    
    // Enhanced: Handle nested variations with hybrid update system
    if (currentMove?.isNestedVariation && currentMove?.originalPath) {
      // console.log(`[CommentBug] updateCurrentCommentMoveContent: Handling nested variation update for path:`, currentMove.originalPath);
      
      // For nested variations, save to both comment mode AND game state
      const updates = {};
      updates[field] = value;
      
      // 1. Update the comment move array (for immediate UI feedback)
      updateCommentMoveContent(currentCommentMoveIndex, updates);
      
      // 2. Also update the game state (for persistence when comment mode exits)
      if (field === 'content') {
        // Use the same dispatch logic as normal mode for nested variations
        const pathParts = currentMove.originalPath.split('|');
        
        if (pathParts.length >= 2) { // This is a variation path
          if (pathParts[0] === '-1') {
            // Start position variation
            dispatch({ 
              type: 'UPDATE_START_POSITION_VARIATION_MOVE_ANALYSIS', 
              payload: { 
                moveId: currentMove.originalPath,
                properties: { content: value }
              }
            });
          } else if (pathParts.length === 2) {
            // First-level variation
            dispatch({ 
              type: 'UPDATE_MOVE_PROPERTIES', 
              payload: { 
                moveId: currentMove.originalPath,
                properties: { content: value }
              }
            });
          } else {
            // Nested variation (2+ levels)
            dispatch({
              type: 'UPDATE_NESTED_VARIATION_CONTENT',
              payload: {
                path: currentMove.originalPath,
                content: value
              }
            });
          }
        }
        
        // console.log(`[CommentBug] updateCurrentCommentMoveContent: Hybrid update completed for nested variation`);
      }
    } else {
      // Standard linear comment move handling
      const updates = {};
      updates[field] = value;
      updateCommentMoveContent(currentCommentMoveIndex, updates);
    }
  }, [isCommentMode, currentCommentMoveIndex, updateCommentMoveContent, commentMoves, dispatch]);

  // Navigation handlers from desktop (for game collection navigation)
  const handleNextGameClick = () => {
    // Get origin and gameIds from location.state
    const origin = location.state?.origin;
    const stateGameIds = location.state?.gameIds;
    
    // Check for collection context using location.state
    if (stateGameIds && Array.isArray(stateGameIds)) {
      const currentIndex = stateGameIds.indexOf(gameId);
      if (currentIndex !== -1 && currentIndex < stateGameIds.length - 1) {
        const nextGameId = stateGameIds[currentIndex + 1];
        
        // Navigate to next game in collection
        navigateToGame(nextGameId, 'next');
        return;
      } else if (currentIndex === stateGameIds.length - 1) {
        // At collection boundary - try API fallback before showing alert
        const searchParams = new URLSearchParams(location.search);
        const tag = searchParams.get('tag');
        
        if (tag && gameMetadata?.nextGameId) {
          // Navigate using API fallback with tag preserved
          navigateToGame(gameMetadata.nextGameId, 'next');
          return;
        }
        
        if (gameMetadata?.nextGameId) {
          // Navigate using API fallback
          navigateToGame(gameMetadata.nextGameId, 'next');
          return;
        }
        
        // Only show alert if no API fallback available
        alert('You are at the last game in this collection.');
        return;
      }
    }

    // Check if we're in a tag-filtered context and have gameMetadata
    const searchParams = new URLSearchParams(location.search);
    const tag = searchParams.get('tag');
    
    if (tag && gameMetadata?.nextGameId) {
      // Navigate to next game with tag preserved
      navigateToGame(gameMetadata.nextGameId, 'next');
      return;
    }
    
    // Fall back to metadata-based navigation
    if (gameMetadata?.nextGameId) {
      navigateToGame(gameMetadata.nextGameId, 'next');
      return;
    }
    
    // Fallback to API-based navigation using nextGameId state
    if (nextGameId) {
      navigateToGame(nextGameId, 'next');
    } else {
      alert('No more games available.');
    }
  };

  const handlePrevGameClick = () => {
    // Get origin and gameIds from location.state
    const origin = location.state?.origin;
    const stateGameIds = location.state?.gameIds;
    
    // Check for collection context using location.state
    if (stateGameIds && Array.isArray(stateGameIds)) {
      const currentIndex = stateGameIds.indexOf(gameId);
      if (currentIndex > 0) {
        const prevGameId = stateGameIds[currentIndex - 1];
        
        // Navigate to previous game in collection
        navigateToGame(prevGameId, 'prev');
        return;
      } else if (currentIndex === 0) {
        // At collection boundary - try API fallback before showing alert
        const searchParams = new URLSearchParams(location.search);
        const tag = searchParams.get('tag');
        
        if (tag && gameMetadata?.prevGameId) {
          // Navigate using API fallback with tag preserved
          navigateToGame(gameMetadata.prevGameId, 'prev');
          return;
        }
        
        if (gameMetadata?.prevGameId) {
          // Navigate using API fallback
          navigateToGame(gameMetadata.prevGameId, 'prev');
          return;
        }
        
        // Only show alert if no API fallback available
        alert('You are at the first game in this collection.');
        return;
      }
    }

    // Check if we're in a tag-filtered context and have gameMetadata
    const searchParams = new URLSearchParams(location.search);
    const tag = searchParams.get('tag');
    
    if (tag && gameMetadata?.prevGameId) {
      // Navigate to previous game with tag preserved
      navigateToGame(gameMetadata.prevGameId, 'prev');
      return;
    }
    
    // Fall back to metadata-based navigation
    if (gameMetadata?.prevGameId) {
      navigateToGame(gameMetadata.prevGameId, 'prev');
      return;
    }
    
    // Fallback to API-based navigation using prevGameId state
    if (prevGameId) {
      navigateToGame(prevGameId, 'prev');
    } else {
      alert('No previous games available.');
    }
  };

  // Helper functions to check if navigation is available using collection-aware logic
  const canNavigateNext = () => {
    const stateGameIds = location.state?.gameIds;
    const searchParams = new URLSearchParams(location.search);
    const tag = searchParams.get('tag');
    
    // Check collection context first
    if (stateGameIds && Array.isArray(stateGameIds)) {
      const currentIndex = stateGameIds.indexOf(gameId);
      if (currentIndex !== -1 && currentIndex < stateGameIds.length - 1) {
        return true; // Can navigate within collection
      } else if (currentIndex === stateGameIds.length - 1) {
        // At collection boundary - check if API fallback is available
        return (tag && gameMetadata?.nextGameId) || gameMetadata?.nextGameId || nextGameId;
      }
    }
    
    // Check API-based navigation
    return (tag && gameMetadata?.nextGameId) || gameMetadata?.nextGameId || nextGameId;
  };

  const canNavigatePrev = () => {
    const stateGameIds = location.state?.gameIds;
    const searchParams = new URLSearchParams(location.search);
    const tag = searchParams.get('tag');
    
    // Check collection context first
    if (stateGameIds && Array.isArray(stateGameIds)) {
      const currentIndex = stateGameIds.indexOf(gameId);
      if (currentIndex > 0) {
        return true; // Can navigate within collection
      } else if (currentIndex === 0) {
        // At collection boundary - check if API fallback is available
        return (tag && gameMetadata?.prevGameId) || gameMetadata?.prevGameId || prevGameId;
      }
    }
    
    // Check API-based navigation
    return (tag && gameMetadata?.prevGameId) || gameMetadata?.prevGameId || prevGameId;
  };

  // Touch event handlers for swipe navigation with smooth scrolling
  const handleTouchStart = (e) => {
    if (isNavigating) return;
    
    // Disable swipe navigation when comments are shown
    if (showComments) return;
    
    // Prevent swipe on interactive elements
    const target = e.target;
    if (target.tagName === 'BUTTON' || 
        target.tagName === 'INPUT' || 
        target.closest('button') ||
        target.closest('.fa-solid')) {
      return;
    }
    
    // Prevent swipe when interacting with scrollable areas in MobileMoves
    if (target.closest('[data-scrollable="true"]') ||
        target.closest('.mobile-moves-scrollable')) {
      return;
    }
    
    setTouchStartY(e.targetTouches[0].clientY);
    setSwipeDirection(null);
    
    // For chess board, we'll track but not set dragging yet
    // This allows piece dragging to work
    if (target.closest('[id="PlayableBoard"]') || 
        target.closest('.react-chessboard') ||
        target.closest('[class*="chessboard"]') ||
        target.closest('[class*="chess-board"]') ||
        target.closest('[data-testid*="board"]')) {
      // Don't set isDragging for chess board initially
      return;
    }
    
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging || isNavigating) return;
    
    // Disable swipe navigation when comments are shown
    if (showComments) return;
    
    // Prevent swipe when interacting with scrollable areas in MobileMoves
    const target = e.target;
    if (target.closest('[data-scrollable="true"]') ||
        target.closest('.mobile-moves-scrollable')) {
      return;
    }
    
    const currentY = e.targetTouches[0].clientY;
    const diffY = currentY - touchStartY;
    
    // Determine swipe direction if not already set - only vertical now
    if (!swipeDirection && Math.abs(diffY) > 10) {
      setSwipeDirection('vertical');
    }
    
    // Handle vertical swipe (game navigation)
    if (swipeDirection === 'vertical') {
      // Apply resistance at boundaries using collection-aware logic
      let resistance = 1;
      if ((diffY > 0 && !canNavigatePrev()) || (diffY < 0 && !canNavigateNext())) {
        resistance = 0.3; // Stronger resistance when no game available
      }
      
      const translation = diffY * resistance;
      setTranslateY(translation);
    }
  };

  const handleTouchEnd = (e) => {
    if (!isDragging || isNavigating) return;
    
    // Disable swipe navigation when comments are shown
    if (showComments) return;
    
    // Prevent swipe when interacting with scrollable areas in MobileMoves
    const target = e.target;
    if (target.closest('[data-scrollable="true"]') ||
        target.closest('.mobile-moves-scrollable')) {
      return;
    }
    
    setIsDragging(false);
    
    // Handle vertical swipe completion (game navigation)
    if (swipeDirection === 'vertical') {
      const threshold = window.innerHeight * 0.2; // 20% of screen height
      
      if (Math.abs(translateY) > threshold) {
        // Navigate if threshold exceeded
        if (translateY > 0) {
          // Swiped down (finger moved down) - go to previous
          handlePrevGameClick();
        } else if (translateY < 0) {
          // Swiped up (finger moved up) - go to next
          handleNextGameClick();
        } else {
          // No game available, snap back
          setTranslateY(0);
        }
      } else {
        // Didn't reach threshold, snap back
        setTranslateY(0);
      }
    }
    
    // Reset touch positions
    setTouchStartY(0);
    setSwipeDirection(null);
  };

  // Navigation function that maintains state context
  const navigateToGame = (targetGameId, direction) => {
    if (isNavigating) return;
    
    setIsNavigating(true);
    
    // Animate out in the direction of swipe
    const exitDistance = direction === 'next' ? -window.innerHeight : window.innerHeight;
    setTranslateY(exitDistance);
    
    // Navigate immediately for smoother transition
    setTimeout(() => {
      // Preserve location.state for maintaining game collection context
      const searchParams = new URLSearchParams(location.search);
      let navigateUrl = `/games/${targetGameId}`;
      
      // Add query parameters if they exist
      if (searchParams.toString()) {
        navigateUrl += `?${searchParams.toString()}`;
      }

      // Use replace instead of push to avoid building up game history
      // This ensures back button always returns to origin (Portal/Profile/Bookmarks)
      history.replace(navigateUrl, location.state);
    }, 250); // Slightly before animation completes for seamless transition
  };

  // Loading state
  if (isLoading) {
    return (
      <div style={{
        width: '100vw',
        minHeight: '100dvh',
        backgroundColor: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'calc(55px + env(safe-area-inset-bottom))',
        boxSizing: 'border-box'
      }}>
        <div style={{ textAlign: 'center' }}>
          <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '32px', marginBottom: '16px' }}></i>
          <div style={{ fontSize: '16px' }}>Loading game...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{
        width: '100vw',
        minHeight: '100dvh',
        backgroundColor: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#ff0050',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'calc(55px + env(safe-area-inset-bottom))',
        paddingLeft: '20px',
        paddingRight: '20px',
        boxSizing: 'border-box'
      }}>
        <div style={{ textAlign: 'center' }}>
          <i className="fa-solid fa-exclamation-triangle" style={{ fontSize: '32px', marginBottom: '16px' }}></i>
          <div style={{ fontSize: '16px', marginBottom: '16px' }}>Error loading game</div>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>{error}</div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button
              onClick={() => {
                setError(null);
                setIsLoading(true);
                // Trigger reload by resetting fetchingGame
                setFetchingGame(false);
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: 'gold',
                color: 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Retry
            </button>
            <button
              onClick={() => history.goBack()}
              style={{
                padding: '10px 20px',
                backgroundColor: '#333',
                color: 'white',
                border: '1px solid #555',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* CSS Animation for Puzzle Overlays */}
      <style>{`
        @font-face {
          font-family: 'Mansalva';
          src: url(${mansalva}) format('truetype');
          font-weight: normal;
          font-style: normal;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.8);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }
        
      `}</style>
      
      {/* Helper function to calculate dynamic board size for Record mode */}
      {(() => {
        // Define dynamic board size calculation function
        window.calculateRecordModeBoardSize = () => {
          // Calculate available container dimensions
          // Parent has 15px padding (30px total) and 80px title height
          const containerWidth = Math.min(
            (window.innerWidth - 20) * 0.98,
            ((window.innerHeight - 130) * 9/16 * 0.95),
            380
          ) - 30; // Subtract padding
          
          const containerHeight = Math.min(
            (window.innerHeight - 130) * 0.98,
            ((window.innerWidth - 20) * 16/9 * 0.95)
          ) - 30 - 80; // Subtract padding and title height
          
          // Use 90% of the smaller dimension to ensure square board fits
          const availableSize = Math.min(containerWidth, containerHeight);
          return Math.max(availableSize * 0.9, 280); // Minimum 280px
        };
        return null; // This is just for function definition
      })()}
      
      {/* Record Mode Layout - Optimized for Screen Recording */}
      {viewMode === 'Record' ? (
        <div style={{
          width: '100vw',
          height: 'calc(100dvh - 55px)',
          backgroundColor: '#000',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '10px',
          boxSizing: 'border-box'
        }}>
          {/* Simple 16:9 Crop Container */}
          <div style={{
            width: 'min(calc((100vw - 20px) * 0.98), calc((100dvh - 130px) * 9/16 * 0.95), 380px)', // 16:9 aspect ratio scaled down 5%
            height: 'min(calc((100dvh - 130px) * 0.98), calc((100vw - 20px) * 16/9 * 0.95))', // 16:9 aspect ratio scaled down 5%
            maxWidth: '380px', // Scaled down max-width by 5%
            border: '2px solid #666',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            padding: '15px',
            boxSizing: 'border-box',
            backgroundColor: (backgroundImage && recordSubTab === 'main') ? 'transparent' : 'transparent',
            backgroundImage: (backgroundImage && recordSubTab === 'main') ? `url(${backgroundImage})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}>
          
          {/* Background Overlay for Better Readability */}
          {backgroundImage && recordSubTab === 'main' && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              zIndex: 0
            }} />
          )}

          {/* Start Position Image/Video Overlay */}
          {(() => {
            const isStartPosition = contextCurrentMove === '-1';
            const introEnabled = getMoveEnhancement(contextCurrentMove, 'startPositionIntro')?.enabled ?? true;
            const mediaData = getMoveEnhancement(contextCurrentMove, 'startPositionMedia');
            const hasMedia = mediaData?.fileURL;
            const isMainTab = recordSubTab === 'main';
            
            return isStartPosition && introEnabled && isMainTab && showIntroOverlay;
          })() && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 100,
              overflow: 'hidden',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              {/* Animated Media or Default Background */}
              <div style={{
                width: '110%',
                height: '110%',
                backgroundColor: '#000',
                position: 'absolute',
                top: '-5%',
                left: '-5%',
                // Pre-position at animation start to prevent flicker
                transform: isAutoPlaying ? 'none' : 'translateX(3%) scale(1.03)',
                animation: isAutoPlaying ? 'introKenBurns 4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards' : 'none',
                transition: isAutoPlaying ? 'none' : 'transform 0.3s ease'
              }}>
                <style>{`
                  @keyframes introKenBurns {
                    0% {
                      transform: translateX(3%) scale(1.03);
                    }
                    95% {
                      transform: translateX(-3%) scale(1) rotate(0.2deg);
                      opacity: 1;
                    }
                    100% {
                      transform: translateX(-3%) scale(1) rotate(0.2deg);
                      opacity: 0.8;
                    }
                  }
                `}</style>
                {getMoveEnhancement(contextCurrentMove, 'startPositionMedia')?.fileURL ? (
                  // User uploaded media
                  getMoveEnhancement(contextCurrentMove, 'startPositionMedia')?.fileType?.startsWith('video/') ? (
                    <video
                      src={getMoveEnhancement(contextCurrentMove, 'startPositionMedia')?.fileURL}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      autoPlay
                      loop
                      muted
                      playsInline
                    />
                  ) : (
                    <img
                      src={getMoveEnhancement(contextCurrentMove, 'startPositionMedia')?.fileURL}
                      alt="Start Position Media"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  )
                ) : (
                  // Default chess logo
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#000'
                  }}>
                    <img
                      src={require('../../images/chesssboard2.png')}
                      alt="Chess Logo"
                      style={{
                        width: 'auto',
                        height: '130%',
                        opacity: 0.7
                      }}
                    />
                  </div>
                )}
              </div>
              
              {/* Title Overlay */}
              {(getMoveEnhancement(contextCurrentMove, 'startPositionIntroTitle')?.showOverlay ?? true) && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: introTextSlideOut 
                    ? 'translate(-50%, -50%) translateX(100vw)' 
                    : 'translate(-50%, -50%)',
                  textAlign: 'center',
                  color: '#000',
                  fontSize: 'clamp(36px, 8vw, 60px)',
                  fontWeight: 'bold',
                  lineHeight: 1.2,
                  textShadow: '2px 2px 4px #FFD700, -2px -2px 4px #FFD700, 2px -2px 4px #FFD700, -2px 2px 4px #FFD700, 0 0 10px #FFD700',
                  maxWidth: '95%',
                  wordBreak: 'normal',
                  overflowWrap: 'normal',
                  zIndex: 10,
                  transition: 'transform 0.5s ease-in-out'
                }}>
                  {(getMoveEnhancement(contextCurrentMove, 'startPositionIntroTitle')?.text || 
                    localAnnotationEdits['__title__']?.value || 
                    gameMetadata?.caption || 
                    'Chess Game').split(' ').map((word, index) => {
                    const isCurrentWord = index === currentSpeakingWordIndex;
                    if (isCurrentWord) {
                      console.log(`[Word Animation] Rendering word ${index} as current: "${word}"`);
                    }
                    return (
                      <div 
                        key={index}
                        style={{
                          marginBottom: '8px', // Increased vertical gap
                          transition: 'transform 0.3s ease, font-size 0.3s ease',
                          transformOrigin: 'center',
                          transform: isCurrentWord ? 'scale(1.3)' : 'scale(1)', // Scale up current word
                          fontWeight: isCurrentWord ? '900' : 'bold', // Extra bold for current word
                          textShadow: isCurrentWord 
                            ? '3px 3px 6px #FFD700, -3px -3px 6px #FFD700, 3px -3px 6px #FFD700, -3px 3px 6px #FFD700, 0 0 15px #FFD700, 0 0 25px #FFD700'
                            : '2px 2px 4px #FFD700, -2px -2px 4px #FFD700, 2px -2px 4px #FFD700, -2px 2px 4px #FFD700, 0 0 10px #FFD700'
                        }}
                      >
                        {word}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Title and Username Section - Fixed Height - Only show in main tab */}
          {recordSubTab === 'main' && (
            <div style={{
              textAlign: 'center',
              height: '80px',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              paddingTop: '35px', // Spacing for Youtube Instagram TikTok Overlay
              // Add smooth fade-in when transitioning from intro to first move
              animation: (contextCurrentMove === '0' && isAutoPlaying && 
                         (getMoveEnhancement('-1', 'startPositionIntro')?.enabled ?? true)) ? 
                         'fadeInFromIntro 0.4s ease-out' : 'none',
              position: 'relative',
              zIndex: 1
            }}>
            {/* Subtitle - Editable in Record Mode */}
            {isEditingUsername ? (
              <input
                ref={recordUsernameInputRef}
                value={localAnnotationEdits['__username__']?.value || `Made with ChessThread`}
                onChange={(e) => handleLocalAnnotationChange('__username__', 'value', e.target.value)}
                onBlur={() => setIsEditingUsername(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === 'Escape') {
                    setIsEditingUsername(false);
                  }
                }}
                style={{
                  background: 'transparent',
                  border: '2px solid gold',
                  borderRadius: '4px',
                  color: 'gold',
                  fontSize: '18px',
                  fontFamily: useChessThreadFont ? 'Mansalva, cursive' : '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  letterSpacing: useChessThreadFont ? '1.5px' : '0px',
                  textAlign: 'center',
                  width: 'calc(100% - 20px)',
                  maxWidth: '300px',
                  margin: '0 auto 12px auto',
                  padding: '4px 8px',
                  display: 'block',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                autoFocus
              />
            ) : (
              <p 
                onClick={() => viewMode === 'Record' && setIsEditingUsername(true)}
                style={{
                  color: 'gold',
                  fontSize: '16px',
                  margin: '0 0 12px 0',
                  textAlign: 'center',
                  fontFamily: useChessThreadFont ? 'Mansalva, cursive' : '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  letterSpacing: useChessThreadFont ? '1.5px' : '0px',
                  cursor: 'pointer',
                  opacity: 0.9,
                  transition: 'opacity 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                onMouseLeave={(e) => e.target.style.opacity = '0.9'}
                title="Click to edit subtitle"
              >
                {localAnnotationEdits['__username__']?.value || `Made with ChessThread`}
              </p>
            )}
            
            {/* Title - Editable in Record Mode */}
            {isEditingTitle ? (
              <input
                ref={recordTitleInputRef}
                value={localAnnotationEdits['__title__']?.value || gameMetadata?.caption || 'Chess Game'}
                onChange={(e) => handleLocalAnnotationChange('__title__', 'value', e.target.value)}
                onBlur={() => setIsEditingTitle(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === 'Escape') {
                    setIsEditingTitle(false);
                  }
                }}
                style={{
                  color: '#fff',
                  fontSize: '24px',
                  fontWeight: 'bold',
                  margin: '0 auto 8px auto',
                  lineHeight: '1.2',
                  letterSpacing: useChessThreadFont ? '2px' : '0px',
                  fontFamily: useChessThreadFont ? 'Mansalva, cursive' : '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  background: 'rgba(0, 0, 0, 0.5)',
                  border: '1px solid gold',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  textAlign: 'center',
                  width: 'calc(100% - 20px)',
                  maxWidth: '300px',
                  display: 'block',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                autoFocus
              />
            ) : (
              <h2 
                onClick={() => viewMode === 'Record' && setIsEditingTitle(true)}
                style={{
                  color: '#fff',
                  fontSize: '24px',
                  fontWeight: 'bold',
                  margin: '0 0 8px 0',
                  lineHeight: '1.2',
                  letterSpacing: useChessThreadFont ? '2px' : '0px',
                  fontFamily: useChessThreadFont ? 'Mansalva, cursive' : '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  cursor: viewMode === 'Record' ? 'pointer' : 'default',
                  opacity: viewMode === 'Record' ? 0.9 : 1,
                  transition: 'opacity 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (viewMode === 'Record') {
                    e.target.style.opacity = '0.7';
                  }
                }}
                onMouseLeave={(e) => {
                  if (viewMode === 'Record') {
                    e.target.style.opacity = '0.9';
                  }
                }}
              >
                {localAnnotationEdits['__title__']?.value || gameMetadata?.caption || 'Chess Game'}
              </h2>
            )}
          </div>
          )}

          {/* Conditional Content: Main Record Mode OR Settings OR Move Enhancements */}
          {recordSubTab === 'settings' ? (
            /* Settings Subtab */
            <div style={{
              width: '100%',
              height: 'calc(100% - 80px)',
              display: 'flex',
              flexDirection: 'column',
              padding: '20px 0',
              color: '#fff',
              overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '20px' }}>
                <button
                  onClick={() => setRecordSubTab('main')}
                  style={{
                    backgroundColor: '#dc3545',
                    border: 'none',
                    color: '#fff',
                    padding: '4px 8px',
                    borderRadius: '50%',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px'
                  }}
                  title="Back to Main Record Tab"
                >
                  <i class="fa-solid fa-arrow-left"></i>
                </button>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  margin: '0',
                  textAlign: 'center',
                  color: '#ff6600',
                  fontFamily: useChessThreadFont ? 'Mansalva, cursive' : '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  letterSpacing: useChessThreadFont ? '2px' : '0px'
                }}>
                  Global Settings
                </h3>
              </div>
              
              {/* Total Length Display */}
              <div style={{
                padding: '20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0px'
              }}>
                <h4 style={{
                  fontSize: '14px',
                  fontWeight: 'normal',
                  margin: 0,
                  color: '#ccc'
                }}>
                  Total Length:
                </h4>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: '#ff0050'
                }}>
                  {totalRecordingTime.replace('.0', '')}s
                </div>
              </div>
              
              {/* Voice Controls */}
              <div style={{
                padding: '20px'
              }}>
                {/* Text to Speech Toggle */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px'
                }}>
                  <h4 style={{
                    fontSize: '14px',
                    fontWeight: 'normal',
                    margin: 0,
                    color: '#ccc'
                  }}>
                    Text to Speech <i className="fa-solid fa-volume-high"></i>
                  </h4>
                  <button
                    onClick={() => {
                      const newTTSState = !voiceoverEnabled;
                      setVoiceoverEnabled(newTTSState);
                      
                      // Unified TTS system - sync robotTTS for start position if it exists
                      if (getMoveEnhancement('-1', 'startPositionIntroTitle')) {
                        updateMoveEnhancement('-1', 'startPositionIntroTitle', {
                          ...getMoveEnhancement('-1', 'startPositionIntroTitle'),
                          robotTTS: newTTSState
                        });
                      }
                    }}
                    style={{
                      backgroundColor: voiceoverEnabled ? '#58b047' : '#666',
                      border: 'none',
                      color: '#fff',
                      padding: '6px 12px',
                      borderRadius: '15px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      minWidth: '50px'
                    }}
                  >
                    {voiceoverEnabled ? 'ON' : 'OFF'}
                  </button>
                </div>

                {/* Voice Selection - Only show when TTS is enabled */}
                {voiceoverEnabled && (
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '20px',
                      fontSize: '14px',
                      color: '#ccc'
                    }}>
                      Voice:
                    </label>
                    <select
                    value={selectedVoiceIndex || ''}
                    onChange={(e) => {
                      const voiceIndex = e.target.value === '' ? null : parseInt(e.target.value);
                      setSelectedVoiceIndex(voiceIndex);
                      if (voiceIndex !== null) {
                        localStorage.setItem('chess-record-voice', voiceIndex.toString());
                      } else {
                        localStorage.removeItem('chess-record-voice');
                      }
                    }}
                    style={{
                      padding: '8px',
                      fontSize: '14px',
                      borderRadius: '6px',
                      border: '1px solid #666',
                      backgroundColor: '#333',
                      color: '#fff',
                      width: '100%'
                    }}
                  >
                    <option value="">System Default</option>
                    {availableVoices.map((voice, index) => (
                      <option key={index} value={index}>
                        {voice.name} ({voice.lang})
                      </option>
                    ))}
                  </select>
                  </div>
                )}
                
                {/* Speech Rate - Only show when TTS is enabled */}
                {/* {voiceoverEnabled && (
                  <div style={{ marginBottom: '0' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    color: '#ccc'
                  }}>
                    Speech Rate: {ttsRate.toFixed(1)}x
                  </label>
                  <input
                    type="range"
                    min="0.5"
                    max="2.0"
                    step="0.1"
                    value={ttsRate}
                    onChange={(e) => {
                      const rate = parseFloat(e.target.value);
                      setTtsRate(rate);
                      localStorage.setItem('chess-voice-rate', rate.toString());
                    }}
                    style={{
                      width: '100%',
                      accentColor: '#ff6600'
                    }}
                  />
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '12px',
                    color: '#999',
                    marginTop: '4px'
                  }}>
                    <span>Slow (0.5x)</span>
                    <span>Fast (2.0x)</span>
                  </div>
                  </div>
                )}
                 */}
                {/* Playback Speed Control */}
                <div style={{ marginBottom: '0', marginTop: voiceoverEnabled ? '20px' : '0' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    color: '#ccc'
                  }}>
                    Playback Speed: {playbackSpeedMultiplier.toFixed(1)}x
                  </label>
                  <input
                    type="range"
                    min="0.5"
                    max="2.0"
                    step="0.1"
                    value={playbackSpeedMultiplier}
                    onChange={(e) => {
                      const speed = parseFloat(e.target.value);
                      setPlaybackSpeedMultiplier(speed);
                      localStorage.setItem('chess-playback-speed', speed.toString());
                    }}
                    style={{
                      width: '100%',
                      accentColor: '#9b59b6'
                    }}
                  />
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '12px',
                    color: '#999',
                    marginTop: '4px'
                  }}>
                    <span>Slow (0.5x)</span>
                    <span>Fast (2.0x)</span>
                  </div>
                  {/* <div style={{
                    fontSize: '11px',
                    color: '#999',
                    marginTop: '6px',
                    lineHeight: '1.3'
                  }}>
                    Affects visual playback and Text-to-Speech
                  </div> */}
                </div>
              </div>
              
              {/* Display Settings */}
              <div style={{
                padding: '0px 20px'
              }}>
                {/* Font Selection */}
                <div style={{ marginBottom: '0' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    color: '#ccc'
                  }}>
                    Title Font:
                  </label>
                  <select
                    value={useChessThreadFont ? 'chessthread' : 'basic'}
                    onChange={(e) => {
                      const useChessThread = e.target.value === 'chessthread';
                      setUseChessThreadFont(useChessThread);
                      localStorage.setItem('chess-use-chessthread-font', JSON.stringify(useChessThread));
                    }}
                    style={{
                      padding: '8px',
                      fontSize: '14px',
                      borderRadius: '6px',
                      border: '1px solid #666',
                      backgroundColor: '#333',
                      color: '#fff',
                      width: '100%'
                    }}
                  >
                    <option value="chessthread">ChessThread</option>
                    <option value="basic">Basic Font</option>
                  </select>
                </div>
              </div>
              
              {/* Background Settings */}
              <div style={{
                padding: '20px'
              }}>
                <div style={{ marginBottom: '15px' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    <label style={{
                      fontSize: '14px',
                      color: '#ccc',
                      margin: 0
                    }}>
                      Background:
                    </label>
                    
                    {/* Upload Button - Flush Right */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleBackgroundImageUpload}
                        style={{
                          display: 'none'
                        }}
                        id="background-image-upload"
                      />
                      <label
                        htmlFor="background-image-upload"
                        style={{
                          padding: '6px 12px',
                          fontSize: '12px',
                          borderRadius: '6px',
                          border: '1px solid #666',
                          backgroundColor: '#333',
                          color: '#fff',
                          cursor: 'pointer',
                          textAlign: 'center'
                        }}
                      >
                        {backgroundImage ? 'Change' : 'Upload Image'}
                      </label>
                      
                      {backgroundImage && (
                        <button
                          onClick={clearBackgroundImage}
                          style={{
                            padding: '6px 12px',
                            fontSize: '12px',
                            borderRadius: '6px',
                            border: '1px solid #ff0050',
                            backgroundColor: 'transparent',
                            color: '#ff0050',
                            cursor: 'pointer'
                          }}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Background Preview */}
                  {backgroundImage && (
                    <div style={{
                      width: '100%',
                      height: '60px',
                      backgroundImage: `url(${backgroundImage})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      borderRadius: '6px',
                      border: '1px solid #666',
                      marginBottom: '10px',
                      position: 'relative'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        borderRadius: '4px',
                        padding: '2px 6px',
                        fontSize: '10px',
                        color: '#ccc'
                      }}>
                        16:9 Preview
                      </div>
                    </div>
                  )}
                  
                  {/* Color Swatches */}
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap',
                    marginBottom: '10px'
                  }}>
                    {[
                      '#1a1a1a', // Dark Gray
                      '#2c2c54', // Dark Blue
                      '#40407a', // Purple
                      '#ff6b6b', // Red
                      '#feca57', // Yellow
                      '#48cae4', // Light Blue
                      '#06ffa5', // Green
                      '#ff9ff3', // Pink
                      '#54a0ff', // Blue
                      '#5f27cd', // Deep Purple
                      '#00d2d3', // Cyan
                      '#ff9f43'  // Orange
                    ].map((color, index) => (
                      <button
                        key={color}
                        onClick={() => handleColorSwatchSelect(color)}
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '4px',
                          backgroundColor: color,
                          border: '2px solid #666',
                          cursor: 'pointer',
                          transition: 'transform 0.1s ease',
                          flexShrink: 0
                        }}
                        onMouseOver={(e) => {
                          e.target.style.transform = 'scale(1.1)';
                          e.target.style.border = '2px solid #fff';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.transform = 'scale(1)';
                          e.target.style.border = '2px solid #666';
                        }}
                        title={`Set background to ${color}`}
                      />
                    ))}
                  </div>
                  
                  <div style={{
                    fontSize: '11px',
                    color: '#999',
                    marginTop: '10px',
                    lineHeight: '1.3'
                  }}>
                    Upload a 16:9 image. Image will be cropped to fit.
                  </div>
                </div>
              </div>
              
              {/* Board Animation Settings */}
              {/* <div style={{
                padding: '20px'
              }}>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    color: '#ccc'
                  }}>
                    Board Animations:
                  </label>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '10px'
                  }}>
                    <input
                      type="checkbox"
                      checked={boardZoomConfig.autoZoom}
                      onChange={(e) => setBoardZoomConfig(prev => ({
                        ...prev,
                        autoZoom: e.target.checked
                      }))}
                      style={{
                        width: '16px',
                        height: '16px',
                        marginRight: '8px',
                        accentColor: '#ff6600'
                      }}
                      id="auto-zoom-checkbox"
                    />
                    <label
                      htmlFor="auto-zoom-checkbox" 
                      style={{
                        fontSize: '14px',
                        color: '#ccc',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      Auto-zoom on exciting moves
                    </label>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center'
                  }}>
                    <button
                      onClick={() => {
                        const currentMove = getMove(contextMoves, contextCurrentMove);
                        if (currentMove && (currentMove.toSquare || currentMove.to || currentMove.endSquare)) {
                          const endSquare = currentMove.toSquare || currentMove.to || currentMove.endSquare;
                          setBoardZoomConfig(prev => ({
                            ...prev,
                            scale: prev.scale === 1 ? 1.2 : 1,
                            focusSquare: prev.scale === 1 ? endSquare : null,
                            isAnimating: false
                          }));
                        }
                      }}
                      style={{
                        padding: '6px 12px',
                        fontSize: '12px',
                        borderRadius: '6px',
                        border: '1px solid #666',
                        backgroundColor: boardZoomConfig.scale > 1 ? '#ff6600' : '#333',
                        color: '#fff',
                        cursor: 'pointer'
                      }}
                    >
                      {boardZoomConfig.scale > 1 ? 'Reset Zoom' : 'Zoom Current'}
                    </button>
                    
                    <span style={{
                      fontSize: '11px',
                      color: '#999'
                    }}>
                      Scale: {boardZoomConfig.scale.toFixed(1)}x
                    </span>
                  </div>
                  
                  <div style={{
                    fontSize: '11px',
                    color: '#999',
                    marginTop: '6px',
                    lineHeight: '1.3'
                  }}>
                    Automatically zoom in on captures, checks, and tactical moves during auto-play.
                  </div>
                </div>
              </div> */}
            </div>
          ) : recordSubTab === 'move-enhancements' ? (
            /* Move Enhancements Subtab */
            <div style={{
              width: '100%',
              height: 'calc(100% - 80px)',
              display: 'flex',
              flexDirection: 'column',
              padding: '20px 0',
              color: '#fff',
              overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '20px' }}>
                <button
                  onClick={() => setRecordSubTab('main')}
                  style={{
                    backgroundColor: '#dc3545',
                    border: 'none',
                    color: '#fff',
                    padding: '4px 8px',
                    borderRadius: '50%',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px'
                  }}
                  title="Back to Main Record Tab"
                >
                  <i class="fa-solid fa-arrow-left"></i>
                     
                      </button>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  margin: '0',
                  textAlign: 'center',
                  color: '#ff6600',
                  fontFamily: useChessThreadFont ? 'Mansalva, cursive' : '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  letterSpacing: useChessThreadFont ? '2px' : '0px'
                }}>
                  Move Edits
                </h3>
              </div>
              
              {/* Current Move Info with integrated Start Button */}
              <div style={{
                padding: '10px 20px',
                marginBottom: '',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  
                  {/* Move Notation */}
                  <div style={{ 
                    fontSize: '16px', 
                    fontWeight: 'bold', 
                    color: '#1a1a1a' ,
                    background: '#58b047',
                    padding: '5px',
                    borderRadius: '5px'
                  }}>
                    {contextCurrentMove === '-1' ? 'Start Position' : (() => {
                      const moveIndex = parseInt(contextCurrentMove.split('|')[0]);
                      const moveNumber = Math.floor(moveIndex / 2) + 1;
                      const isWhite = moveIndex % 2 === 0;
                      const notation = getMove(contextMoves, contextCurrentMove)?.notation || '';
                      return `${moveNumber}${isWhite ? '.' : '...'} ${notation}`;
                    })()} {contextCurrentMove === '-1' ? '' : (parseInt(contextCurrentMove) % 2 === 0 ? 'White' : 'Black')}
                  </div>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  {/* Minus Button */}
                  <button
                    onClick={() => adjustMoveTime(-0.2)}
                    disabled={!canDecreaseMoveTime()}
                    style={{
                      backgroundColor: 'transparent',
                      border: '2px solid #58b047',
                      borderRadius: '4px',
                      color: canDecreaseMoveTime() ? '#58b047' : '#666',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      padding: '2px 6px',
                      cursor: canDecreaseMoveTime() ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: canDecreaseMoveTime() ? 1 : 0.5
                    }}
                  >
                    <i className="fa-solid fa-minus"></i>
                  </button>
                  
                  {/* Timer */}
                  <div style={{ 
                    fontSize: '14px', 
                    color: '#58b047', 
                    fontWeight: 'bold',
                    border: '2px solid #58b047',
                    borderRadius: '6px',
                    padding: '4px 8px'
                  }}>
                    {currentMoveTime}
                  </div>
                  
                  {/* Plus Button */}
                  <button
                    onClick={() => adjustMoveTime(0.2)}
                    style={{
                      backgroundColor: 'transparent',
                      border: '2px solid #58b047',
                      borderRadius: '4px',
                      color: '#58b047',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      padding: '2px 6px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <i className="fa-solid fa-plus"></i>
                  </button>
                </div>
              </div>
              
              {/* Enhancement Categories - Conditional Rendering */}
              {contextCurrentMove === '-1' ? (
                /* Start Position Special Controls */
                <div style={{
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  
                  {/* Intro Title and Controls - Only show when intro is enabled */}
                  {(getMoveEnhancement(contextCurrentMove, 'startPositionIntro')?.enabled ?? true) && (
                    <>
                      {/* Title Input */}
                      <div style={{
                        padding: '0 20px 20px 20px'
                      }}>
                        <h4 style={{
                          fontSize: '14px',
                          fontWeight: 'normal',
                          margin: '10px 0 10px 0',
                          color: '#ccc'
                        }}>
                          Intro Title ({(getMoveEnhancement(contextCurrentMove, 'startPositionIntroTitle')?.text || 
                            localAnnotationEdits['__title__']?.value || 
                            gameMetadata?.caption || 
                            'Chess Game').length}/100)
                        </h4>
                        <input
                          type="text"
                          value={getMoveEnhancement(contextCurrentMove, 'startPositionIntroTitle')?.text || 
                                 localAnnotationEdits['__title__']?.value || 
                                 gameMetadata?.caption || 
                                 'Chess Game'}
                          onChange={(e) => {
                            const text = e.target.value.slice(0, 100);
                            updateMoveEnhancement(contextCurrentMove, 'startPositionIntroTitle', {
                              ...getMoveEnhancement(contextCurrentMove, 'startPositionIntroTitle'),
                              text: text
                            });
                          }}
                          maxLength={100}
                          style={{
                            width: '70%',
                            height: '32px',
                            backgroundColor: 'rgba(0, 0, 0, 0.3)',
                            color: '#fff',
                            border: '1px solid #666',
                            borderRadius: '6px',
                            padding: '10px',
                            fontSize: '14px',
                            fontFamily: 'inherit',
                            position: 'relative'
                          }}
                          placeholder="Enter intro title..."
                        />
                      </div>
                      
                      {/* Show Title and Robot TTS Checkboxes */}
                      <div style={{
                        padding: '0 20px 10px 20px',
                        display: 'flex',
                        gap: '30px'
                      }}>
                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '14px',
                          color: '#ccc',
                          cursor: 'pointer'
                        }}>
                          <input
                            type="checkbox"
                            checked={getMoveEnhancement(contextCurrentMove, 'startPositionIntroTitle')?.showOverlay ?? true}
                            onChange={(e) => {
                              updateMoveEnhancement(contextCurrentMove, 'startPositionIntroTitle', {
                                ...getMoveEnhancement(contextCurrentMove, 'startPositionIntroTitle'),
                                text: getMoveEnhancement(contextCurrentMove, 'startPositionIntroTitle')?.text || 
                                      localAnnotationEdits['__title__']?.value || 
                                      gameMetadata?.caption || 
                                      'Chess Game',
                                showOverlay: e.target.checked,
                                robotTTS: getMoveEnhancement(contextCurrentMove, 'startPositionIntroTitle')?.robotTTS ?? false
                              });
                            }}
                            style={{
                              width: '16px',
                              height: '16px'
                            }}
                          />
                          Show Title
                        </label>
                        
                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '14px',
                          color: '#ccc',
                          cursor: 'pointer'
                        }}>
                          <input
                            type="checkbox"
                            checked={voiceoverEnabled}
                            onChange={(e) => {
                              // Unified TTS system - sync both settings
                              setVoiceoverEnabled(e.target.checked);
                              updateMoveEnhancement(contextCurrentMove, 'startPositionIntroTitle', {
                                ...getMoveEnhancement(contextCurrentMove, 'startPositionIntroTitle'),
                                text: getMoveEnhancement(contextCurrentMove, 'startPositionIntroTitle')?.text || 
                                      localAnnotationEdits['__title__']?.value || 
                                      gameMetadata?.caption || 
                                      'Chess Game',
                                showOverlay: getMoveEnhancement(contextCurrentMove, 'startPositionIntroTitle')?.showOverlay ?? true,
                                robotTTS: e.target.checked
                              });
                            }}
                            style={{
                              width: '16px',
                              height: '16px'
                            }}
                          />
                          TTS <i className="fa-solid fa-volume-high"></i>
                        </label>
                      </div>
                    </>
                  )}
                  
                  {/* File Upload Area - Only show when intro is enabled */}
                  {(getMoveEnhancement(contextCurrentMove, 'startPositionIntro')?.enabled ?? true) && (
                  <div style={{
                    padding: '10px 20px'
                  }}>
                    <h4 style={{
                      fontSize: '14px',
                      fontWeight: 'normal',
                      margin: '0 0 15px 0',
                      color: '#ccc'
                    }}>
                      Intro Background
                    </h4>
                    
                    {/* Hidden File Input */}
                    <input
                      type="file"
                      accept="image/*,video/*"
                      style={{ display: 'none' }}
                      ref={(fileInput) => {
                        if (fileInput && !fileInput.dataset.initialized) {
                          fileInput.dataset.initialized = 'true';
                          fileInput.addEventListener('change', (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              try {
                                // console.log('File selected:', file.name, file.type, file.size);
                                
                                // Validate file size (max 50MB for mobile)
                                if (file.size > 50 * 1024 * 1024) {
                                  alert('File too large. Please select a file under 50MB.');
                                  return;
                                }
                                
                                // Create file URL for preview
                                const fileURL = URL.createObjectURL(file);
                                // console.log('Created blob URL:', fileURL);
                                
                                updateMoveEnhancement(contextCurrentMove, 'startPositionMedia', {
                                  enabled: true,
                                  file: file,
                                  fileURL: fileURL,
                                  fileName: file.name,
                                  fileType: file.type
                                });
                              } catch (error) {
                                console.error('Error processing file upload:', error);
                                alert('Failed to process file. Please try again.');
                              }
                            } else {
                              // console.log('No file selected');
                            }
                            // Reset input to allow re-selecting same file
                            e.target.value = '';
                          });
                        }
                      }}
                    />
                    
                    {/* Upload Area - Only show if no file uploaded */}
                    {!getMoveEnhancement(contextCurrentMove, 'startPositionMedia')?.fileURL && (
                      <div
                        onClick={(e) => {
                          const fileInput = e.currentTarget.parentNode.querySelector('input[type="file"]');
                          if (fileInput) {
                            // console.log('Triggering file input click');
                            fileInput.click();
                          }
                        }}
                        style={{
                          border: '2px dashed #666',
                          borderRadius: '8px',
                          padding: '20px',
                          textAlign: 'center',
                          backgroundColor: 'rgba(0, 0, 0, 0.3)',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          // marginBottom: '15px'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.borderColor = '#58b047';
                          e.target.style.backgroundColor = 'rgba(88, 176, 71, 0.1)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.borderColor = '#666';
                          e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.3)';
                        }}
                      >
                        <i className="fa-solid fa-upload" style={{ fontSize: '24px', color: '#666', marginBottom: '' }}></i>
                        {/* <div style={{ fontSize: '14px', color: '#ccc', marginBottom: '' }}>
                          Upload JPG, PNG, GIF, MP4, MOV
                        </div> */}
                      
                      </div>
                    )}
                    
                    {/* File Preview */}
                    {getMoveEnhancement(contextCurrentMove, 'startPositionMedia')?.fileURL && (
                      <div style={{
                        border: '1px solid #58b047',
                        borderRadius: '8px',
                        padding: '15px',
                        backgroundColor: 'rgba(88, 176, 71, 0.1)'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <div style={{ fontSize: '12px', color: '#58b047', fontWeight: 'bold' }}>
                            Uploaded: {getMoveEnhancement(contextCurrentMove, 'startPositionMedia')?.fileName}
                          </div>
                          <button
                            onClick={() => {
                              // Clean up file URL
                              const currentMedia = getMoveEnhancement(contextCurrentMove, 'startPositionMedia');
                              if (currentMedia?.fileURL) {
                                URL.revokeObjectURL(currentMedia.fileURL);
                              }
                              updateMoveEnhancement(contextCurrentMove, 'startPositionMedia', null);
                            }}
                            style={{
                              backgroundColor: '#ff4444',
                              border: 'none',
                              color: '#fff',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Remove
                          </button>
                        </div>
                        
                        {/* Media Preview */}
                        {getMoveEnhancement(contextCurrentMove, 'startPositionMedia')?.fileType?.startsWith('image/') ? (
                          <img
                            src={getMoveEnhancement(contextCurrentMove, 'startPositionMedia')?.fileURL}
                            alt="Preview"
                            style={{
                              width: '100%',
                              maxHeight: '120px',
                              objectFit: 'contain',
                              borderRadius: '4px'
                            }}
                          />
                        ) : (
                          <video
                            src={getMoveEnhancement(contextCurrentMove, 'startPositionMedia')?.fileURL}
                            style={{
                              width: '100%',
                              maxHeight: '120px',
                              objectFit: 'contain',
                              borderRadius: '4px'
                            }}
                            controls
                            muted
                          />
                        )}
                      </div>
                    )}
                  </div>
                  )}
                  
                  {/* Add Intro Checkbox */}
                  <div style={{
                    padding: '10px 20px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <h4 style={{
                      fontSize: '14px',
                      fontWeight: 'normal',
                      margin: 0,
                      color: '#ccc'
                    }}>
                      Add Intro
                    </h4>
                    <input
                      type="checkbox"
                      checked={getMoveEnhancement(contextCurrentMove, 'startPositionIntro')?.enabled ?? true}
                      onChange={(e) => {
                        updateMoveEnhancement(contextCurrentMove, 'startPositionIntro', {
                          enabled: e.target.checked
                        });
                      }}
                      style={{
                        width: '18px',
                        height: '18px',
                        accentColor: '#58b047',
                        cursor: 'pointer'
                      }}
                    />
                  </div>
                  
                  
                </div>
              ) : (
                /* Regular Move Enhancements */
                <div style={{
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  
                  {/* Zoom, Mini-Text, and Annotation Buttons - Single Row */}
                  <div style={{
                    padding: '20px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '15px'
                  }}>
                    
                    
                    {/* Mini-Text Button */}
                    <button
                      onClick={() => {
                        // Toggle or create text overlay for this move
                        const currentTextOverlay = getMoveEnhancement(contextCurrentMove, 'textOverlay');
                        if (currentTextOverlay?.text) {
                          // Text overlay exists, scroll to it
                          setTimeout(() => {
                            const miniTextSection = document.querySelector('[data-section="mini-text-overlay"]');
                            if (miniTextSection) {
                              miniTextSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                          }, 100);
                        } else {
                          // No text overlay, create one with placeholder
                          updateMoveEnhancement(contextCurrentMove, 'textOverlay', {
                            enabled: true,
                            text: '',
                            position: 'beneath-square'
                          });
                          // Scroll to it after it's created
                          setTimeout(() => {
                            const miniTextSection = document.querySelector('[data-section="mini-text-overlay"]');
                            if (miniTextSection) {
                              miniTextSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              // Focus on the input field
                              const input = miniTextSection.querySelector('input');
                              if (input) {
                                input.focus();
                              }
                            }
                          }, 100);
                        }
                      }}
                      style={{
                        backgroundColor: getMoveEnhancement(contextCurrentMove, 'textOverlay')?.enabled ? '#58b047' : '#666',
                        border: 'none',
                        color: '#fff',
                        padding: '12px 16px',
                        borderRadius: '15px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        minWidth: '80px',
                        height: '60px'
                      }}
                    >
                      Mini-Text
                    </button>
                    
                    {/* Annotation Button - Always show, green when has content */}
                    {(() => {
                      const currentMoveId = contextCurrentMove === '-1' ? '-1' : (contextCurrentMove || 'start');
                      const localEdits = localAnnotationEdits[currentMoveId] || {};
                      const hasContent = localEdits.content !== undefined ? localEdits.content : '';
                      
                      // Always show button, green if has content
                      return (
                        <button
                          onClick={() => {
                            // Switch to main record mode and set this specific move as actively editing
                            setRecordSubTab('main');
                            setActivelyEditingMoveId(currentMoveId);
                            // Small delay to ensure DOM is updated before focus
                            setTimeout(() => {
                              if (recordTextareaRef.current) {
                                recordTextareaRef.current.focus();
                              }
                            }, 100);
                          }}
                          style={{
                            backgroundColor: hasContent ? '#58b047' : '#666',
                            border: 'none',
                            color: '#fff',
                            padding: '12px 16px',
                            borderRadius: '15px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            minWidth: '80px',
                            height: '60px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px'
                          }}
                        >
                          Annotate
                          <i className="fa-solid fa-volume-high"></i>
                        </button>
                      );
                    })()}
                    {/* Zoom Button */}
                    <button 
                      onClick={() => {
                        // Toggle zoom enhancement for this move
                        const currentZoomEnhancement = getMoveEnhancement(contextCurrentMove, 'zoom');
                        if (currentZoomEnhancement && currentZoomEnhancement.enabled) {
                          // Remove zoom enhancement
                          updateMoveEnhancement(contextCurrentMove, 'zoom', null);
                        } else {
                          // Add zoom enhancement
                          updateMoveEnhancement(contextCurrentMove, 'zoom', {
                            enabled: true,
                            scale: 1.5
                            // duration is calculated dynamically based on playback speed
                          });
                        }
                      }}
                      style={{
                        backgroundColor: getMoveEnhancement(contextCurrentMove, 'zoom')?.enabled ? '#58b047' : '#666',
                        border: 'none',
                        color: '#fff',
                        padding: '0px 16px',
                        borderRadius: '15px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        minWidth: '80px',
                        height: '60px'
                      }}
                    >
                     {getMoveEnhancement(contextCurrentMove, 'zoom')?.enabled ? 'Zoom Enabled' : 'Zoom In'}
                    </button>
                  </div>

                  {/* Text Enhancement (Mini-Text Overlay) - Show per move */}
                  {(() => {
                    const hasTextOverlay = getMoveEnhancement(contextCurrentMove, 'textOverlay')?.enabled;
                    
                    // Only show the section if current move has text overlay enhancement
                    if (hasTextOverlay) {
                      return (
                        <div 
                          data-section="mini-text-overlay"
                          style={{
                            padding: '10px 20px'
                          }}>
                          <h4 style={{
                            fontSize: '14px',
                            fontWeight: 'normal',
                            margin: '0 0 15px 0',
                            color: '#ccc'
                          }}>
                            Mini-Text Overlay (silent)
                          </h4>
                          
                          {/* Text Input */}
                          <div style={{
                            marginBottom: '10px'
                          }}>
                            <input
                              type="text"
                              placeholder="Enter text..."
                              maxLength={9}
                              value={getMoveEnhancement(contextCurrentMove, 'textOverlay')?.text || ''}
                              onChange={(e) => {
                                const text = e.target.value;
                                if (text.length === 0) {
                                  // Clear text overlay if empty
                                  updateMoveEnhancement(contextCurrentMove, 'textOverlay', null);
                                } else {
                                  // Set text overlay
                                  updateMoveEnhancement(contextCurrentMove, 'textOverlay', {
                                    enabled: true,
                                    text: text,
                                    position: 'beneath-square'
                                  });
                                }
                              }}
                              style={{
                                width: '100%',
                                maxWidth: '200px',
                                padding: '8px 12px',
                                borderRadius: '4px',
                                border: '1px solid #666',
                                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                color: '#fff',
                                fontSize: '14px',
                                outline: 'none',
                                boxSizing: 'border-box'
                              }}
                            />
                          </div>
                        </div>
                      );
                    }
                    return null; // Hide if no text overlay exists
                  })()}
                  
                  {/* Classifications Enhancement */}
                  <div style={{
                    padding: '10px 20px'
                  }}>
                    <h4 style={{
                      fontSize: '14px',
                      fontWeight: 'normal',
                      margin: '0 0 15px 0',
                      color: '#ccc'
                    }}>
                      Classifications
                    </h4>
                    
                    {/* Icon Grid */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(5, 1fr)',
                      gap: '8px',
                      marginBottom: '10px'
                    }}>
                      {CLASSIFICATION_ICONS.map((classificationIcon) => {
                        const currentClassification = getMoveEnhancement(contextCurrentMove, 'classification');
                        const isSelected = currentClassification?.iconId === classificationIcon.id;
                        
                        return (
                          <div
                            key={classificationIcon.id}
                            onClick={() => {
                              if (isSelected) {
                                // Toggle off - clear the classification
                                updateMoveEnhancement(contextCurrentMove, 'classification', null);
                              } else {
                                // Toggle on - set this classification
                                updateMoveEnhancement(contextCurrentMove, 'classification', {
                                  enabled: true,
                                  iconId: classificationIcon.id,
                                  iconName: classificationIcon.name,
                                  iconPath: classificationIcon.icon,
                                  position: 'target-square'
                                });
                              }
                            }}
                            style={{
                              width: '32px',
                              height: '32px',
                              border: isSelected ? '2px solid #58b047' : '1px solid #666',
                              borderRadius: '4px',
                              backgroundColor: isSelected ? 'rgba(88, 176, 71, 0.2)' : 'rgba(0, 0, 0, 0.3)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              opacity: isSelected ? 1 : 0.7
                            }}
                            title={classificationIcon.name}
                          >
                            <img
                              src={classificationIcon.icon}
                              alt={classificationIcon.name}
                              style={{
                                width: '20px',
                                height: '20px',
                                objectFit: 'contain'
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Current Selection Display */}
                    {getMoveEnhancement(contextCurrentMove, 'classification') && (
                      <div style={{
                        fontSize: '12px',
                        color: '#58b047',
                        textAlign: 'center',
                        marginTop: '8px',
                        fontWeight: 'bold'
                      }}>
                        Selected: {getMoveEnhancement(contextCurrentMove, 'classification')?.iconName}
                      </div>
                    )}
                  </div>
                  
                  
                  {/* Image Overlay Enhancement */}
                  {/* <div style={{
                    padding: '20px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <h4 style={{
                      fontSize: '14px',
                      fontWeight: 'normal',
                      margin: 0,
                      color: '#ccc'
                    }}>
                      Image Overlay
                    </h4>
                    <button 
                      onClick={() => {
                        // Toggle image enhancement for this move
                        // const currentImageEnhancement = getMoveEnhancement(contextCurrentMove, 'image');
                        // if (currentImageEnhancement && currentImageEnhancement.enabled) {
                        //   // Remove image enhancement
                        //   updateMoveEnhancement(contextCurrentMove, 'image', null);
                        // } else {
                        //   // Add image enhancement
                        //   updateMoveEnhancement(contextCurrentMove, 'image', {
                        //     enabled: true,
                        //     imagePath: null
                        //   });
                        // }
                      }}
                      style={{
                        backgroundColor: getMoveEnhancement(contextCurrentMove, 'image')?.enabled ? '#58b047' : '#666',
                        border: 'none',
                        color: '#fff',
                        padding: '6px 12px',
                        borderRadius: '15px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        minWidth: '50px'
                      }}
                    >
                      {getMoveEnhancement(contextCurrentMove, 'image')?.enabled ? 'SOON' : 'SOON'}
                    </button>
                  </div> */}
                  
                </div>
              )}
            </div>
          ) : (
            /* Main Record Mode Content */
            <>
          {/* Chess Board - Fixed Container with Zoom Support */}
          <div style={{
            width: (() => {
              const baseSize = window.calculateRecordModeBoardSize();
              const finalContainerSize = baseSize * 1.02; // Container size NEVER changes
              console.log('🔍 [ViewGameMobile] BOARD CONTAINER SIZE - FIXED:', { 
                baseSize, 
                finalContainerSize,
                isZoomed: boardZoomConfig.isZoomed, 
                zoomFactor: boardZoomConfig.zoomFactor,
                containerStaysAt: `${finalContainerSize}px`
              });
              return `${finalContainerSize}px`;
            })(),
            height: (() => {
              const baseSize = window.calculateRecordModeBoardSize();
              const finalContainerSize = baseSize * 1.02; // Container size NEVER changes
              console.log('🔍 [ViewGameMobile] BOARD CONTAINER HEIGHT - FIXED:', `${finalContainerSize}px`);
              return `${finalContainerSize}px`;
            })(),
            marginBottom: '8px',
            flexShrink: 0,  // Prevent board from shrinking
            position: 'relative',
            zIndex: 1,
            overflow: 'hidden', // Hide scaled board edges for zoom effect
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            // Add smooth fade-in when transitioning from intro to first move
            animation: (contextCurrentMove === '0' && isAutoPlaying) ? 
                       'fadeInFromIntro 0.2s ease-out' : 'none' // Start Frame Animation Template
          }}>
            <style>{`
              @keyframes fadeInFromIntro {
                0% {
                  opacity: 0.7;
                  transform: scale(0.98);
                }
                100% {
                  opacity: 1;
                  transform: scale(1);
                }
              }
            `}</style>
            
            {/* Scalable Board Wrapper */}
            <div style={{
              width: `${window.calculateRecordModeBoardSize() * (boardZoomConfig.isZoomed ? boardZoomConfig.zoomFactor : 1)}px`,
              height: `${window.calculateRecordModeBoardSize() * (boardZoomConfig.isZoomed ? boardZoomConfig.zoomFactor : 1)}px`,
              transform: (() => {
                if (!boardZoomConfig.isZoomed || !boardZoomConfig.targetSquare) {
                  console.log('🔍 [ViewGameMobile] No zoom or no target square - translate(0, 0)');
                  return 'translate(0, 0)';
                }
                
                const baseSize = window.calculateRecordModeBoardSize();
                const zoomedSize = baseSize * boardZoomConfig.zoomFactor;
                const orientation = contextOrientation || 'white';
                const targetSquare = boardZoomConfig.targetSquare;
                
                // Parse target square (e.g., 'e4' -> file: 4, rank: 3)
                const file = targetSquare.charCodeAt(0) - 'a'.charCodeAt(0); // 0-7 (a-h)
                const rank = parseInt(targetSquare[1]) - 1; // 0-7 (1-8)
                
                console.log('🔍 [ViewGameMobile] Smart positioning for:', { 
                  targetSquare, 
                  file, 
                  rank, 
                  orientation 
                });
                
                // Calculate smart slide direction based on edge position and orientation
                let slideDirection = { x: 0, y: 0 }; // 0 = center, -1 = left/up, 1 = right/down
                
                // Horizontal slide logic
                if (file <= 1) {
                  // A or B file - show LEFT side
                  slideDirection.x = orientation === 'white' ? 1 : -1; // FLIPPED: slide RIGHT to show left side better
                } else if (file >= 6) {
                  // G or H file - show RIGHT side  
                  slideDirection.x = orientation === 'white' ? -1 : 1; // FLIPPED: slide LEFT to show right side better
                }
                
                // Vertical slide logic
                if (rank <= 1) {
                  // Rank 1 or 2 - show BOTTOM
                  slideDirection.y = orientation === 'white' ? -1 : 1; // FLIPPED: slide UP to show bottom better
                } else if (rank >= 6) {
                  // Rank 7 or 8 - show TOP
                  slideDirection.y = orientation === 'white' ? 1 : -1; // FLIPPED: slide DOWN to show top better
                }
                
                // Calculate translation based on slide direction
                const maxTranslate = (zoomedSize - baseSize) / 2;
                // SLIDE DISTANCE: 50% of max - adjust this percentage to control slide amount
                const slidePercentage = 0.2; // TODO: Adjust this value to control slide distance (0.3-0.7 range)
                let translateX = slideDirection.x * maxTranslate * slidePercentage;
                let translateY = slideDirection.y * maxTranslate * slidePercentage;
                
                // Final constraint to prevent empty space
                translateX = Math.max(-maxTranslate, Math.min(maxTranslate, translateX));
                translateY = Math.max(-maxTranslate, Math.min(maxTranslate, translateY));
                
                const result = `translate(${translateX}px, ${translateY}px)`;
                console.log('🔍 [ViewGameMobile] Smart slide result:', { 
                  slideDirection, 
                  translateX, 
                  translateY,
                  result,
                  targetSquare 
                });
                return result;
              })(),
              transition: 'none', // Instant change as requested
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: boardZoomConfig.isZoomed ? 'rgba(255, 0, 0, 0.1)' : 'transparent', // Debug red tint when zoomed
              border: boardZoomConfig.isZoomed ? '2px solid red' : 'none' // Debug border
            }}>
            <BoardThree
              boardSize={(() => {
                const baseSize = window.calculateRecordModeBoardSize();
                const finalBoardSize = boardZoomConfig.isZoomed ? baseSize * boardZoomConfig.zoomFactor : baseSize;
                console.log('🔍 [ViewGameMobile] BOARDTHREE BOARDSIZE PROP:', {
                  baseSize,
                  finalBoardSize,
                  isZoomed: boardZoomConfig.isZoomed,
                  zoomFactor: boardZoomConfig.zoomFactor,
                  'ACTUAL PROP VALUE': finalBoardSize
                });
                return finalBoardSize;
              })()}
              ref={boardRef}
              isRecordMode={true}
              moveEnhancements={moveEnhancements}
              boardTheme={localStorage.getItem('boardTheme') || 'blue'}
              showBoardNotation={localStorage.getItem('showCoordinates') === 'true'}
              
              boardStyle={{
                borderRadius: '8px',
                boxShadow: '0 0 20px rgba(0,0,0,0.5)'
              }}
              stockfishAnalysis={stockfishAnalysis}
              stockfishIsAnalyzing={stockfishIsAnalyzing}
              isMobile={true}
              isPuzzleMode={gameHasPuzzleTag}
              puzzleIncorrectMove={puzzleIncorrectMove}
              showPuzzleOverlay={gameHasPuzzleTag}
              onNewMove={detectNewMove}
              mobileActiveColor={mobileActiveColor}
              mobileArrowMode={mobileArrowMode}
              mobileArrowColor={mobileArrowColor}
              mobileArrowStart={mobileArrowStart}
              onMobileArrowStart={setMobileArrowStart}
              arePiecesDraggable={!mobileArrowMode && mobileActiveColor === undefined}
              onMobileArrowEnd={(endSquare) => {
                if (boardRef.current) {
                  boardRef.current.handleMobileArrowEnd(endSquare);
                }
              }}
            />
            </div> {/* Close scalable board wrapper */}
          </div> {/* Close fixed board container */}

          {/* Conditional Content Area - Takes Remaining Space */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            overflow: 'hidden',
            // Add smooth fade-in when transitioning from intro to first move
            animation: (contextCurrentMove === '0' && isAutoPlaying) ? 
                       'fadeInFromIntro 0.2s ease-out' : 'none' // Start Frame Animation Template
          }}>
{(() => {
            const currentMove = getMove(contextMoves, contextCurrentMove);
            const originalMoveAnnotation = currentMove?.annotation || '';
            const originalMoveContent = currentMove?.content || '';
            const moveNotation = currentMove?.notation || '';
            
            // Create unique move ID for local editing
            const currentMoveId = contextCurrentMove === '-1' ? '-1' : (contextCurrentMove || 'start');
            const localEdits = localAnnotationEdits[currentMoveId] || {};
            
            // Use local edits if available, fallback to original
            const moveAnnotation = localEdits.annotation !== undefined ? localEdits.annotation : originalMoveAnnotation;
            const moveContent = localEdits.content !== undefined ? localEdits.content : originalMoveContent;
            
            // Calculate move number for display
            let moveDisplay = '';
            if (contextCurrentMove !== '-1' && moveNotation) {
              const moveIndex = parseInt(contextCurrentMove.split('|')[0]);
              const moveNumber = Math.floor(moveIndex / 2) + 1;
              const isWhite = moveIndex % 2 === 0;
              moveDisplay = `${moveNumber}${isWhite ? '.' : '...'} ${moveNotation}`;
            }
            
            const hasAnnotation = !!(moveAnnotation || moveContent || (viewMode === 'Record' && activelyEditingMoveId === currentMoveId));
            
            return (
              <div style={{
                position: 'relative',
                width: '100%',
                maxWidth: '400px',
                overflow: 'hidden',
                zIndex: 1
              }}>
                {/* Layer 1: MobileMovesEngine with Slider (normal flow - maintains container height) */}
                <div style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  boxSizing: 'border-box',
                  padding: '0px 10px',
                  opacity: hasAnnotation ? 0 : 1,
                  pointerEvents: hasAnnotation ? 'none' : 'auto'
                }}>
                  {/* Move Slider */}
                  <div style={{
                    width: '100%',
                    height: '28px',
                    marginBottom: '4px',
                    flexShrink: 0,
                    padding: '0 20px',
                    boxSizing: 'border-box',
                    position: 'relative'
                  }}>
                    <style>
                      {`
                        .enhanced-progress-slider {
                          position: relative;
                          width: 100%;
                          height: 12px;
                          background: #333;
                          border-radius: 6px;
                          cursor: pointer;
                          overflow: hidden;
                          touch-action: none;
                          user-select: none;
                        }
                        .enhanced-progress-slider:active {
                          transform: scaleY(1.2);
                          transition: transform 0.1s ease;
                        }
                        .progress-fill {
                          position: absolute;
                          top: 0;
                          left: 0;
                          height: 100%;
                          background: linear-gradient(90deg, #58b047, #6bc252);
                          border-radius: 6px;
                          transition: width 0.2s ease;
                          pointer-events: none;
                        }
                        .progress-hover-preview {
                          position: absolute;
                          top: -35px;
                          background: rgba(0,0,0,0.8);
                          color: white;
                          padding: 4px 8px;
                          border-radius: 4px;
                          font-size: 12px;
                          font-weight: bold;
                          pointer-events: none;
                          transform: translateX(-50%);
                          opacity: 0;
                          transition: opacity 0.2s ease;
                          z-index: 20;
                        }
                        .enhanced-progress-slider:hover .progress-hover-preview {
                          opacity: 1;
                        }
                      `}
                    </style>
                    <div 
                      className="enhanced-progress-slider"
                      onMouseDown={(e) => {
                        if (isAutoPlaying) {
                          if (autoPlayIntervalRef.current) {
                            clearTimeout(autoPlayIntervalRef.current);
                          }
                          setIsAutoPlaying(false);
                        }
                        
                        const sliderElement = e.currentTarget;
                        const rect = sliderElement.getBoundingClientRect();
                        const percentage = (e.clientX - rect.left) / rect.width;
                        const moveIndex = Math.round(percentage * contextMoves.length) - 1;
                        const targetMove = moveIndex < 0 ? '-1' : String(moveIndex);
                        goToMove(targetMove);
                        
                        const handleMouseMove = (moveEvent) => {
                          const newRect = sliderElement.getBoundingClientRect();
                          const newPercentage = Math.max(0, Math.min(1, (moveEvent.clientX - newRect.left) / newRect.width));
                          const newMoveIndex = Math.round(newPercentage * contextMoves.length) - 1;
                          const newTargetMove = newMoveIndex < 0 ? '-1' : String(newMoveIndex);
                          goToMove(newTargetMove);
                        };
                        
                        const handleMouseUp = () => {
                          document.removeEventListener('mousemove', handleMouseMove);
                          document.removeEventListener('mouseup', handleMouseUp);
                        };
                        
                        document.addEventListener('mousemove', handleMouseMove);
                        document.addEventListener('mouseup', handleMouseUp);
                      }}
                      onTouchStart={(e) => {
                        if (isAutoPlaying) {
                          if (autoPlayIntervalRef.current) {
                            clearTimeout(autoPlayIntervalRef.current);
                          }
                          setIsAutoPlaying(false);
                        }
                        
                        const sliderElement = e.currentTarget;
                        const rect = sliderElement.getBoundingClientRect();
                        const touch = e.touches[0];
                        const percentage = (touch.clientX - rect.left) / rect.width;
                        const moveIndex = Math.round(percentage * contextMoves.length) - 1;
                        const targetMove = moveIndex < 0 ? '-1' : String(moveIndex);
                        goToMove(targetMove);
                        
                        const handleTouchMove = (moveEvent) => {
                          const newTouch = moveEvent.touches[0];
                          const newRect = sliderElement.getBoundingClientRect();
                          const newPercentage = Math.max(0, Math.min(1, (newTouch.clientX - newRect.left) / newRect.width));
                          const newMoveIndex = Math.round(newPercentage * contextMoves.length) - 1;
                          const newTargetMove = newMoveIndex < 0 ? '-1' : String(newMoveIndex);
                          goToMove(newTargetMove);
                        };
                        
                        const handleTouchEnd = () => {
                          document.removeEventListener('touchmove', handleTouchMove);
                          document.removeEventListener('touchend', handleTouchEnd);
                        };
                        
                        document.addEventListener('touchmove', handleTouchMove);
                        document.addEventListener('touchend', handleTouchEnd);
                      }}
                    >
                      {/* Progress Fill */}
                      <div 
                        className="progress-fill"
                        style={{
                          width: `calc(30px + ${contextMoves.length > 0 ? ((parseInt(contextCurrentMove === '-1' ? -1 : contextCurrentMove.split('|')[0]) + 1) / contextMoves.length) * (100 - 6) : 0}%)`,
                          marginLeft: '0px'
                        }}
                      />
                      
                      
                      {/* Hover Preview */}
                      <div className="progress-hover-preview">
                        Move {contextCurrentMove === '-1' ? 'Start' : (parseInt(contextCurrentMove.split('|')[0]) + 1)}
                      </div>
                    </div>
                    {/* Progress Bar Circle Indicator with Selected Icon - TEMPORARILY HIDDEN */}
                    {/* <div 
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowIconSelector(!showIconSelector);
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        
                        // If it's a right-click, show icon selector
                        if (e.button === 2) {
                          setShowIconSelector(!showIconSelector);
                          return;
                        }
                        
                        // Otherwise, handle drag
                        if (isAutoPlaying) {
                          if (autoPlayIntervalRef.current) {
                            clearTimeout(autoPlayIntervalRef.current);
                          }
                          setIsAutoPlaying(false);
                        }
                        
                        const sliderElement = e.currentTarget.parentElement.querySelector('.enhanced-progress-slider');
                        if (!sliderElement) return;
                        
                        const handleMouseMove = (moveEvent) => {
                          const rect = sliderElement.getBoundingClientRect();
                          const percentage = Math.max(0, Math.min(1, (moveEvent.clientX - rect.left) / rect.width));
                          const moveIndex = Math.round(percentage * contextMoves.length) - 1;
                          const targetMove = moveIndex < 0 ? '-1' : String(moveIndex);
                          goToMove(targetMove);
                        };
                        
                        const handleMouseUp = () => {
                          document.removeEventListener('mousemove', handleMouseMove);
                          document.removeEventListener('mouseup', handleMouseUp);
                        };
                        
                        document.addEventListener('mousemove', handleMouseMove);
                        document.addEventListener('mouseup', handleMouseUp);
                      }}
                      onTouchStart={(e) => {
                        e.stopPropagation();
                        
                        if (isAutoPlaying) {
                          if (autoPlayIntervalRef.current) {
                            clearTimeout(autoPlayIntervalRef.current);
                          }
                          setIsAutoPlaying(false);
                        }
                        
                        const sliderElement = e.currentTarget.parentElement.querySelector('.enhanced-progress-slider');
                        if (!sliderElement) return;
                        
                        const handleTouchMove = (moveEvent) => {
                          const rect = sliderElement.getBoundingClientRect();
                          const newTouch = moveEvent.touches[0];
                          const percentage = Math.max(0, Math.min(1, (newTouch.clientX - rect.left) / rect.width));
                          const moveIndex = Math.round(percentage * contextMoves.length) - 1;
                          const targetMove = moveIndex < 0 ? '-1' : String(moveIndex);
                          goToMove(targetMove);
                        };
                        
                        const handleTouchEnd = () => {
                          document.removeEventListener('touchmove', handleTouchMove);
                          document.removeEventListener('touchend', handleTouchEnd);
                        };
                        
                        document.addEventListener('touchmove', handleTouchMove);
                        document.addEventListener('touchend', handleTouchEnd);
                      }}
                      style={{
                        position: 'absolute',
                        top: '25%',
                        left: `calc(30px + ${((parseInt(contextCurrentMove === '-1' ? -1 : contextCurrentMove.split('|')[0]) + 1) / (contextMoves.length + 1)) * (100 - 6)}%)`,
                        transform: 'translate(-50%, -50%)',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: '#58b047',
                        border: '2px solid #fff',
                        zIndex: 15,
                        pointerEvents: 'auto',
                        cursor: 'grab',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <img 
                        src={CLASSIFICATION_ICONS.find(icon => icon.id === selectedProgressIcon)?.icon || BestIcon} 
                        alt="Progress" 
                        style={{
                          width: '12px',
                          height: '12px',
                          objectFit: 'contain'
                        }}
                      />
                    </div> */}
                    
                    {/* Icon Selector Panel - Two Rows */}
                    {showIconSelector && (
                      <div style={{
                        position: 'absolute',
                        top: '35px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #444',
                        borderRadius: '8px',
                        padding: '8px',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(5, 1fr)',
                        gap: '8px',
                        zIndex: 10,
                        boxShadow: '0 4px 8px rgba(0,0,0,0.5)'
                      }}>
                        {CLASSIFICATION_ICONS.map((icon) => (
                          <div
                            key={icon.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedProgressIcon(icon.id);
                              setShowIconSelector(false);
                            }}
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              backgroundColor: selectedProgressIcon === icon.id ? '#444' : '#2a2a2a',
                              border: selectedProgressIcon === icon.id ? '2px solid #58b047' : '1px solid #333',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#444';
                            }}
                            onMouseLeave={(e) => {
                              if (selectedProgressIcon !== icon.id) {
                                e.currentTarget.style.backgroundColor = '#2a2a2a';
                              }
                            }}
                            title={icon.name}
                          >
                            <img 
                              src={icon.icon} 
                              alt={icon.name}
                              style={{
                                width: '18px',
                                height: '18px',
                                objectFit: 'contain'
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* MobileMovesEngine */}
                  <div style={{
                    overflow: 'hidden',
                    minHeight: '0',
                    padding: '0px 5px'
                  }}>
                    <MobileMovesEngine
                      moves={contextMoves}
                      currentMove={contextCurrentMove}
                      goToMove={goToMove}
                      orientation={contextOrientation}
                      isMobile={true}
                      boardSize={Math.min(boardSize, window.innerWidth - 40)}
                      starredMoves={contextStarredMoves}
                      startPosition={state.startPosition}
                      isCommentMode={false}
                      onEnterCommentMode={() => {}}
                      commentMoves={[]}
                      currentCommentMoveIndex={-1}
                      hideTabSidebar={true}
                      hideMainLineHeader={true}
                    />
                  </div>
                </div>
                
                {/* Layer 2: Annotation Overlay (absolute positioning on top) */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  boxSizing: 'border-box',
                  opacity: hasAnnotation ? 1 : 0,
                  pointerEvents: hasAnnotation ? 'auto' : 'none',
                  zIndex: 2,
                  backgroundColor: hasAnnotation ? 'transparent' : 'transparent'
                }}>
                  <div style={{
                    width: '100%',
                    maxWidth: '320px',
                    margin: '10px auto',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    boxSizing: 'border-box',
                    backgroundColor: '#a1a1a150',

                      // border: '2px solid #1a1a1a',
                      textAlign: 'center',
                      borderRadius: '6px',
                      padding: '10px'


                  }}>
                
                      {/* Green Move Button Indicator */}
                      {moveDisplay && (
                        <div style={{
                          display: 'inline-block',
                          backgroundColor: '#58b047',
                          color: 'black',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          marginBottom: '8px',
                          maxWidth: '60px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {moveDisplay}
                        </div>
                      )}
{(moveContent || (viewMode === 'Record' && activelyEditingMoveId === currentMoveId)) && (
                        <div style={{
                          border: (viewMode === 'Record' && (!moveContent || activelyEditingMoveId === currentMoveId)) ? '2px solid #58b047' : 'none',
                          borderRadius: '6px',
                          padding: (viewMode === 'Record' && (!moveContent || activelyEditingMoveId === currentMoveId)) ? '8px' : '0'
                        }}>
                          <textarea
                            ref={recordTextareaRef}
                            value={moveContent}
                            onChange={(e) => handleLocalAnnotationChange(currentMoveId, 'content', e.target.value)}
                            placeholder={viewMode === 'Record' && !moveContent ? 'Add content annotation...' : ''}
                            onFocus={() => setActivelyEditingMoveId(currentMoveId)}
                            onBlur={() => {
                              // Only close if there's no content
                              if (!moveContent) {
                                setActivelyEditingMoveId(null);
                              }
                            }}
                            onKeyDown={(e) => {
                              // Exit editing mode on Escape key
                              if (e.key === 'Escape') {
                                if (!moveContent) {
                                  setActivelyEditingMoveId(null);
                                }
                                e.target.blur();
                              }
                            }}
                            onKeyUp={(e) => {
                              // Force blur check after any key input
                              setTimeout(() => {
                                if (!moveContent && !e.target.value.trim() && activelyEditingMoveId === currentMoveId) {
                                  // If still empty after keyup, consider closing on next blur
                                  e.target.setAttribute('data-can-close', 'true');
                                }
                              }, 100);
                            }}
                            style={{
                              color: 'white',
                              fontSize: '16px',
                              fontStyle: 'italic',
                              fontWeight: 'bold',
                              background: 'transparent',
                              border: 'none',
                              width: '100%',
                              textAlign: 'center',
                              resize: 'none',
                              minHeight: '20px',
                              outline: 'none'
                            }}
                            onInput={(e) => {
                              // Auto-resize textarea to fit content
                              e.target.style.height = 'auto';
                              e.target.style.height = e.target.scrollHeight + 'px';
                            }}
                          />
                        </div>
                      )}
                  </div>
                </div>
              </div>
            );
          })()}
          
          </div> {/* Close Conditional Content Area */}
            </>
          )}
          
          {/* Fixed Bottom Content - Logo or Navigation Controls */}
          {recordSubTab === 'move-enhancements' ? (
            /* Navigation Controls for Move Enhancements */
            <div style={{
              position: 'absolute',
              bottom: '15px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1,
              width: '90%',
              maxWidth: '320px'
            }}>
              <MobileNavigationControls
                currentMoveOverride={contextCurrentMove}
                movesOverride={contextMoves}
                goToMoveCallback={goToMove}
              />
            </div>
          ) : (
            /* ChessThread Logo for other tabs */
            <div style={{
              position: 'absolute',
              bottom: '15px',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: 'gold',
              fontSize: '16px',
              fontFamily: 'Mansalva, cursive',
              letterSpacing: '1px',
              opacity: 0.8,
              pointerEvents: 'none',
              zIndex: 1
            }}>
              <img 
                src={chessslogo} 
                alt="ChessThread Logo" 
                style={{ 
                  width: '40px', 
                  height: '40px',
                  objectFit: 'contain'
                }} 
              />
              <span>ChessThread</span>
            </div>
          )}
          
          </div> {/* Close 16:9 container */}
          
          {/* External Controls Panel - Single Row */}
          <div style={{
            width: 'min(calc((100vw - 20px) * 0.95), calc((100dvh - 130px) * 9/16 * 0.95), 380px)',
            maxWidth: '380px',
            height: '60px',
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            justifyContent: 'space-evenly',
            padding: '0 10px',
            marginTop: '10px',
            backgroundColor: 'transparent',
            boxSizing: 'border-box'
          }}>
            {/* Home Button */}
            <button
              onClick={() => setViewMode('View')}
              style={{
                backgroundColor: 'rgba(255, 0, 80, 0.7)',
                border: 'none',
                color: '#fff',
                width: '36px',
                height: '36px',
                borderRadius: '18px',
                fontSize: '16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
              title="Home"
            >
              <i className="fa-solid fa-house"></i>
            </button>
            
            {/* TTS Button */}
            <button
              onClick={() => {
                const newTTSState = !voiceoverEnabled;
                setVoiceoverEnabled(newTTSState);
                if (!newTTSState && isSpeaking) {
                  stopSpeaking();
                }
              }}
              style={{
                backgroundColor: voiceoverEnabled ? '#58b047' : '#666',
                border: 'none',
                color: '#fff',
                width: '36px',
                height: '36px',
                borderRadius: '18px',
                fontSize: '16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
              title={voiceoverEnabled ? "TTS ON" : "TTS OFF"}
            >
              <i className="fa-solid fa-volume-high"></i>
            </button>
            
            {/* Navigation Controls or BOARD VIEW button */}
            {recordSubTab === 'main' ? (
              <MobileNavigationControls
                currentMoveOverride={contextCurrentMove}
                movesOverride={contextMoves}
                goToMoveCallback={goToMove}
                autoPlayState={isAutoPlaying}
                toggleAutoPlayCallback={toggleAutoPlay}
              />
            ) : (
              <button
                onClick={() => setRecordSubTab('main')}
                style={{
                  backgroundColor: '#3066be',
                  border: 'none',
                  color: '#fff',
                  width: '36px',
                  height: '36px',
                  borderRadius: '18px',
                  fontSize: '16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
                title="Return to board view"
              >
                <i className="fa-solid fa-chess-board"></i>
              </button>
            )}
            
            {/* Settings Button */}
            <button
              onClick={() => setRecordSubTab(recordSubTab === 'settings' ? 'main' : 'settings')}
              style={{
                backgroundColor: recordSubTab === 'settings' ? '#ff6600' : '#666',
                border: 'none',
                color: '#fff',
                width: '36px',
                height: '36px',
                borderRadius: '18px',
                fontSize: '16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
              title="Settings"
            >
              <i className="fa-solid fa-gear"></i>
            </button>
            
            {/* Move Enhancements Button */}
            <button
              onClick={() => setRecordSubTab(recordSubTab === 'move-enhancements' ? 'main' : 'move-enhancements')}
              style={{
                backgroundColor: recordSubTab === 'move-enhancements' ? '#ff6600' : '#666',
                border: 'none',
                color: '#fff',
                width: '36px',
                height: '36px',
                borderRadius: '18px',
                fontSize: '16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
              title="Move Enhancements"
            >
              <i className="fa-solid fa-chess-pawn"></i>
            </button>
            
         
          </div>
        </div>
      ) : (
      /* Normal View Mode */
      <div style={{
        width: '100vw',
        minHeight: '100dvh',
        backgroundColor: '#1a1a1a',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        // Proper safe area handling for iPad
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'calc(55px + env(safe-area-inset-bottom))', // Bottom nav + safe area
        boxSizing: 'border-box',
        transform: `translateY(${translateY}px)`,
        transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      >
      
      {/* Chess Board and Moves Container */}
      {!showComments && (
        <div style={{ 
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flex: 1,
          overflow: 'hidden',
          paddingBottom: '10px' // Minimal padding since we're calculating proper board size
          }}>
          <div style={{
            width: `${boardSize}px`,
            maxWidth: `${boardSize}px`,
            display: 'flex',
            flexDirection: 'column',
            gap: 'clamp(4px, 1vh, 8px)',
            boxSizing: 'border-box'
          }}>          

            {/* Chess Board */}
            <div style={{ 
              width: '100%',
              position: 'relative',
              display: 'flex',
              justifyContent: 'center'
            }}>
              {game && (
                <div
                  style={{ touchAction: 'none' }}
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchMove={(e) => {
                    e.stopPropagation();
                    e.preventDefault(); // Prevent default scroll
                  }}
                  onTouchEnd={(e) => e.stopPropagation()}
                >
                  <BoardThree 
                    ref={boardRef}
                    boardSize={boardSize} 
                    boardTheme={localStorage.getItem('boardTheme') || 'blue'}
                    showBoardNotation={localStorage.getItem('showCoordinates') === 'true'}
                    isViewMode={true}
                    isRecordMode={false}
                    isCommenting={isCommentMode}
                    // Add player data from gameMetadata
                    initialPOVPlayer={gameMetadata?.povPlayer}
                    initialOppositePlayer={gameMetadata?.oppositePlayer}
                    // Add comment mode detection
                    onNewMove={detectNewMove}
                    // Add puzzle mode detection to disable engine arrows
                    gameHasPuzzleTag={gameHasPuzzleTag}
                    // Mobile annotation tool props
                    activeColor={mobileActiveColor}
                    mobileArrowMode={mobileArrowMode}
                    mobileArrowColor={mobileArrowColor}
                    mobileArrowStart={mobileArrowStart}
                    onMobileArrowStart={setMobileArrowStart}
                    arePiecesDraggable={!mobileArrowMode && mobileActiveColor === undefined}
                  />
                  
                  {/* Puzzle Overlay System */}
                  {gameHasPuzzleTag && (
                    <>
                      {/* Incorrect Move Overlay */}
                      {puzzleIncorrectMove && (
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          backgroundColor: 'rgba(255, 77, 77, 0.95)',
                          color: 'white',
                          padding: '20px 30px',
                          borderRadius: '10px',
                          fontSize: '18px',
                          fontWeight: 'bold',
                          textAlign: 'center',
                          zIndex: 1000,
                          border: '3px solid #ff4d4d',
                          boxShadow: '0 4px 12px rgba(255, 77, 77, 0.4)',
                          animation: 'fadeIn 0.3s ease-in-out'
                        }}>
                          <div>Try Again</div>
                          <div style={{ marginTop: '5px', fontSize: '24px', color: 'white' }}><i className="fa-solid fa-chess-pawn"></i></div>
                        </div>
                      )}
                      
                      {/* Puzzle Complete Overlay */}
                      {puzzleComplete && (
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          backgroundColor: 'rgba(88, 176, 71, 0.95)',
                          color: 'white',
                          padding: '20px 30px',
                          borderRadius: '10px',
                          fontSize: '18px',
                          fontWeight: 'bold',
                          textAlign: 'center',
                          zIndex: 1000,
                          border: '3px solid #58b047',
                          boxShadow: '0 4px 12px rgba(88, 176, 71, 0.4)',
                          animation: 'fadeIn 0.3s ease-in-out'
                        }}>
                          <div>Puzzle Complete</div>
                          <div style={{ marginTop: '10px', fontSize: '24px', color: 'white' }}>
                            <i className="fa-solid fa-chess-king"></i>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Navigation Controls - Hide on iPads */}
           {!isTablet && (
           <div style={{ border: '1px solid #333', borderRadius: '10px', backgroundColor: '', }}>
             <MobileNavigationControls 
               isCommentMode={isCommentMode}
               commentMoves={commentMoves}
               currentCommentMoveIndex={currentCommentMoveIndex}
               goToMoveCallback={goToMove}
               currentMoveOverride={contextCurrentMove}
               movesOverride={contextMoves}
               gameHasPuzzleTag={gameHasPuzzleTag}
               puzzleComplete={puzzleComplete}
               onCommentMoveClick={(moveIndex) => {
                 updateHalfMoveStates(moveIndex);
                 setCurrentCommentMoveIndex(moveIndex);
                 navigateToCommentMove(moveIndex);
                //  console.log('Navigate to comment move via controls:', moveIndex);
               }}
               onTriggerExit={() => setShowConfirmation(true)}
               onConfirmDelete={confirmDelete}
             />
           </div>
           )}
              

            

            {/* Moves and Engine Content */}
            <div style={{ 
              width: '100%',
              boxSizing: 'border-box',
              border: '1px solid #444',
              borderRadius: '10px',
              padding: '4px',
              backgroundColor: '#0a0a0a'
            }}>
              <MobileMovesEngine 
                showStockfish={true}
                stockfishEvaluation={null}
                height={120}
                comments={getSortedCommentsForMobile(comments, contextMoves, contextCurrentMove)}
                findCommentVariations={findCommentVariations}
                goToMoveCallback={goToMove}
                isCommentMode={isCommentMode}
                commentModeSource={commentModeSource}
                commentStep={commentStep}
                commentMoves={commentMoves}
                currentCommentMoveIndex={currentCommentMoveIndex}
                parentMoveIndex={parentMoveIndex}
                showConfirmation={showConfirmation}
                onExitCommentMode={exitCommentMode}
                onGoBackToMoves={goBackToMoves}
                onProceedToCaption={proceedToCaption}
                onSubmitComment={submitCommentMoves}
                onCommentSubmit={handleUnifiedCommentSubmit}
                onCommentMoveClick={(moveIndex) => {
                  updateHalfMoveStates(moveIndex);
                  setCurrentCommentMoveIndex(moveIndex);
                  navigateToCommentMove(moveIndex);
                  // console.log('Navigate to comment move via MobileMovesEngine:', moveIndex);
                }}
                currentMoveNotation={getCurrentMoveNotation()}
                onConfirmDelete={confirmDelete}
                onCancelDelete={cancelDelete}
                onUpdateCommentModeSource={(newSource) => {
                  // console.log('Updating comment mode source from:', commentModeSource, 'to:', newSource);
                  setCommentModeSource(newSource);
                }}
                // Pass comment move content data and update callback
                commentMoveData={currentCommentMoveData}
                onUpdateCommentMoveContent={updateCurrentCommentMoveContent}
                gameHasPuzzleTag={gameHasPuzzleTag}
                puzzleProgress={puzzleProgress}
                puzzleComplete={puzzleComplete}
                currentFEN={state.currentFEN}
                puzzleCurrentMove={contextCurrentMove}
                setShowComments={setShowComments}
                onPuzzleRetry={() => {
                  // Reset puzzle to starting position
                  goToMove('-1');
                  setPuzzleIncorrectMove(false);
                  setPuzzleComplete(false);
                  const initialProgress = calculatePuzzleProgress('-1', state.moves);
                  setPuzzleProgress(initialProgress);
                }}
                onPuzzleSolution={() => {
                  // Autoplay all moves to completion (like desktop version)
                  // console.log('Showing puzzle solution - autoplaying all moves');
                  if (state.moves && state.moves.length > 0) {
                    // Start autoplay from current position to the end
                    let currentIndex = 0;
                    if (contextCurrentMove !== '-1' && !contextCurrentMove.includes('|')) {
                      currentIndex = parseInt(contextCurrentMove) + 1;
                    }
                    
                    const autoplayNextMove = (index) => {
                      if (index < state.moves.length) {
                        goToMove(index.toString());
                        // Continue to next move after 800ms delay (slower for solution viewing)
                        setTimeout(() => {
                          autoplayNextMove(index + 1);
                        }, 800);
                      } else {
                        // Reached the end of all moves - check if puzzle is complete
                        // console.log('Solution autoplay complete - checking puzzle completion');
                        const finalMoveIndex = state.moves.length - 1;
                        const nextOpponentMoveIndex = finalMoveIndex + 1;
                        
                        // If there are no more moves after the final move, puzzle is complete
                        if (nextOpponentMoveIndex >= state.moves.length) {
                          // console.log('Puzzle complete via solution autoplay');
                          setPuzzleComplete(true);
                        }
                      }
                    };
                    
                    // Start autoplay
                    autoplayNextMove(currentIndex);
                  }
                }}
                onNextPuzzle={handleNextGameClick}
                isInPuzzleFilter={isInPuzzleFilter()}
                redirectToPortal={redirectToPortalWithPuzzleFilter}
                onDeleteHalfMoves={deleteHalfMoves}
                // Board communication props for annotation tools
                onActiveColorChange={setMobileActiveColor}
                onMobileArrowModeChange={setMobileArrowMode}
                onMobileArrowColorChange={setMobileArrowColor}
                onMobileArrowStartChange={setMobileArrowStart}
                boardRef={boardRef}
              />
            </div>
            
            {/* Best Line Component - Show when in comment mode AND Comment Creation Interface is visible */}
            {isCommentMode && commentModeSource !== 'stockfish' && (
              <div style={{
                width: '100%',
                padding: '12px',
                backgroundColor: 'rgb(10, 10, 10',
                borderRadius: '10px',
                border: '1px solid gold',
                boxSizing: 'border-box',
                // marginBottom: '10px',
                height: '90px'
              }}>
                <BestLineComponent 
                  analysis={unifiedAnalysis} 
                  currentFEN={state.currentFEN} 
                  showLabelAbove={true}
                  userOrientation={contextOrientation}
                />
              </div>
            )}
            
            {/* Title and Username Section - Show when NOT in comment mode OR when in Stockfish comment mode */}
            {(!isCommentMode || (isCommentMode && commentModeSource === 'stockfish')) && (
            <div style={{
              width: '100%',
              padding: '0px',
              backgroundColor: '#1a1a1a',
              borderRadius: '10px',
              border: '1px solid #333',
              boxSizing: 'border-box'
            }}>
        {/* Title and Creator */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '8px',
          borderBottom: '1px solid #444',
          padding: '8px',
          paddingLeft: '12px',
          backgroundColor: '#0a0a0a',
          borderRadius: '10px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* Back Button - only show when we have an origin */}
            {location.state?.origin && (
              <i 
                className="fa-solid fa-arrow-left"
                onClick={() => {
                  // Check for pending comment moves before navigating
                  if (commentMoves.length > 0 && isCommentMode) {
                    // Show confirmation using existing Delete/Continue UI
                    setShowConfirmation(true);
                    return;
                  }
                  
                  // Navigate directly to the origin instead of using goBack
                  // This ensures we skip any intermediate game navigation
                  if (location.state.origin === 'BOOKMARKS') {
                    history.push('/bookmarks');
                  } else if (location.state.origin === 'PROFILE') {
                    // Extract profile handle from returnLabel if available
                    const match = location.state.returnLabel?.match(/@(\w+)/);
                    const handle = match ? match[1] : user?.credentials?.handle;
                    history.push(handle ? `/profile/${handle}` : '/profile');
                  } else if (location.state.origin === 'PORTAL' || 
                             location.state.origin === 'TRENDING' || 
                             location.state.origin === 'FOLLOWING') {
                    history.push('/portal', { activeTab: location.state.tab });
                  } else {
                    // Fallback to goBack if origin is unknown
                    history.goBack();
                  }
                }}
                style={{
                  color: '#888',
                  fontSize: '16px',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s ease'
                }}
                onMouseDown={(e) => e.currentTarget.style.opacity = '0.7'}
                onMouseUp={(e) => e.currentTarget.style.opacity = '1'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              />
            )}
            
            {(() => {
              // Check if we're in a comment variation and get commenter's username
              const currentMoveId = contextCurrentMove;
              let displayUsername = gameMetadata?.creatorHandle || 'Anonymous';
              let isCommentVariation = false;
              
              if (currentMoveId.includes('|')) {
                try {
                  // Use helper function to get the deepest comment info
                  const deepestCommentInfo = getDeepestCommentInfo(currentMoveId, contextMoves, state, comments);
                  if (deepestCommentInfo && deepestCommentInfo.username) {
                    displayUsername = deepestCommentInfo.username;
                    isCommentVariation = true;
                  }
                } catch (error) {
                  console.error('Error getting commenter username:', error);
                }
              }
              
              // Check if we're in Record mode to allow editing
              const isRecordMode = viewMode === 'Record';
              const customUsername = localAnnotationEdits['__username__']?.value;
              const finalDisplayUsername = customUsername || displayUsername;

              return (
                <div>
                  {isRecordMode && isEditingUsername ? (
                    <input
                      ref={recordUsernameInputRef}
                      value={customUsername || displayUsername || 'Anonymous'}
                      onChange={(e) => handleLocalAnnotationChange('__username__', 'value', e.target.value)}
                      onBlur={() => setIsEditingUsername(false)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === 'Escape') {
                          setIsEditingUsername(false);
                        }
                      }}
                      style={{
                        background: 'transparent',
                        border: '2px solid gold',
                        borderRadius: '4px',
                        color: isCommentVariation ? '#ff6600' : 'gold',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        fontFamily: 'inherit',
                        padding: '2px 6px',
                        textAlign: 'center',
                        width: '150px',
                        outline: 'none'
                      }}
                    />
                  ) : (
                    <div 
                      onClick={() => {
                        if (displayUsername && displayUsername !== 'Anonymous') {
                          history.push(`/profile/${displayUsername}`, {
                            returnTo: `/games/${gameId}`,
                            returnLabel: 'Back to Game',
                            from: 'game'
                          });
                        }
                      }}
                      style={{
                        color: isCommentVariation ? '#ff6600' : 'gold',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        cursor: displayUsername && displayUsername !== 'Anonymous' ? 'pointer' : 'default',
                        transition: 'opacity 0.2s ease'
                      }}
                      onMouseDown={(e) => {
                        if (displayUsername && displayUsername !== 'Anonymous') {
                          e.currentTarget.style.opacity = '0.7';
                        }
                      }}
                      onMouseUp={(e) => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                      @{finalDisplayUsername}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Dislike Button */}
            <button
              onClick={handleDislikeClick}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: gameMetadata?.dislikes?.includes(user?.credentials?.userId) ? '#ff0050' : '#888',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '6px',
                transition: 'color 0.2s ease'
              }}
              title="Dislike"
            >
              <i className="fa-solid fa-thumbs-down"></i>
            </button>

            {/* Admin/Tester Record Mode Button */}
            {(user?.credentials?.role === 'Admin' || user?.credentials?.role === 'Tester') && (
              <button
                onClick={() => setViewMode(viewMode === 'View' ? 'Record' : 'View')}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: viewMode === 'Record' ? '#9b59b6' : '#888',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '6px',
                  transition: 'color 0.2s ease'
                }}
                title={viewMode === 'Record' ? 'Exit Record Mode' : 'Enter Record Mode'}
              >
                <i className="fa-solid fa-video"></i>
              </button>
            )}

            {/* Bookmark Button */}
            <button
              onClick={handleBookmarkClick}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: isBookmarked ? 'gold' : '#888',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '6px',
                transition: 'color 0.2s ease'
              }}
              title={isBookmarked ? 'Remove Bookmark' : 'Bookmark'}
            >
              <i className={isBookmarked ? "fa-solid fa-bookmark" : "fa-regular fa-bookmark"}></i>
            </button>

            {/* Share Button */}
            <button
              onClick={() => {
                const shareUrl = window.location.href;
                setShareLink(shareUrl);
                
                if (navigator.share) {
                  navigator.share({
                    title: cleanProfanity(gameMetadata?.caption) || 'Chess Game',
                    text: `Check out this chess game by ${gameMetadata?.creatorHandle || 'Anonymous'}`,
                    url: shareUrl
                  }).catch(() => {
                    // If share fails, show popup
                    setShowSharePopup(true);
                  });
                } else {
                  // Show popup for manual copy
                  setShowSharePopup(true);
                }
              }}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: '#888',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '6px',
                transition: 'color 0.2s ease'
              }}
              title="Share"
            >
              <i className="fa-solid fa-paper-plane"></i>
            </button>

            {/* Comments Button */}
            {/* <button
              onClick={() => setShowComments(true)}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: comments && comments.length > 0 ? '#58b047' : '#888',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '6px',
                transition: 'color 0.2s ease',
                position: 'relative'
              }}
              title="Comments"
            >
              <i className="fa-solid fa-comment"></i>
              {comments && comments.length > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '2px',
                  right: '2px',
                  backgroundColor: '#58b047',
                  color: 'white',
                  borderRadius: '50%',
                  width: '14px',
                  height: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '9px',
                  fontWeight: 'bold'
                }}>
                  {comments.length > 9 ? '9+' : comments.length}
                </span>
              )}
            </button> */}

            {/* Like Button */}
            <button
              onClick={handleLikeClick}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: gameMetadata?.likes?.includes(user?.credentials?.userId) ? '#ff1493' : '#888',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '6px',
                transition: 'color 0.2s ease'
              }}
              title="Like"
            >
              <i className={gameMetadata?.likes?.includes(user?.credentials?.userId) ? "fa-solid fa-heart" : "fa-regular fa-heart"}></i>
            </button>
          </div>
        </div>

        {/* Caption with Info Icon */}
        {gameMetadata?.caption && (
          <div 
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px',
              paddingTop: '0px'
            }}
          >
            {/* Info/Pawn Icon */}
            <button
              onClick={() => setShowGameInfo(!showGameInfo)}
              style={{
                backgroundColor: showGameInfo ? '#333' : 'transparent',
                border: 'none',
                borderRadius: '8px',
color: showGameInfo ? '#ff0050' : (() => {
                  // Check if we're in a comment variation
                  const currentMoveId = contextCurrentMove;
                  let isInCommentVariation = false;
                  
                  // Check if current position is in a comment variation
                  if (currentMoveId.includes('|')) {
                    try {
                      const parts = currentMoveId.split('|');
                      const mainMoveIdx = parseInt(parts[0], 10);
                      const [variationIdx] = parts[1].split(':').map(Number);
                      
                      let variation = null;
                      if (mainMoveIdx === -1) {
                        variation = state?.startPosition?.variations?.[variationIdx];
                      } else {
                        const mainMove = contextMoves[mainMoveIdx];
                        variation = mainMove?.variations?.[variationIdx];
                      }
                      
                      // Check if this variation contains comment moves
                      if (variation && variation.length > 0) {
                        isInCommentVariation = variation.some(varMove => 
                          varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT
                        );
                      }
                    } catch (error) {
                      console.error('Error checking if in comment variation:', error);
                    }
                  }
                  
                  // If in comment variation, return orange
                  if (isInCommentVariation) return '#ff6600';
                  
                  // Otherwise check for content/annotations
                  let hasContent = false;
                  if (currentMoveId === '-1') {
                    hasContent = !!state.startPosition?.content;
                  } else if (!currentMoveId.includes('|')) {
                    const moveIndex = parseInt(currentMoveId, 10);
                    hasContent = !!contextMoves[moveIndex]?.content;
                  } else {
                    try {
                      const { getNodeAtPathInfinite } = require('../../util/VariationPathUtils');
                      const result = getNodeAtPathInfinite(contextMoves, currentMoveId, state);
                      hasContent = result.success && !!result.node?.content;
                    } catch (error) {
                      console.error('Error checking variation content:', error);
                    }
                  }
                  
                  return hasContent ? '#58b047' : '#ccc'; // green if has content, gray otherwise
                })(),
                fontSize: '16px',
                cursor: 'pointer',
                padding: '4px 8px',
                transition: 'all 0.2s ease',
                flexShrink: 0
              }}
              title="BOARD VIEW"
            >
              <i className="fa-solid fa-chess-pawn"></i>
            </button>
            
            {/* Move Annotation or Caption Text */}
            <div 
              onClick={() => setShowGameInfo(true)}
              style={{
color: (() => {
                  // Use same priority system as title and icon: Move Annotations > Variation Captions > Comment Captions > Default
                  const currentMoveId = contextCurrentMove;
                  let hasAnnotation = false;
                  let hasVariationCaption = false;
                  let hasCommentCaption = false;
                  
                  // 1. Check for move annotations
                  if (currentMoveId === '-1') {
                    hasAnnotation = !!state.startPosition?.content;
                  } else if (!currentMoveId.includes('|')) {
                    const moveIndex = parseInt(currentMoveId, 10);
                    hasAnnotation = !!contextMoves[moveIndex]?.content;
                  } else {
                    try {
                      const { getNodeAtPathInfinite } = require('../../util/VariationPathUtils');
                      const result = getNodeAtPathInfinite(contextMoves, currentMoveId, state);
                      hasAnnotation = result.success && !!result.node?.content;
                    } catch (error) {
                      console.error('Error checking variation content:', error);
                    }
                  }
                  
                  // 2. Check for variation captions (if no annotation)
                  if (!hasAnnotation && currentMoveId.includes('|')) {
                    try {
                      const parts = currentMoveId.split('|');
                      if (parts.length >= 2) {
                        const mainMoveIdx = parseInt(parts[0], 10);
                        const [variationIdx] = parts[1].split(':').map(Number);
                        
                        if (mainMoveIdx === -1) {
                          hasVariationCaption = !!state?.startPosition?.variationCaptions?.[variationIdx];
                        } else {
                          const mainMove = contextMoves[mainMoveIdx];
                          hasVariationCaption = !!(mainMove?.variationCaptions?.[variationIdx]);
                        }
                      }
                    } catch (error) {
                      console.error('Error checking variation caption:', error);
                    }
                  }
                  
                  // 3. Check for comment captions (if no annotation or variation caption)
                  if (!hasAnnotation && !hasVariationCaption && currentMoveId.includes('|')) {
                    try {
                      const parts = currentMoveId.split('|');
                      if (parts.length >= 2) {
                        const mainMoveIdx = parseInt(parts[0], 10);
                        const [variationIdx] = parts[1].split(':').map(Number);
                        
                        let variation = null;
                        if (mainMoveIdx === -1) {
                          variation = state?.startPosition?.variations?.[variationIdx];
                        } else {
                          const mainMove = contextMoves[mainMoveIdx];
                          variation = mainMove?.variations?.[variationIdx];
                        }
                        
                        if (variation && variation.length > 0) {
                          const hasCommentMoves = variation.some(varMove => 
                            varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT
                          );
                          
                          if (hasCommentMoves) {
                            const firstCommentMove = variation.find(varMove => 
                              varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT
                            );
                            
                            if (firstCommentMove) {
                              const creatorId = firstCommentMove.creator.id;
                              const matchingComment = comments?.find(comment => comment.id === creatorId);
                              hasCommentCaption = !!(matchingComment && matchingComment.content);
                            }
                          }
                        }
                      }
                    } catch (error) {
                      console.error('Error checking comment caption:', error);
                    }
                  }
                  
                  // Return color based on priority
                  if (hasAnnotation) return '#58b047'; // green
                  if (hasVariationCaption) return 'gold';
                  if (hasCommentCaption) return '#ff6600'; // orange
                  return '#ccc'; // default
                })(),
                fontSize: '16px',
                textAlign: 'left',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                lineHeight: '1.4',
                flex: 1
              }}
            >
              {(() => {
                // Priority system: Move Annotations > Comment Content > Game Title
                const currentMoveId = contextCurrentMove;
                let displayContent = null;
                let contentType = 'title'; // 'annotation', 'commentContent', 'title'
                
                // 1. First priority: Move annotations (move.content)
                if (currentMoveId === '-1') {
                  if (state.startPosition?.content) {
                    displayContent = state.startPosition.content;
                    contentType = 'annotation';
                  }
                } else if (!currentMoveId.includes('|')) {
                  const moveIndex = parseInt(currentMoveId, 10);
                  if (contextMoves[moveIndex]?.content) {
                    displayContent = contextMoves[moveIndex].content;
                    contentType = 'annotation';
                  }
                } else {
                  // Handle variation move content using VariationPathUtils
                  try {
                    const { getNodeAtPathInfinite } = require('../../util/VariationPathUtils');
                    const result = getNodeAtPathInfinite(contextMoves, currentMoveId, state);
                    if (result.success && result.node?.content) {
                      displayContent = result.node.content;
                      contentType = 'annotation';
                    }
                  } catch (error) {
                    console.error('Error getting variation content for display:', error);
                    displayContent = null;
                  }
                }
                
                // 2. Second priority: Comment content (if no annotation and in comment variation)
                if (!displayContent && currentMoveId.includes('|')) {
                  try {
                    // Use helper function to get the deepest comment info
                    const deepestCommentInfo = getDeepestCommentInfo(currentMoveId, contextMoves, state, comments);
                    if (deepestCommentInfo && deepestCommentInfo.content) {
                      displayContent = deepestCommentInfo.content;
                      contentType = 'commentContent';
                    }
                  } catch (error) {
                    console.error('Error getting comment content for display:', error);
                  }
                }
                
                // 3. Final fallback: Game title
                const finalContent = displayContent || gameMetadata.caption;
                
                // Apply color coding based on content type
                const getContentColor = () => {
                  switch (contentType) {
                    case 'commentContent':
                      return '#ff6600'; // orange for comment content
                    case 'annotation':
                      return '#58b047'; // green for annotations
                    case 'title':
                    default:
                      return '#ccc'; // default color
                  }
                };
                
                // Enhanced rendering for comment content with @username formatting
                if (contentType === 'commentContent' && finalContent) {
                  // Parse @username pattern and render with orange clickable links
                  const parts = cleanProfanity(finalContent).split(/(@\w+)/g);
                  
                  return (
                    <span style={{ color: getContentColor() }}>
                      {parts.map((part, index) => {
                        if (part.startsWith('@')) {
                          const username = part.substring(1);
                          return (
                            <span
                              key={index}
                              style={{
                                color: '#ff6600',
                                cursor: 'pointer',
                                textDecoration: 'underline'
                              }}
                              onClick={() => {
                                if (username && username !== 'Anonymous') {
                                  history.push(`/profile/${username}`, {
                                    returnTo: `/game/${gameId}`,
                                    returnLabel: 'Back to Game',
                                    from: 'game-title'
                                  });
                                }
                              }}
                            >
                              {part}
                            </span>
                          );
                        }
                        return part;
                      })}
                    </span>
                  );
                }
                
                return (
                  <span style={{ color: getContentColor() }}>
                    {cleanProfanity(finalContent)}
                  </span>
                );
              })()}
            </div>
          </div>
        )}
            </div>
            )}

          </div>
          
        </div>
      )}

      





      {/* Game Info Popup */}
      {showGameInfo && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '20px',
            paddingTop: '15vh' // Moved down to avoid iPhone notch cutoff
          }}
          onClick={() => setShowGameInfo(false)}
        >
          <div 
            style={{
              backgroundColor: '#222',
              border: '1px solid #444',
              borderRadius: '8px',
              padding: '16px',
              color: 'white',
              fontSize: '16px',
              width: '100%',
              maxWidth: '400px',
              maxHeight: '80vh',
              overflow: 'auto',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowGameInfo(false)}
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                backgroundColor: 'transparent',
                border: 'none',
                color: '#ff0050',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '4px 8px',
                lineHeight: '1',
                fontWeight: 'bold'
              }}
            >
              ✕
            </button>
          
          {(() => {
            // Get current move content and captions (including variations)
            const currentMoveId = contextCurrentMove;
            let moveContent = null;
            let variationCaption = null;
            let commentCaption = null;
            let commenterUsername = null;
            
            // Get move content
            if (currentMoveId === '-1') {
              moveContent = state.startPosition?.content;
            } else if (!currentMoveId.includes('|')) {
              const moveIndex = parseInt(currentMoveId, 10);
              moveContent = contextMoves[moveIndex]?.content;
            } else {
              // Handle variation move content using VariationPathUtils
              try {
                const { getNodeAtPathInfinite } = require('../../util/VariationPathUtils');
                const result = getNodeAtPathInfinite(contextMoves, currentMoveId, state);
                moveContent = (result.success && result.node?.content) ? result.node.content : null;
              } catch (error) {
                console.error('Error getting variation content for INFO POPUP:', error);
                moveContent = null;
              }
            }
            
            // Get variation and comment captions (if in variation)
            if (currentMoveId.includes('|')) {
              try {
                const parts = currentMoveId.split('|');
                if (parts.length >= 2) {
                  const mainMoveIdx = parseInt(parts[0], 10);
                  const [variationIdx] = parts[1].split(':').map(Number);
                  
                  // Get variation caption (only from first level for now)
                  if (mainMoveIdx === -1) {
                    variationCaption = state?.startPosition?.variationCaptions?.[variationIdx];
                  } else {
                    const mainMove = contextMoves[mainMoveIdx];
                    variationCaption = mainMove?.variationCaptions?.[variationIdx];
                  }
                  
                  // Use helper function to get the deepest comment info
                  const deepestCommentInfo = getDeepestCommentInfo(currentMoveId, contextMoves, state, comments);
                  if (deepestCommentInfo) {
                    commentCaption = deepestCommentInfo.content;
                    commenterUsername = deepestCommentInfo.username;
                  }
                }
              } catch (error) {
                console.error('Error getting captions for INFO POPUP:', error);
              }
            }
            
            // Show info popup if there's move content OR captions
            const hasContent = moveContent || variationCaption || commentCaption;
            
            return hasContent ? (
              // Move Content/Caption Section
              <div style={{ marginBottom: '12px' }}>
                {/* Variation Caption */}
                {variationCaption && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{
                      color: 'gold',
                      lineHeight: '1.5',
                      fontSize: '14px',
                      backgroundColor: '#111',
                      padding: '12px',
                      borderRadius: '8px',
                      borderLeft: '3px solid gold'
                    }}>
                      <strong style={{ 
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '15px'
                      }}>
                        Variation Caption:
                      </strong>
                      {variationCaption}
                    </div>
                  </div>
                )}
                
                {/* Comment Caption */}
                {commentCaption && (
                  <div style={{ marginBottom: '12px' }}>
                    {/* Commenter Username - above the bordered area */}
                    {commenterUsername && (
                      <div style={{ marginBottom: '8px' }}>
                        <span
                          style={{
                            color: '#ff6600',
                            fontSize: '18px',
                            fontWeight: 'bold',
                            cursor: commenterUsername !== 'Anonymous' ? 'pointer' : 'default'
                          }}
                          onClick={() => {
                            if (commenterUsername && commenterUsername !== 'Anonymous') {
                              setShowGameInfo(false);
                              history.push(`/profile/${commenterUsername}`, {
                                returnTo: `/game/${gameId}`,
                                returnLabel: 'Back to Game',
                                from: 'game-info-comment'
                              });
                            }
                          }}
                        >
                          @{commenterUsername}
                        </span>
                      </div>
                    )}
                    <div style={{
                      color: '#ff6600',
                      lineHeight: '1.5',
                      fontSize: '14px',
                      backgroundColor: '#111',
                      padding: '12px',
                      borderRadius: '8px',
                      borderLeft: '3px solid #ff6600'
                    }}>
                      <strong style={{ 
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '15px'
                      }}>
                        Comment Caption:
                      </strong>
                      {(() => {
                        // Parse @username pattern and render with orange clickable links
                        if (commentCaption) {
                          const parts = commentCaption.split(/(@\w+)/g);
                          
                          return parts.map((part, index) => {
                            if (part.startsWith('@')) {
                              const username = part.substring(1);
                              return (
                                <span
                                  key={index}
                                  style={{
                                    color: '#ff6600',
                                    cursor: 'pointer',
                                    textDecoration: 'underline'
                                  }}
                                  onClick={() => {
                                    if (username && username !== 'Anonymous') {
                                      setShowGameInfo(false);
                                      history.push(`/profile/${username}`, {
                                        returnTo: `/game/${gameId}`,
                                        returnLabel: 'Back to Game',
                                        from: 'game-info'
                                      });
                                    }
                                  }}
                                >
                                  {part}
                                </span>
                              );
                            }
                            return part;
                          });
                        }
                        return commentCaption;
                      })()}
                    </div>
                  </div>
                )}
                
                {/* Move Annotation */}
                {moveContent && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ 
                      color: '#58b047',
                      lineHeight: '1.5',
                      fontSize: '14px',
                      backgroundColor: '#111',
                      padding: '12px',
                      borderRadius: '8px',
                      borderLeft: '3px solid #58b047'
                    }}>
                      <strong style={{ 
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '15px'
                      }}>
                        {(() => {
                          // Get current move notation
                          let notation = '';
                          if (currentMoveId === '-1') {
                            notation = 'Start Position';
                          } else if (!currentMoveId.includes('|')) {
                            const moveIndex = parseInt(currentMoveId, 10);
                            notation = contextMoves[moveIndex]?.notation || 'Move';
                          } else {
                            try {
                              const { getNodeAtPathInfinite } = require('../../util/VariationPathUtils');
                              const result = getNodeAtPathInfinite(contextMoves, currentMoveId, state);
                              notation = (result.success && result.node?.notation) ? result.node.notation : 'Move';
                            } catch (error) {
                              notation = 'Move';
                            }
                          }
                          return `${notation} Annotation:`;
                        })()}
                      </strong>
                      {cleanProfanity(moveContent)}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // Tags/Players/Difficulty Sections
              <>
                {/* Tags Section */}
                {gameMetadata?.tags && gameMetadata.tags.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '8px'
                    }}>
                      <strong>Tags:</strong>
                      {gameMetadata.tags.map((tag, index) => (
                        <div
                          key={index}
                          onClick={() => handleTagClick(tag)}
                          style={{
                            ...getTagStyle(tag),
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            transition: 'opacity 0.2s ease'
                          }}
                          onMouseDown={(e) => e.currentTarget.style.opacity = '0.7'}
                          onMouseUp={(e) => e.currentTarget.style.opacity = '1'}
                          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                        >
                          {getTagDisplay(tag)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Players Section */}
                <div style={{ marginBottom: '12px' }}>
                  <strong>Players:</strong>
                  <div style={{ marginTop: '5px', paddingLeft: '10px' }}>
                    <div style={{ marginBottom: '3px' }}>
                      <span style={{ 
                        display: 'inline-block',
                        width: '12px',
                        height: '12px',
                        backgroundColor: 'white',
                        marginRight: '8px',
                        verticalAlign: 'middle'
                      }}></span>
                      {(() => {
                        const pov = gameMetadata?.pov;
                        const povPlayer = gameMetadata?.povPlayer;
                        const oppositePlayer = gameMetadata?.oppositePlayer;
                        
                        // If pov is true, povPlayer is white; if false, oppositePlayer is white
                        const whitePlayerData = pov ? povPlayer : oppositePlayer;
                        const whitePlayerName = whitePlayerData?.[0] || 'Not specified';
                        const whitePlayerRating = whitePlayerData?.[1];
                        
                        return cleanProfanity(whitePlayerName) + (whitePlayerRating && whitePlayerRating.trim() !== '' ? ` (${whitePlayerRating})` : '');
                      })()}
                    </div>
                    <div>
                      <span style={{ 
                        display: 'inline-block',
                        width: '12px',
                        height: '12px',
                        backgroundColor: '#333',
                        border: '1px solid #666',
                        marginRight: '8px',
                        verticalAlign: 'middle'
                      }}></span>
                      {(() => {
                        const pov = gameMetadata?.pov;
                        const povPlayer = gameMetadata?.povPlayer;
                        const oppositePlayer = gameMetadata?.oppositePlayer;
                        
                        // If pov is true, oppositePlayer is black; if false, povPlayer is black
                        const blackPlayerData = pov ? oppositePlayer : povPlayer;
                        const blackPlayerName = blackPlayerData?.[0] || 'Not specified';
                        const blackPlayerRating = blackPlayerData?.[1];
                        
                        return cleanProfanity(blackPlayerName) + (blackPlayerRating && blackPlayerRating.trim() !== '' ? ` (${blackPlayerRating})` : '');
                      })()}
                    </div>
                  </div>
                </div>
                
                {/* Difficulty Section */}
                <div style={{ marginBottom: '12px' }}>
                  <strong>Difficulty:</strong> {gameMetadata?.difficulty || 'All Levels'}
                </div>
              </>
            );
          })()}
          
          {/* Caption Section */}
          {gameMetadata?.caption && (
            <>
              <br />
              <div style={{ 
                borderTop: '1px solid #444',
                paddingTop: '12px',
                marginTop: '8px'
              }}>
                <strong>Caption:</strong>
                <div style={{ 
                  marginTop: '8px',
                  color: '#ccc',
                  lineHeight: '1.5'
                }}>
                  {cleanProfanity(gameMetadata.caption)}
                </div>
              </div>
            </>
          )}
          </div>
        </div>
      )}

      {/* Main Content - Now handled in board container above */}
     

      {/* Share Popup */}
      {showSharePopup && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '20px',
          zIndex: 1000,
          minWidth: '280px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '15px'
          }}>
            <h3 style={{ margin: 0, color: 'white', fontSize: '18px' }}>Share Game</h3>
            <button
              onClick={() => setShowSharePopup(false)}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: '#888',
                fontSize: '20px',
                cursor: 'pointer'
              }}
            >
              <i className="fa-solid fa-times"></i>
            </button>
          </div>
          
          <div style={{
            backgroundColor: '#0a0a0a',
            padding: '10px',
            borderRadius: '8px',
            marginBottom: '15px',
            wordBreak: 'break-all'
          }}>
            <p style={{ margin: 0, color: '#888', fontSize: '12px' }}>{shareLink}</p>
          </div>
          
          <button
            onClick={() => {
              navigator.clipboard.writeText(shareLink).then(() => {
                setShowSharePopup(false);
                // You could show a success toast here
              });
            }}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: 'gold',
              color: 'black',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Copy Link
          </button>
        </div>
      )}

      {/* Comment Tab View */}
      {showComments && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: 'calc(100dvh - 55px)', // Account for bottom navigation
            paddingTop: 'env(safe-area-inset-top)', // iPhone notch safe area
            paddingBottom: 'env(safe-area-inset-bottom)', // iPhone gesture area
            backgroundColor: '#000',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            transform: `translateX(${commentTranslateX}px)`,
            transition: isCommentSwiping ? 'none' : 'transform 0.3s ease-out'
          }}
          onTouchStart={(e) => {
            // Don't interfere with scrolling if touch starts on scrollable content
            const target = e.target;
            if (target.closest('[data-scrollable="true"]') || 
                target.closest('button') || 
                target.closest('input') ||
                target.closest('textarea')) {
              return;
            }
            
            // Store the starting X position
            const touch = e.touches[0];
            setCommentTouchStartX(touch.clientX);
            setIsCommentSwiping(false);
          }}
          onTouchMove={(e) => {
            // Don't track if starting from interactive elements
            const target = e.target;
            if (target.closest('[data-scrollable="true"]') || 
                target.closest('button') || 
                target.closest('input') ||
                target.closest('textarea')) {
              return;
            }
            
            // Track horizontal movement
            const touch = e.touches[0];
            const diffX = touch.clientX - commentTouchStartX;
            
            // Only track left-to-right swipes (positive diffX) with minimum threshold
            if (diffX > 10) {
              setIsCommentSwiping(true);
              // Apply some resistance to the swipe
              setCommentTranslateX(diffX * 0.8);
              // Prevent vertical scroll when swiping horizontally
              e.preventDefault();
            }
          }}
          onTouchEnd={() => {
            // Check if swipe was far enough to close comments
            const threshold = window.innerWidth * 0.25; // 25% of screen width
            
            if (commentTranslateX > threshold) {
              // Animate out and close comments
              setCommentTranslateX(window.innerWidth);
              setTimeout(() => {
                setShowComments(false);
                setCommentTranslateX(0); // Reset for next time
              }, 300);
            } else {
              // Snap back to original position
              setCommentTranslateX(0);
            }
            setIsCommentSwiping(false);
          }}
        >
          {/* Comment Header */}
          <div style={{
            padding: '16px',
            borderBottom: '1px solid #333',
            backgroundColor: '#1a1a1a',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <button
              onClick={() => setShowComments(false)}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: '#888',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '4px'
              }}
            >
              <i className="fa-solid fa-arrow-left"></i>
            </button>
            <h3 style={{ margin: 0, color: 'white', fontSize: '18px', flex: 1 }}>Comments ({comments.length})</h3>
          </div>

          {/* Comments List */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px',
            // paddingBottom: '80px', // Extra padding to keep comments above Add Comment input
            backgroundColor: '#000'
          }}>
            {comments.length === 0 ? (
              <p style={{ color: '#666', textAlign: 'center', marginTop: '40px' }}>No comments yet. Be the first to comment!</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {getSortedCommentsForMobile(comments, contextMoves, contextCurrentMove).map((comment, sortedIndex) => {
                  // Find the original index of this comment in the unsorted comments array
                  const originalIndex = comments.findIndex(c => 
                    (c.id && comment.id && c.id === comment.id) || 
                    (c.content === comment.content && c.username === comment.username && c.timestamp === comment.timestamp)
                  );
                  
                  const isHighlighted = isCurrentMoveComment(comment, contextCurrentMove, contextMoves);
                  
                  return (
                    <div
                      key={comment.id || sortedIndex}
                      style={{
                        backgroundColor: isHighlighted ? 'rgba(255, 102, 0, 0.1)' : '#1a1a1a',
                        borderRadius: '12px',
                        padding: '12px',
                        border: isHighlighted ? '2px solid #ff6600' : '1px solid #333'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '8px'
                      }}>
                        <span 
                          style={{
                            color: '#ff6600',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            cursor: 'pointer'
                          }}
                          onClick={() => {
                            if (comment.username && comment.username !== 'Anonymous') {
                              history.push(`/profile/${comment.username}`, {
                                returnTo: `/game/${gameId}`,
                                returnLabel: 'Back to Game',
                              from: 'game-comments'
                            });
                          }
                        }}
                      >
                        @{comment.username || 'Anonymous'}
                      </span>
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <i 
                          className="fa-solid fa-circle-exclamation" 
                          style={{
                            color: '#666',
                            fontSize: '14px',
                            cursor: 'pointer'
                          }}
                          onClick={() => handleCommentReport(originalIndex)}
                        ></i>
                        <i 
                          className="fa-solid fa-heart" 
                          style={{
                            color: comment.likes?.includes(user?.credentials?.userId) ? '#ff0050' : '#666',
                            fontSize: '14px',
                            cursor: 'pointer',
                            opacity: likingComments.has(comment.id || `temp-${originalIndex}`) ? 0.6 : 1
                          }}
                          onClick={() => handleCommentLike(originalIndex)}
                        ></i>
                      </div>
                    </div>
                    <p style={{
                      color: '#ccc',
                      fontSize: '14px',
                      margin: 0,
                      lineHeight: '1.4'
                    }}>
                      {cleanProfanity(comment.content)}
                    </p>
                    {/* Moves Preview Button for comments with variations */}
                    {renderMovesPreviewButton(comment.id, contextMoves)}
                    {/* Move-linked comment navigation button - same as desktop */}
                    {comment.moveIndex !== undefined && comment.moveIndex !== null && comment.moveNotation && (
                      <div style={{
                        marginTop: '8px',
                        padding: '8px',
                        backgroundColor: '#333',
                        borderRadius: '8px',
                        border: '1px solid #ff6600',
                        width: 'fit-content'
                      }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Close comments and return to board view
                            setShowComments(false);
                            // Navigate to the move on the board
                            goToMove(comment.moveIndex);
                          }}
                          style={{
                            background: contextCurrentMove === comment.moveIndex ? '#58b047' : '#666',
                            color: contextCurrentMove === comment.moveIndex ? 'black' : 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            fontSize: '11px',
                            cursor: 'pointer',
                            fontWeight: 'bold'
                          }}
                        title={`Go to move: ${comment.moveNotation}`}
                      >
                        {(() => {
                          // Calculate move number display - same logic as desktop
                          if (comment.moveIndex === '-1') {
                            return 'Start Position';
                          }
                          const cumulativeIndex = calculateCumulativeMoveIndex(comment.moveIndex);
                          const moveNumber = Math.floor(cumulativeIndex / 2) + 1;
                          const isWhite = cumulativeIndex % 2 === 0;
                          return `${moveNumber}${isWhite ? '.' : '...'} ${comment.moveNotation}`;
                        })()}
                      </button>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Unified Comment Input */}
          <div style={{
            borderTop: '1px solid #333',
            backgroundColor: '#1a1a1a',
            paddingBottom: '100px'
          }}>
            <UnifiedMobileCommentInput
              onSubmit={handleUnifiedCommentSubmit}
              placeholder={user?.authenticated ? "Add a comment..." : "Login to comment"}
              disabled={!user?.authenticated}
              submitting={submittingComment}
              showLinkToMove={contextCurrentMove !== '-1'} // Hide link option at start position
              currentMoveNotation={getCurrentMoveNotation()}
              rateLimitError={rateLimitError}
            />
          </div>
        </div>
      )}

      {/* Report Comment Modal */}
      {reportModalOpen && (
        <>
          <div
            onClick={() => {
              setReportModalOpen(false);
              setReportCommentIndex(null);
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.8)',
              zIndex: 1000
            }}
          />
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: '#000',
            border: '3px solid #ff0000',
            borderRadius: '12px',
            padding: '20px',
            zIndex: 1001,
            width: '70%',
            maxWidth: '280px',
            textAlign: 'center'
          }}>
            <h3 style={{ color: '#fff', marginBottom: '20px' }}>Report Comment</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
              <button
                onClick={() => handleReportSubmit('spam')}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#000',
                  color: '#ff0000',
                  border: '2px solid #ff0000',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Spam
              </button>
              <button
                onClick={() => handleReportSubmit('offensive')}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#000',
                  color: '#ff0000',
                  border: '2px solid #ff0000',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Offensive
              </button>
              <button
                onClick={() => handleReportSubmit('other')}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#000',
                  color: '#ff0000',
                  border: '2px solid #ff0000',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Other
              </button>
            </div>
            <button
              onClick={() => {
                setReportModalOpen(false);
                setReportCommentIndex(null);
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: '#333',
                color: '#fff',
                border: '2px solid #666',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </>
      )}

      {/* Bookmark Modal */}
      {showBookmarkModal && (
        <BookmarkModal
          onClose={() => setShowBookmarkModal(false)}
          onSave={handleBookmarkSave}
          gameId={gameId}
          isViewGameContext={true}
        />
      )}

      {/* Backdrop for popups */}
      {showSharePopup && (
        <div
          onClick={() => setShowSharePopup(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)',
            zIndex: 999
          }}
        />
      )}
      </div>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  user: state.user
});

export default connect(mapStateToProps)(ViewGameMobile);